import { assetPath, esc } from "./shared/viewer-utils";

(() => {
  type AnyRow = Record<string, any>;
  type Dashboard = {
    account?: AnyRow | null;
    players?: AnyRow[];
    gameRosters?: AnyRow[];
    availableGameTitles?: string[];
    applications?: AnyRow[];
    openCompetitions?: AnyRow[];
    tradeRequests?: AnyRow[];
    teamRosterInvites?: AnyRow[];
    rosterInvites?: AnyRow[];
    broadcastRequests?: AnyRow[];
    notifications?: AnyRow[];
    activity?: AnyRow[];
  };
  type PlayerMarket = {
    freeAgents?: AnyRow[];
    tradeListed?: AnyRow[];
    myTradeListed?: AnyRow[];
  };

  const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T | null;
  const params = new URLSearchParams(window.location.search);
  const resetToken = params.get("reset") || "";
  const playerActivateToken = params.get("activate") || "";
  const requestedSection = String(params.get("section") || "").trim();
  const recommendedLeagueId = String(params.get("recommendedLeagueId") || "").trim();

  let dashboard: Dashboard = {};
  let playerMarket: PlayerMarket = {};
  let rosterDraft: AnyRow[] = [];
  let selectedPlayerId = "";
  let captainCenterTab: "players" | "competitions" | "requests" | "settings" = "players";
  let selectedCaptainCompetitionId = "";
  let selectedCaptainTeamKey = "";
  let activeViewMode = (localStorage.getItem("teamPortalViewMode") === "player" ? "player" : "manager") as "manager" | "player";
  let activeSection = ["competitions", "find-tournaments", "settings", "profile"].includes(requestedSection) ? requestedSection : "profile";
  let activeMatchFilter = "all";
  let selectedMatchCompetitionId = "";
  let selectedMatchId = "";
  let activeMatchDetailTab: "details" | "lobby" | "chat" = "details";
  let matchCompetitionColumnCollapsed = false;
  const matchChatDrafts = new Map<string, string>();
  let selectedMatchLadderTeamId = "";
  let matchCompetitionSearch = "";
  let matchCompetitionGame = "all";
  let matchCompetitionStatus = "all";
  let matchCompetitionSort = "next";
  let activeCompetitionId = "";
  let activeCompetitionTab = "overview";
  let playerOverviewEditing = false;
  let activeProfileTab = "overview";
  let discoveryTab = params.get("compTab") || "all";
  let discoverySort = params.get("compSort") || "recent";
  let discoveryPage = Math.max(1, Number(params.get("compPage") || 1));
  let discoverySearchTimer = 0;
  const discoveryEntryTabs: Record<string, string> = {};
  const discoveryEntryExpanded: Record<string, boolean> = {};
  const discoveryEntryTeams: Record<string, string> = {};
  if (recommendedLeagueId) discoveryEntryExpanded[recommendedLeagueId] = true;
  let intendedCompetitionFocused = false;
  const discoveryEntryPlayers = new Map<string, Set<string>>();
  const discoveryRulesAccepted = new Set<string>();
  let selectedLogoPreviewUrl = "";
  let selectedBannerPreviewUrl = "";
  let notificationsOpen = false;
  let captainPlayerLookup: AnyRow | null = null;
  let captainPlayerLookupId = "";
  let captainPlayerLookupTimer = 0;
  let dashboardRefreshInFlight = false;
  let connectedAccounts: AnyRow[] = [];
  let oauthProviders: Record<string, boolean> = {};
  let playerOverviewEditDraft: AnyRow | null = null;
  let captainRosterRequestOpen = false;
  let captainRosterRequestMode: "add" | "remove" | "replace" = "add";
  let captainRosterRequestMessage = "";

  const clean = (value: unknown) => String(value ?? "").trim();
  const list = (value: unknown): AnyRow[] => Array.isArray(value) ? value : [];
  const first = (...values: unknown[]) => clean(values.find((value) => clean(value)) || "");
  const safeJson = (value: unknown): any[] => {
    if (Array.isArray(value)) return value;
    if (!clean(value)) return [];
    try {
      const parsed = JSON.parse(clean(value));
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };
  const initials = (value: unknown) => clean(value || "Team").split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "T";
  const playerKey = (player: AnyRow, index: number) => clean(player.id || player.in_game_name || player.player_name || `player-${index}`).toLowerCase();
  const splitGames = (value: unknown) => clean(value).split(",").map((item) => item.trim()).filter(Boolean);
  const accessLevel = (value: unknown) => ["manager", "owner", "captain", "admin"].includes(clean(value).toLowerCase()) ? "manager" : "player";
  const canManageMembership = (membership: AnyRow | null | undefined) => accessLevel(membership?.role) === "manager" || accessLevel(membership?.access_level) === "manager";
  const rosterStatus = (value: unknown) => ["inactive", "benched", "left"].includes(clean(value).toLowerCase()) ? "inactive" : "active";
  const tradeStatus = (value: unknown) => ["trade_listed", "free_agent", "unavailable"].includes(clean(value).toLowerCase()) ? clean(value).toLowerCase() : "rostered";
  const teamRole = (value: unknown) => {
    const key = clean(value).toLowerCase().replace(/[_\s]+/g, "-");
    const roles: Record<string, string> = {
      captain: "Captain",
      "co-captain": "Co-Captain",
      cocaptain: "Co-Captain",
      player: "Player",
      starter: "Player",
      sub: "Sub",
      substitute: "Sub",
      coach: "Coach",
    };
    return roles[key] || "Player";
  };
  const playerPlatformId = (player: AnyRow = {}) => first(player.bracketr_player_id, player.platform_id, player.platformId, player.player_external_id);
  const playerSteamId = (player: AnyRow = {}) => first(player.steam_id, player.steamId, player.steam_epic_id, player.external_id);
  const playerEpicId = (player: AnyRow = {}) => first(player.epic_id, player.epicId);
  const trackerHref = (value: unknown) => {
    const raw = clean(value);
    if (!raw) return "";
    return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  };
  const twitchUsername = (value: unknown) => {
    const raw = clean(value);
    if (!raw) return "";
    try {
      const parsed = new URL(/^https?:\/\//i.test(raw) ? raw : `https://twitch.tv/${raw.replace(/^@/, "")}`);
      if (parsed.hostname.toLowerCase() === "twitch.tv" || parsed.hostname.toLowerCase() === "www.twitch.tv") {
        return decodeURIComponent(parsed.pathname.split("/").filter(Boolean)[0] || "");
      }
    } catch {}
    return raw.replace(/^@/, "").replace(/^twitch\.tv\//i, "").split(/[/?#]/)[0];
  };
  const kickUsername = (value: unknown) => {
    const raw = clean(value);
    if (!raw) return "";
    try {
      const parsed = new URL(/^https?:\/\//i.test(raw) ? raw : `https://kick.com/${raw.replace(/^@/, "")}`);
      if (parsed.hostname.toLowerCase() === "kick.com" || parsed.hostname.toLowerCase() === "www.kick.com") {
        return decodeURIComponent(parsed.pathname.split("/").filter(Boolean)[0] || "");
      }
    } catch {}
    return raw.replace(/^@/, "").replace(/^kick\.com\//i, "").split(/[/?#]/)[0];
  };
  const youtubeUsername = (value: unknown) => {
    const raw = clean(value);
    if (!raw) return "";
    try {
      const parsed = new URL(/^https?:\/\//i.test(raw) ? raw : `https://youtube.com/@${raw.replace(/^@/, "")}`);
      if (["youtube.com", "www.youtube.com", "m.youtube.com"].includes(parsed.hostname.toLowerCase())) {
        const parts = parsed.pathname.split("/").filter(Boolean);
        const handle = parts.find((part) => part.startsWith("@"));
        return decodeURIComponent((handle || parts.at(-1) || "").replace(/^@/, ""));
      }
    } catch {}
    return raw.replace(/^@/, "").replace(/^(?:www\.)?youtube\.com\/@?/i, "").split(/[/?#]/)[0];
  };
  const trackerId = (value: unknown) => {
    const raw = clean(value);
    if (!raw) return "";
    const steamProfile = raw.match(/steamcommunity\.com\/profiles\/(\d+)/i);
    if (steamProfile?.[1]) return steamProfile[1];
    return raw.replace(/^https?:\/\/rocketleague\.tracker\.network\/rocket-league\/profile\/(?:steam|epic|psn|xbl)\//i, "").replace(/\/.*$/i, "");
  };
  const looksLikeSteamId = (value: unknown) => /^\d{15,20}$/.test(trackerId(value));
  const rocketLeagueTrackerLink = (steam = "", epic = "", ps4 = "", xbox = "") => {
    const steamId = trackerId(steam);
    if (steamId && looksLikeSteamId(steam)) return `https://rocketleague.tracker.network/rocket-league/profile/steam/${encodeURIComponent(steamId)}/overview`;
    const epicId = trackerId(epic);
    if (epicId) return `https://rocketleague.tracker.network/rocket-league/profile/epic/${encodeURIComponent(epicId)}/overview`;
    const xboxId = trackerId(xbox);
    if (xboxId) return `https://rocketleague.tracker.network/rocket-league/profile/xbl/${encodeURIComponent(xboxId)}/overview`;
    const ps4Id = trackerId(ps4);
    if (ps4Id) return `https://rocketleague.tracker.network/rocket-league/profile/psn/${encodeURIComponent(ps4Id)}/overview`;
    return "";
  };
  const tradeStatusLabel = (value: unknown) => ({
    rostered: "Rostered",
    trade_listed: "Open to trade",
    free_agent: "Free agent",
    unavailable: "Unavailable",
  }[tradeStatus(value)] || "Rostered");
  const uniqueGames = (values: unknown[]) => [...new Set(values.map(clean).filter(Boolean))];
  const teamGameTitles = (team: AnyRow = {}) => uniqueGames([
    ...safeJson(team.game_titles_json || team.game_titles),
    ...splitGames(team.game_titles),
    ...splitGames(team.platform || team.team_platform || team.game_title),
  ]);
  const teamGameLabel = (team: AnyRow = {}) => teamGameTitles(team).join(", ") || "No game set";
  const captainTeamKey = (team: AnyRow = {}) => team.team_account_id ? `portal:${clean(team.team_account_id)}` : `registry:${clean(team.registry_team_id)}`;
  const playerGameTitles = (player: AnyRow = {}) => uniqueGames([
    ...safeJson(player.games_json || player.games),
    ...list(player.games).map(clean),
    ...splitGames(player.game_titles),
  ]);
  const playerOverviewEditGames = (fallbackGames: string[]) => {
    if (playerOverviewEditDraft) return uniqueGames(list(playerOverviewEditDraft.games).map(clean));
    return fallbackGames;
  };
  const gamePills = (games: string[]) => games.length
    ? `<span class="player-game-pill-row">${games.map((game) => `<span class="registry-pill blue">${esc(game)}</span>`).join("")}</span>`
    : `<span class="player-game-empty">Not assigned</span>`;
  const teamDisplayName = (team: AnyRow = {}) => {
    return first(team.team_name, team.short_name, "Team");
  };

  function teamIcon(name: string) {
    const attrs = `viewBox="0 0 24 24" aria-hidden="true"`;
    const icons: Record<string, string> = {
      home: `<svg ${attrs}><rect x="4" y="4" width="6" height="6" rx="1"/><rect x="14" y="4" width="6" height="6" rx="1"/><rect x="4" y="14" width="6" height="6" rx="1"/><rect x="14" y="14" width="6" height="6" rx="1"/></svg>`,
      roster: `<svg ${attrs}><path d="M8 7h8"/><path d="M8 12h8"/><path d="M8 17h5"/><rect x="4" y="3" width="16" height="18" rx="2"/></svg>`,
      players: `<svg ${attrs}><path d="M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20"/><circle cx="10" cy="8" r="4"/><path d="M20 20v-1a3 3 0 0 0-2.5-2.95"/><path d="M16.5 4.3a4 4 0 0 1 0 7.4"/></svg>`,
      matches: `<svg ${attrs}><rect x="4" y="5" width="16" height="15" rx="2"/><path d="M8 3v4"/><path d="M16 3v4"/><path d="M4 10h16"/><path d="m8 15 2 2 5-5"/></svg>`,
      competitions: `<svg ${attrs}><path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 4h10v5a5 5 0 0 1-10 0V4Z"/><path d="M7 7H4a3 3 0 0 0 3 3"/><path d="M17 7h3a3 3 0 0 1-3 3"/></svg>`,
      find: `<svg ${attrs}><circle cx="11" cy="11" r="7"/><path d="m16 16 4 4"/><path d="M11 7v4l3 2"/></svg>`,
      active: `<svg ${attrs}><path d="M12 3 19 6v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3Z"/><path d="m8.5 12 2.2 2.2 4.8-5"/></svg>`,
      managers: `<svg ${attrs}><path d="m5 9 3.5 3L12 6l3.5 6L19 9l-1.5 9h-11L5 9Z"/><path d="M7 21h10"/></svg>`,
      games: `<svg ${attrs}><rect x="3" y="8" width="18" height="10" rx="4"/><path d="M8 13h4"/><path d="M10 11v4"/><circle cx="16.5" cy="12" r=".8"/><circle cx="18.5" cy="15" r=".8"/></svg>`,
      market: `<svg ${attrs}><path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7"/><path d="M2 7h20l-2 5H4L2 7Z"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`,
      requests: `<svg ${attrs}><path d="M5 4h14v16H5z"/><path d="M8 8h8"/><path d="M8 12h5"/><path d="m14 16 1.5 1.5L19 14"/></svg>`,
      settings: `<svg ${attrs}><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.05.05a2 2 0 0 1-2.83 2.83l-.05-.05A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 1.55V21a2 2 0 0 1-4 0v-.05a1.7 1.7 0 0 0-1-1.55 1.7 1.7 0 0 0-1.88.34l-.05.05a2 2 0 0 1-2.83-2.83l.05-.05A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.55-1H3a2 2 0 0 1 0-4h.05a1.7 1.7 0 0 0 1.55-1 1.7 1.7 0 0 0-.34-1.88l-.05-.05a2 2 0 1 1 2.83-2.83l.05.05A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.55V3a2 2 0 0 1 4 0v.05a1.7 1.7 0 0 0 1 1.55 1.7 1.7 0 0 0 1.88-.34l.05-.05a2 2 0 1 1 2.83 2.83l-.05.05A1.7 1.7 0 0 0 19.4 9c.25.6.84 1 1.55 1H21a2 2 0 0 1 0 4h-.05a1.7 1.7 0 0 0-1.55 1Z"/></svg>`,
      notifications: `<svg ${attrs}><path d="M18 9a6 6 0 1 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M10 21h4"/></svg>`,
      role: `<svg ${attrs}><path d="M12 3 4 7v5c0 5 3.5 8 8 9 4.5-1 8-4 8-9V7l-8-4Z"/><path d="M9 12h6"/></svg>`,
      access: `<svg ${attrs}><rect x="4" y="10" width="16" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/><path d="M12 14v2"/></svg>`,
      status: `<svg ${attrs}><circle cx="12" cy="12" r="9"/><path d="m8 12 2.5 2.5L16 9"/></svg>`,
      unread: `<svg ${attrs}><path d="M4 6h16v12H4z"/><path d="m4 7 8 6 8-6"/><circle cx="18" cy="6" r="3"/></svg>`,
      rank: `<svg ${attrs}><path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 4h10v5a5 5 0 0 1-10 0V4Z"/><path d="M7 7H4a3 3 0 0 0 3 3"/><path d="M17 7h3a3 3 0 0 1-3 3"/></svg>`,
      record: `<svg ${attrs}><path d="M4 19V5"/><path d="M8 19V9"/><path d="M12 19V3"/><path d="M16 19v-7"/><path d="M20 19V8"/></svg>`,
      challenges: `<svg ${attrs}><path d="M7 7h10v10H7z"/><path d="M3 12h4"/><path d="M17 12h4"/><path d="M12 3v4"/><path d="M12 17v4"/></svg>`,
      broadcast: `<svg ${attrs}><rect x="4" y="7" width="12" height="10" rx="2"/><path d="m16 10 4-2v8l-4-2"/><path d="M8 12h4"/></svg>`,
    };
    return icons[name] || icons.players;
  }

  function hydrateStaticIcons() {
    document.querySelectorAll<HTMLElement>("[data-team-icon]").forEach((el) => {
      const name = clean(el.dataset.teamIcon);
      if (name && !el.innerHTML.trim()) el.innerHTML = teamIcon(name);
    });
  }

  async function api(path: string, options: RequestInit = {}) {
    const response = await fetch(path, {
      credentials: "same-origin",
      cache: "no-store",
      ...options,
      headers: options.body instanceof FormData ? options.headers : {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.error || data.message || "Request failed") as Error & { status?: number };
      error.status = response.status;
      throw error;
    }
    return data;
  }

  function setText(id: string, text: unknown) {
    const el = $(id);
    if (el) el.textContent = clean(text);
  }

  function setMessage(id: string, message = "", tone: "ok" | "error" | "" = "") {
    const el = $(id);
    if (!el) return;
    el.textContent = message;
    el.dataset.tone = tone;
  }

  function revokePreviewUrl(url: string) {
    if (url) URL.revokeObjectURL(url);
  }

  function filePreviewUrl(inputId: string, currentUrl = "") {
    const file = $<HTMLInputElement>(inputId)?.files?.[0];
    if (!file) return currentUrl;
    revokePreviewUrl(currentUrl);
    return URL.createObjectURL(file);
  }

  function previewAssetUrl(src: unknown) {
    const raw = clean(src);
    if (/^(blob:|data:)/i.test(raw)) return raw;
    return assetPath(raw);
  }

  function renderImageSlot(id: string, src: unknown, fallback: unknown, alt: string) {
    const el = $(id);
    if (!el) return;
    const url = previewAssetUrl(src);
    if (url) {
      el.innerHTML = `<img src="${esc(url)}" alt="${esc(alt)}" loading="eager">`;
      el.classList.add("has-image");
    } else {
      el.textContent = initials(fallback);
      el.classList.remove("has-image");
    }
  }

  function renderBannerPreview(id: string, src: unknown, fallbackText = "No banner") {
    const el = $(id);
    if (!el) return;
    const url = previewAssetUrl(src);
    if (url) {
      el.innerHTML = `<img src="${esc(url)}" alt="Team banner preview" loading="eager">`;
      el.classList.add("has-image");
    } else {
      el.textContent = fallbackText;
      el.classList.remove("has-image");
    }
  }

  function showAuth() {
    $("teamPortalApp")?.setAttribute("data-state", "auth");
    $("portalLoading")?.classList.add("hidden");
    $("dashboardView")?.classList.add("hidden");
    $("authView")?.classList.remove("hidden");
    $("teamPlayerDrawer")?.classList.remove("open");
    document.body.classList.remove("team-portal-app-mode");
    if (resetToken || playerActivateToken) {
      $("loginCard")?.classList.add("hidden");
      $("forgotCard")?.classList.add("hidden");
      $("resetCard")?.classList.remove("hidden");
      setText("resetTitle", playerActivateToken ? "Activate Player Account" : "Set New Password");
      setText("resetCopy", playerActivateToken ? "Choose a password to claim this player profile and open your Player Portal." : "Choose a new password for this portal account.");
      setText("resetSubmitLabel", playerActivateToken ? "Activate Account" : "Update Password");
    }
  }

  function showDashboard() {
    $("teamPortalApp")?.setAttribute("data-state", "ready");
    $("portalLoading")?.classList.add("hidden");
    $("authView")?.classList.add("hidden");
    $("dashboardView")?.classList.remove("hidden");
    document.body.classList.add("team-portal-app-mode");
  }

  function openModal(id: string) {
    const modal = $(id);
    if (!modal) return;
    if (id === "captainCreateTeamModal" || id === "captainEditTeamModal") fillCaptainGameSelect();
    if (id === "captainAddPlayerModal") {
      ($<HTMLInputElement>("captainPlayerBracketrId") || {}).value = "";
      resetCaptainPlayerLookup();
      setMessage("captainAddPlayerMessage", "");
    }
    modal.classList.remove("hidden");
    modal.setAttribute("aria-hidden", "false");
  }

  function closeModals() {
    document.querySelectorAll<HTMLElement>(".team-modal").forEach((modal) => {
      modal.classList.add("hidden");
      modal.setAttribute("aria-hidden", "true");
    });
  }

  function allMatches() {
    return list(dashboard.applications).flatMap((app) => list(app.matches).map((match) => ({
      ...match,
      __league_name: app.league_name,
      __application_id: app.id,
      __application_status: app.status,
      __competition_stage: app.competition_stage,
    })));
  }

  function competitionKey(app: AnyRow) {
    return clean(app.league_id || app.id || app.league_name);
  }

  function selectedCompetition() {
    const applications = list(dashboard.applications);
    if (!activeCompetitionId && applications.length) activeCompetitionId = competitionKey(applications[0]);
    return applications.find((app) => competitionKey(app) === activeCompetitionId) || null;
  }

  function competitionMatches(app: AnyRow | null) {
    if (!app) return [];
    return list(app.matches).map((match) => ({
      ...match,
      __league_name: app.league_name,
      __application_id: app.id,
      __application_status: app.status,
      __competition_stage: app.competition_stage,
    }));
  }

  function competitionBroadcastRequests(app: AnyRow | null) {
    if (!app) return [];
    const leagueId = String(app.league_id || "");
    return list(dashboard.broadcastRequests).filter((request) => String(request.league_id || "") === leagueId);
  }

  function matchState(match: AnyRow) {
    const raw = clean(match.status).toLowerCase();
    if (["completed", "complete", "finished", "bye"].includes(raw)) return "completed";
    if (["live", "active", "in_progress", "in-progress"].includes(raw)) return "live";
    return "upcoming";
  }

  function formatDate(value: unknown) {
    const raw = clean(value);
    if (!raw) return "Not scheduled";
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    return date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
  }

  function statusPill(status: unknown) {
    const label = clean(status || "pending").replaceAll("_", " ");
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const tone = ["active", "approved", "checked-in", "manager"].includes(key) ? "green" : (["pending", "player"].includes(key) ? "blue" : "amber");
    return `<span class="registry-pill ${tone}">${esc(label)}</span>`;
  }

  function hydrateRoster() {
    const gameMap = new Map<string, Set<string>>();
    list(dashboard.gameRosters).forEach((gameRoster) => {
      const gameTitle = clean(gameRoster.game_title);
      list(gameRoster.roster).forEach((player) => {
        const key = clean(player.in_game_name || player.player_name).toLowerCase();
        if (!key || !gameTitle) return;
        if (!gameMap.has(key)) gameMap.set(key, new Set());
        gameMap.get(key)?.add(gameTitle);
      });
    });

    rosterDraft = list(dashboard.players).map((player, index) => {
      const key = clean(player.in_game_name || player.player_name).toLowerCase();
      const games = safeJson(player.games_json || player.games);
      return {
        ...player,
        __id: playerKey(player, index),
        access_level: canManageMembership(player) ? "manager" : "player",
        status: rosterStatus(player.status),
        trade_status: tradeStatus(player.trade_status),
        games: games.length ? games.map(clean).filter(Boolean) : [...(gameMap.get(key) || new Set<string>())],
      };
    });
    if (activeSection !== "settings" && (!selectedPlayerId || !rosterDraft.some((player, index) => playerKey(player, index) === selectedPlayerId))) {
      selectedPlayerId = playerKey(rosterDraft[0] || {}, 0);
    }
  }

  function render() {
    showDashboard();
    hydrateStaticIcons();
    // Deep links must win before any account/header decoration runs. In
    // particular, org-to-player switching can briefly expose a mixed account
    // payload; that must not leave the static Home panel selected.
    setSection(activeSection);
    if (activeSection === "settings") {
      try {
        renderSettings();
      } catch (error) {
        console.error("Captain Quarters render failed", error);
        const target = $("captainQuartersWorkspace");
        if (target) {
          target.innerHTML = `
            <section class="captain-column captain-render-error">
              <h2>Captain Quarters could not load</h2>
              <p>${esc((error as Error)?.message || "An unexpected player data error occurred.")}</p>
              <button class="brk-btn primary" type="button" data-reload-captain-quarters>Try Again</button>
            </section>`;
        }
      }
    }
    const account = dashboard.account || {};
    const isPlayerAccount = clean(account.account_type) === "player";
    const canManageTeam = !isPlayerAccount || account.can_manage_team === true || account.can_manage_team === 1;
    activeViewMode = activeSection === "settings" && canManageTeam ? "manager" : "player";
    const teamName = first(account.team_name, "Player Portal");
    const playerName = first(account.in_game_name, account.display_name, account.player_name, selectedPlayer()?.in_game_name, selectedPlayer()?.player_name, teamName, "Player");
    const sideName = isPlayerAccount ? playerName : teamName;
    setText("sideTeamName", sideName);
    renderImageSlot("sideAvatar", isPlayerAccount ? "" : account.logo_path, sideName, isPlayerAccount ? "Player avatar" : `${teamName} logo`);
    setText("sideTeamRole", isPlayerAccount ? "User View" : (activeViewMode === "manager" ? "Captain Quarters" : "User Access"));
    renderImageSlot(
      "teamUserbarAvatar",
      activeViewMode === "manager" && !isPlayerAccount ? account.logo_path : "",
      activeViewMode === "manager" ? teamName : first(selectedPlayer()?.in_game_name, selectedPlayer()?.player_name, teamName),
      activeViewMode === "manager" && !isPlayerAccount ? `${teamName} logo` : "Player avatar",
    );
    setText("teamUserbarName", activeViewMode === "manager" ? teamName : first(selectedPlayer()?.in_game_name, selectedPlayer()?.player_name, "Player"));
    document.body.dataset.teamPortalView = activeViewMode;
    document.querySelectorAll<HTMLElement>("[data-view-mode]").forEach((button) => {
      button.classList.toggle("is-active", button.dataset.viewMode === activeViewMode);
      button.classList.toggle("hidden", button.dataset.viewMode === "manager" && !canManageTeam);
    });
    document.querySelectorAll<HTMLElement>(".manager-only").forEach((el) => {
      el.classList.toggle("hidden", activeViewMode === "player" || !canManageTeam);
    });
    document.querySelectorAll<HTMLElement>(".delete-team-only").forEach((el) => {
      el.classList.toggle("hidden", activeViewMode === "player" || !canManageTeam || account.can_delete_team === false);
    });
    // Activate the requested panel before rendering its contents. A failure in an
    // unrelated dashboard widget must never send a deep link back to Home.
    if (activeSection === "home") {
      renderPlayerPortalOverview();
      renderStats();
      renderRoster();
      renderMyTeams();
    } else if (activeSection === "competitions") {
      renderMatches();
      renderCompetitions();
    } else if (activeSection === "find-tournaments") {
      renderOpenCompetitions();
    } else if (activeSection === "competition-hub") {
      renderCompetitionHub();
    } else if (activeSection === "profile") {
      renderProfile();
    }
    renderNotifications();
    renderNavBadges();
    renderActivity();
    renderPortalContext();
  }

  function setSection(section: string) {
    if (section === "home") section = "profile";
    const account = dashboard.account || {};
    const canManageTeam = clean(account.account_type) !== "player" || account.can_manage_team === true || account.can_manage_team === 1;
    if (section === "roster") section = "profile";
    if (section === "notifications") section = "profile";
    if (section === "matches") section = "competitions";
    if (section === "teams") section = "settings";
    if (section === "requests") {
      captainCenterTab = "requests";
      section = "settings";
    }
    activeViewMode = section === "settings" && canManageTeam ? "manager" : "player";
    if (section === "competition-hub" && !selectedCompetition()) section = "competitions";
    activeSection = section;
    if (!resetToken && !playerActivateToken) {
      const url = new URL(window.location.href);
      url.searchParams.set("section", section);
      window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
    }
    document.body.classList.toggle("team-portal-overview-section", section === "home");
    document.body.classList.toggle("team-portal-compact-section", section === "competitions");
    document.body.classList.toggle("team-portal-profile-section", section === "profile");
    document.body.classList.toggle("team-portal-discovery-section", section === "find-tournaments");
    const overview = $("playerPortalOverview");
    if (overview) {
      overview.classList.toggle("hidden", section !== "home");
      if (section !== "home") overview.innerHTML = "";
    }
    document.querySelector<HTMLElement>(".registry-page-head")?.classList.toggle("hidden", section === "settings" || section === "home" || section === "competitions" || section === "profile");
    document.querySelectorAll<HTMLElement>("[data-section]").forEach((button) => {
      const active = button.dataset.section === section;
      button.classList.toggle("is-active", active);
      button.classList.toggle("active", active);
    });
    document.querySelectorAll<HTMLElement>("[data-panel]").forEach((panel) => {
      panel.classList.toggle("is-active", panel.dataset.panel === section);
    });
    const titles: Record<string, string> = {
      home: "Dashboard",
      teams: "Captain Quarters",
      matches: "Competition Centre",
      competitions: "Competition Centre",
      "find-tournaments": "Find Competitions",
      "competition-hub": selectedCompetition()?.league_name || "Competition Hub",
      requests: "Captain Quarters",
      settings: "Captain Quarters",
      profile: "Profile",
      activity: "Activity",
    };
    setText("pageTitle", titles[section] || "Player Portal");
    setText("teamPortalSubtitle", section === "competition-hub"
      ? "Track ladder position, challenges, accepted matches, results, broadcasts, streams, and rules from one competition workspace."
      : section === "teams"
        ? "Captain Quarters: manage teams, rosters, competition lineups, and captain requests."
      : section === "competitions"
        ? "Select a competition first, then manage matches, check-ins, entries, and linked competition status."
      : section === "find-tournaments"
        ? "Find tournaments, ladders and leagues accepting entries."
      : section === "settings"
        ? "Create teams, add registered players by Bracketr ID, and manage captain roster access."
      : section === "profile"
        ? "Manage the player identity and platform details teams use across Bracketr."
      : section === "home"
        ? "Your next match, team invites, teams, competitions, and profile readiness."
        : activeViewMode === "manager"
          ? "Captain Quarters: manage players, game rosters, access levels, and match readiness from one workspace."
          : "View your teams, competitions, matches, and requests from one workspace.");
    $("competitionHubTab")?.classList.toggle("hidden", !selectedCompetition());
  }

  function renderPortalContext() {
    const target = $("playerPortalContext");
    if (!target) return;
    const hidesPortalContext = activeSection === "home" || activeSection === "profile" || activeSection === "competitions";
    target.classList.toggle("hidden", hidesPortalContext);
    if (hidesPortalContext) {
      target.innerHTML = "";
      return;
    }
    const account = dashboard.account || {};
    const selected = selectedPlayer() || {};
    const readinessItems = profileReadinessItems(account, selected);
    const readinessComplete = readinessItems.filter((item) => item.done).length;
    const readinessPercent = Math.round((readinessComplete / readinessItems.length) * 100);
    const teams = playerMemberships().length;
    const activeMatches = allMatches().filter((match) => matchState(match) !== "completed").length;
    const pendingRequests = list(dashboard.rosterInvites).filter((invite) => clean(invite.status).toLowerCase() === "pending").length;
    const cards = [
      ["Profile", `${readinessPercent}%`, `${readinessComplete}/${readinessItems.length} ready`, "profile"],
      ["Captain Quarters", teams, teams === 1 ? "linked team" : "linked teams", "settings"],
      ["Competition Centre", activeMatches, activeMatches === 1 ? "active match" : "active matches", "competitions"],
    ];
    target.innerHTML = cards.map(([label, value, hint, section]) => `
      <button class="${activeSection === section ? "is-active" : ""}" type="button" data-section-jump="${esc(String(section))}">
        <span>${esc(String(label))}</span>
        <strong>${esc(value)}</strong>
        <small>${esc(String(hint))}</small>
      </button>
    `).join("");
  }

  function playerMemberships() {
    const account = dashboard.account || {};
    const memberships = list(account.memberships);
    if (memberships.length) return memberships;
    return list(dashboard.players)
      .filter((player) => clean(player.team_name) || clean(player.team_account_id))
      .map((player) => ({
        team_account_id: player.team_account_id,
        team_name: player.team_name || account.team_name,
        short_name: player.short_name || "",
        logo_path: player.logo_path || "",
        access_level: canManageMembership(player) ? "manager" : "player",
        role: player.role || "Player",
        status: player.status || "active",
      }));
  }

  function playerRecordLabel(player: AnyRow = {}) {
    const team = clean(player.team_name);
    const role = first(player.role, accessLevel(player.access_level) === "manager" ? "Captain" : "Player");
    return team ? `${role} at ${team}` : role;
  }

  function renderPlayerPortalOverview() {
    const target = $("playerPortalOverview");
    if (!target) return;
    target.classList.toggle("hidden", activeSection !== "home");
    if (activeSection !== "home") {
      target.innerHTML = "";
      return;
    }
    target.innerHTML = `<section class="registry-detail-card player-overview-card player-overview-loading"><p class="team-empty compact">Loading player overview...</p></section>`;
    try {
      renderPlayerPortalOverviewContent(target);
    } catch (error) {
      console.error("Player overview render failed:", error);
      target.innerHTML = `
        <section class="registry-detail-card player-overview-card player-overview-loading">
          <div class="registry-detail-section">
            <h3>Dashboard</h3>
            <p class="team-empty">We couldn't draw your player overview just then. Your session is still active.</p>
            <button class="brk-btn primary" type="button" data-refresh-player-overview>Reload Overview</button>
          </div>
        </section>
      `;
    }
  }

  function renderPlayerPortalOverviewContent(target: HTMLElement) {
    const account = dashboard.account || {};
    const selected = selectedPlayer() || {};
    const playerName = first(account.in_game_name, account.display_name, selected.in_game_name, selected.player_name, "Player");
    const emailAddress = first(account.email, account.contact_email, selected.email);
    const realName = first(account.player_name, selected.player_name, emailAddress, "No real name linked");
    const bracketrId = first(account.bracketr_player_id, playerPlatformId(selected), "No Bracketr ID yet");
    const steamId = first(account.steam_id, account.steam_epic_id, selected.steam_id, selected.steam_epic_id);
    const epicId = first(account.epic_id, selected.epic_id);
    const ps4Id = first(account.ps4_id, selected.ps4_id);
    const xboxId = first(account.xbox_id, selected.xbox_id);
    const externalId = first(account.player_external_id, selected.player_external_id);
    const savedTrackerLink = first(account.player_tracker_link, selected.player_tracker_link);
    const generatedTrackerLink = rocketLeagueTrackerLink(steamId, epicId, ps4Id, xboxId);
    const trackerLink = savedTrackerLink || generatedTrackerLink;
    const trackerUrl = trackerHref(trackerLink);
    const memberships = playerMemberships();
    const captainMemberships = memberships.filter(canManageMembership);
    const pendingInvites = list(dashboard.rosterInvites).filter((invite) => clean(invite.status).toLowerCase() === "pending");
    const applications = list(dashboard.applications);
    const openCompetitions = list(dashboard.openCompetitions);
    const notifications = list(dashboard.notifications);
    const activeMatches = allMatches().filter((match) => matchState(match) !== "completed");
    const accountGames = playerGameTitles(account);
    const games = accountGames.length ? accountGames : playerGameTitles(selected);
    const editGames = playerOverviewEditGames(games);
    const nextAction = playerHomeNextAction({ account, selected, games, pendingInvites, memberships, applications });
    const nextMatch = playerHomeNextMatch();
    const recentNotices = notifications.slice(0, 3);
    const identityRows = [
      ["Full Name", esc(realName)],
      ["Alias", esc(playerName)],
      ["Email", esc(emailAddress || "Not linked")],
      ["Bracketr Player ID", esc(bracketrId)],
      ["Account Status", esc(account.email_verified ? "Email verified" : "Email pending verification")],
    ];
    const platformRows = [
      ["Games", gamePills(games)],
      ["Steam ID", esc(steamId || "Not supplied")],
      ["Epic Username", esc(epicId || "Not supplied")],
      ["PlayStation Username", esc(ps4Id || "Not supplied")],
      ["Xbox Username", esc(xboxId || "Not supplied")],
      ["Player Tracker", trackerUrl ? `<a href="${esc(trackerUrl)}" target="_blank" rel="noopener noreferrer">${esc(trackerLink)}</a>` : "Not linked"],
    ];
    const accessRows = [
      ["Teams", esc(memberships.length ? memberships.map((team) => teamDisplayName(team)).join(", ") : "None yet")],
      ["Captain Access", esc(captainMemberships.length ? `${captainMemberships.length} team${captainMemberships.length === 1 ? "" : "s"}` : "Not active")],
      ["Competitions", esc(applications.length ? `${applications.length} connected` : "None yet")],
      ["Player Logo", esc(first(account.player_logo_path, selected.player_logo_path) ? "Supplied" : "Not supplied")],
    ];
    const logoPath = first(account.player_logo_path, selected.player_logo_path);
    const statCards = [
      ["My Matches", activeMatches.length || 0, "Active or upcoming"],
      ["Open Competitions", openCompetitions.length || 0, "Available to join"],
      ["My Teams", memberships.length || 0, "Team memberships"],
      ["Invites", pendingInvites.length || 0, pendingInvites.length ? "Waiting for you" : "None pending"],
      ["Notices", notifications.length || 0, "Player notifications"],
    ];
    const shortcutCards = [
      ["Change Password", "Update your login security", "settings"],
      ["Captain Quarters", captainMemberships.length ? "Manage captain tools" : "Create or manage teams", "settings"],
      ["Competition Centre", applications.length ? "Entries and fixtures" : "No entries yet", "competitions"],
      ["Find Comps", "Browse open registrations", "find-tournaments"],
      ["Profile", "Edit player identity", "profile"],
    ];
    target.innerHTML = `
      <section class="player-overview-master">
        <article class="registry-detail-card player-overview-card">
          <div class="player-overview-hero">
            <span class="registry-avatar player-overview-avatar">${logoPath ? `<img src="${esc(assetPath(logoPath))}" alt="">` : esc(initials(playerName))}</span>
            <div class="player-overview-identity">
              <div class="player-overview-name-row">
                <h2>${esc(playerName)}</h2>
                <span class="registry-pill green">${esc(account.status || "Active")}</span>
              </div>
              <p>${esc(realName)}</p>
              <div class="registry-chip-row">
                <span class="registry-pill blue">${esc(bracketrId)}</span>
                <span class="registry-pill">${esc(games[0] || "Player")}</span>
                <span class="registry-pill">${esc(captainMemberships.length ? "Captain access" : "Player access")}</span>
              </div>
            </div>
            <div class="player-overview-hero-actions">
              <button class="brk-btn primary" type="button" data-toggle-player-edit>Edit Details</button>
            </div>
          </div>
          <div class="player-overview-summary">
            ${statCards.map(([label, value, hint]) => `
              <article>
                <span>${esc(String(label))}</span>
                <strong>${esc(String(value))}</strong>
                <small>${esc(String(hint))}</small>
              </article>
            `).join("")}
          </div>
          <section class="player-home-command-grid">
            <article class="player-home-command-card ${esc(nextAction.tone)}">
              <span>${esc(nextAction.label)}</span>
              <h3>${esc(nextAction.title)}</h3>
              <p>${esc(nextAction.copy)}</p>
              <button class="team-primary-btn" type="button" data-section-jump="${esc(nextAction.section)}">${esc(nextAction.action)}</button>
            </article>
            <article class="player-home-command-card">
              <span>Next Match</span>
              ${nextMatch ? `
                <h3>${esc(first(nextMatch.opponent_name, nextMatch.team1_name, nextMatch.team2_name, "Opponent TBA"))}</h3>
                <p>${esc(first(nextMatch.__league_name, nextMatch.league_name, "Competition"))} - ${esc(formatMatchSlot(nextMatch))}</p>
                <button class="team-secondary-btn" type="button" data-section-jump="competitions">Open Competition Centre</button>
              ` : `
                <h3>No match queued</h3>
                <p>Upcoming fixtures will appear here as soon as a competition schedules your team.</p>
                <button class="team-secondary-btn" type="button" data-section-jump="competitions">View Competitions</button>
              `}
            </article>
            <article class="player-home-command-card">
              <span>Recent Notices</span>
              ${recentNotices.length ? `
                <div class="player-home-notice-list">
                  ${recentNotices.map((notice) => `
                    <button type="button" data-notification-open>
                      <strong>${esc(notice.title || "Notification")}</strong>
                      <small>${esc(first(notice.team_name, notice.league_name, notice.created_at, "Portal notice"))}</small>
                    </button>
                  `).join("")}
                </div>
              ` : `
                <h3>All quiet</h3>
                <p>No new portal notices are waiting right now.</p>
              `}
            </article>
          </section>
          ${pendingInvites.length ? `
            <section class="player-overview-invites">
              <div class="player-overview-section-head"><h3>Pending Team Invites</h3><button class="brk-btn" type="button" data-section-jump="requests">Captain Quarters</button></div>
              <div class="player-membership-list">
                ${pendingInvites.slice(0, 3).map((invite) => `
                  <article class="player-invite-card">
                    <span class="registry-avatar">${esc(initials(first(invite.team_name, invite.short_name, "Team")))}</span>
                    <span><strong>${esc(invite.team_name || "Team invite")}</strong><small>${esc(first(invite.requester_in_game_name, invite.requester_display_name, "Captain"))} invited you as ${esc(invite.role || "Player")}</small></span>
                    <div class="team-inline-actions">
                      <button class="team-primary-btn" type="button" data-roster-invite-accept="${esc(invite.id)}">Accept</button>
                      <button class="team-secondary-btn" type="button" data-roster-invite-deny="${esc(invite.id)}">Deny</button>
                    </div>
                  </article>
                `).join("")}
              </div>
            </section>
          ` : ""}
          <section class="player-dashboard-lists">
            <article>
              <div class="player-overview-section-head"><h3>Open Competitions</h3><button class="brk-btn" type="button" data-section-jump="find-tournaments">View All</button></div>
              <div class="player-dashboard-list">
                ${openCompetitions.length ? openCompetitions.slice(0, 4).map((competition) => `
                  <button class="player-dashboard-list-item" type="button" data-section-jump="find-tournaments">
                    <span class="registry-avatar">${esc(initials(first(competition.league_name, competition.name, "Competition")))}</span>
                    <span><strong>${esc(first(competition.league_name, competition.name, "Competition"))}</strong><small>${esc(first(competition.game_title, competition.format, "Open registration"))}</small></span>
                    <span class="registry-pill green">Open</span>
                  </button>
                `).join("") : `<p class="team-empty compact">No open competitions right now.</p>`}
              </div>
            </article>
            <article>
              <div class="player-overview-section-head"><h3>My Competitions</h3><button class="brk-btn" type="button" data-section-jump="competitions">View Matches</button></div>
              <div class="player-dashboard-list">
                ${applications.length ? applications.slice(0, 4).map((application) => `
                  <button class="player-dashboard-list-item" type="button" data-section-jump="competitions">
                    <span class="registry-avatar">${esc(initials(first(application.league_name, application.competition_name, "Competition")))}</span>
                    <span><strong>${esc(first(application.league_name, application.competition_name, "Competition"))}</strong><small>${esc(first(application.team_name, application.game_title, "Registered"))}</small></span>
                    ${statusPill(application.status || "active")}
                  </button>
                `).join("") : `<p class="team-empty compact">Your competitions and matches will appear here.</p>`}
              </div>
            </article>
          </section>
          <section class="player-dashboard-teams">
            <article>
              <div class="player-overview-section-head"><h3>My Teams</h3><button class="brk-btn" type="button" data-section-jump="settings">Open Captain Quarters</button></div>
              <div class="player-membership-list">
                ${memberships.length ? memberships.map((team) => `
                  <button class="player-membership-item" type="button" data-player-team-jump="${esc(team.team_account_id || "")}">
                    <span class="registry-avatar">${esc(initials(first(team.team_name, team.short_name, "Team")))}</span>
                    <span><strong>${esc(teamDisplayName(team))}</strong><small>${esc(first(team.role, accessLevel(team.access_level) === "manager" ? "Captain access" : "Player"))}</small></span>
                    ${statusPill(team.status || "active")}
                  </button>
                `).join("") : `<p class="team-empty compact">You are not on any teams yet.</p>`}
              </div>
            </article>
          </section>
        </article>
        <aside class="master-panel player-overview-actions">
          <div class="master-panel-heading compact">
            <div>
              <h2>Profile Readiness</h2>
              <p>Complete the details teams need before inviting you.</p>
            </div>
          </div>
          ${renderProfileReadiness(account, selected, memberships)}
          <div class="team-inline-actions player-dashboard-actions">
            <button class="brk-btn primary" type="button" data-section-jump="profile">Edit Profile</button>
            <button class="brk-btn" type="button" data-section-jump="find-tournaments">Find Comps</button>
          </div>
        </aside>
        ${renderPlayerProfileEditModal({ account, selected, playerName, realName, steamId, epicId, ps4Id, xboxId, externalId, trackerLink, generatedTrackerLink, editGames })}
      </section>
    `;
  }

  function renderPlayerProfileEditModal(context: {
    account: AnyRow;
    selected: AnyRow;
    playerName: string;
    realName: string;
    steamId: string;
    epicId: string;
    ps4Id: string;
    xboxId: string;
    externalId: string;
    trackerLink: string;
    generatedTrackerLink: string;
    editGames: string[];
  }) {
    if (!playerOverviewEditing) return "";
    const editGames = playerOverviewEditGames(context.editGames);
    const connectedIdentity = (provider: string) => {
      const account = connectedAccounts.find((item) => clean(item.provider).toLowerCase() === provider);
      return first(account?.provider_username, account?.provider_email, account?.provider_user_id);
    };
    const connectedTwitch = connectedIdentity("twitch");
    const connectedKick = connectedIdentity("kick");
    const connectedYoutube = connectedIdentity("youtube");
    return `
      <section class="team-modal player-overview-edit-modal" aria-hidden="false">
        <div class="team-modal-backdrop" data-close-player-edit></div>
        <form class="team-modal-card wide player-overview-edit" id="playerOverviewEditForm">
        <div class="master-panel-heading compact">
          <div><h2>Edit Player Details</h2><p>Choose the games you are available to play before teams can invite you.</p></div>
          <div class="team-inline-actions">
            <button class="ghost-btn" type="button" data-close-player-edit>Close</button>
            <button class="master-button small-button" type="submit">Save</button>
          </div>
        </div>
        <section class="player-edit-section">
          <div class="player-edit-section-head"><span>01</span><div><h3>Player Identity</h3><p>Your public player name and private full name.</p></div></div>
          <div class="player-edit-fields two">
            <label>Player Name<input name="in_game_name" value="${esc(context.playerName)}" required></label>
            <label>Full Name<input name="player_name" value="${esc(context.realName === "No real name linked" || context.realName === "Not supplied" ? "" : context.realName)}"></label>
          </div>
        </section>
        <section class="player-edit-section">
          <div class="player-edit-section-head"><span>02</span><div><h3>Game Accounts</h3><p>Use the identifier or username shown on each platform.</p></div></div>
          <div class="player-edit-fields four">
            <label>Steam ID<input name="steam_id" value="${esc(context.steamId)}" placeholder="17-digit SteamID64"></label>
            <label>Epic Username<input name="epic_id" value="${esc(context.epicId)}"></label>
            <label>PlayStation Username<input name="ps4_id" value="${esc(context.ps4Id)}"></label>
            <label>Xbox Username<input name="xbox_id" value="${esc(context.xboxId)}"></label>
          </div>
        </section>
        <input type="hidden" name="player_external_id" value="${esc(context.externalId)}">
        <section class="player-edit-section">
          <div class="player-edit-section-head"><span>03</span><div><h3>Channels &amp; Games</h3><p>Help teams understand where you play and create content.</p></div></div>
          <div class="player-edit-fields three">
            <label>Twitch Account<input name="twitch_url" value="${esc(connectedTwitch)}" placeholder="Not connected" readonly></label>
            <label>Kick Account<input name="kick_url" value="${esc(connectedKick)}" placeholder="Not connected" readonly></label>
            <label>YouTube Account<input name="youtube_url" value="${esc(connectedYoutube)}" placeholder="Not connected" readonly></label>
          </div>
          <div class="player-connected-field-note">
            <span>Streaming identities are verified through Connected Accounts.</span>
            <button class="brk-btn" type="button" data-manage-profile-connections>Manage Connections</button>
          </div>
          <label class="player-edit-games">Available Games
          <div class="team-game-picker player-game-picker" role="group" aria-label="Player available games">
            ${uniqueGames([...availableGames(), ...editGames, "Rocket League"]).sort((a, b) => a.localeCompare(b)).map((game) => `
              <label class="team-game-picker-option">
                <input type="checkbox" name="games" value="${esc(game)}" ${editGames.some((item) => item.toLowerCase() === game.toLowerCase()) ? "checked" : ""}>
                <span>${esc(game)}</span>
              </label>
            `).join("")}
          </div>
          </label>
        </section>
        <section class="player-edit-section player-edit-tracker">
          <div class="player-edit-section-head"><span>04</span><div><h3>Rocket League Tracker</h3><p>Open the public profile or attempt to import current competitive ratings.</p></div></div>
          <div class="player-tracker-input-row">
            <label>Tracker Profile URL<input name="player_tracker_link" type="url" data-generated-value="${esc(context.generatedTrackerLink)}" value="${esc(context.trackerLink)}" placeholder="Auto-generated from your selected platform"></label>
            <a class="brk-btn" data-open-player-tracker href="${esc(trackerHref(context.trackerLink) || "#")}" target="_blank" rel="noopener noreferrer" ${context.trackerLink ? "" : "aria-disabled=\"true\""}>Open Tracker ↗</a>
            <button class="brk-btn primary" type="button" data-scrape-player-tracker>Get Rankings</button>
          </div>
          <div class="player-ranking-grid">
            <label>1v1 Rating<input name="rocket_league_mmr_1v1" type="number" min="0" max="5000" value="${esc(context.account.rocket_league_mmr_1v1)}" placeholder="Not found"></label>
            <label>2v2 Rating<input name="rocket_league_mmr_2v2" type="number" min="0" max="5000" value="${esc(context.account.rocket_league_mmr_2v2)}" placeholder="Not found"></label>
            <label>3v3 Rating<input name="rocket_league_mmr_3v3" type="number" min="0" max="5000" value="${esc(context.account.rocket_league_mmr_3v3)}" placeholder="Not found"></label>
          </div>
          <p class="player-tracker-note">Tracker may block automated requests. If it does, open the profile and enter the ratings manually.</p>
        </section>
        <p id="playerOverviewEditMessage" class="team-message span-2" role="status"></p>
        </form>
      </section>
    `;
  }

  function playerHomeNextMatch() {
    const matches = allMatches()
      .filter((match) => matchState(match) !== "completed")
      .sort((a, b) => {
        const aStart = matchStartTime(a);
        const bStart = matchStartTime(b);
        if (aStart && bStart) return aStart - bStart;
        if (aStart) return -1;
        if (bStart) return 1;
        return clean(a.scheduled_at || a.match_time || a.slot_date).localeCompare(clean(b.scheduled_at || b.match_time || b.slot_date));
      });
    return matches[0] || null;
  }

  function playerHomeNextAction(context: {
    account: AnyRow;
    selected: AnyRow;
    games: string[];
    pendingInvites: AnyRow[];
    memberships: AnyRow[];
    applications: AnyRow[];
  }) {
    const matches = allMatches();
    const checkinMatch = matches.find((match) => Boolean(match.match_checkin_is_open) && !match.current_team_checked_in);
    const liveResultMatch = matches.find((match) => matchState(match) === "live" && !match.result_submission);
    const missingPlatform = !first(context.account.steam_id, context.account.steam_epic_id, context.selected.steam_id, context.selected.steam_epic_id, context.account.epic_id, context.selected.epic_id, context.account.ps4_id, context.selected.ps4_id);
    if (context.pendingInvites.length) {
      return {
        label: "Action Needed",
        title: "Review team invite",
        copy: `${context.pendingInvites.length} roster invite${context.pendingInvites.length === 1 ? "" : "s"} waiting for your approval.`,
        action: "Captain Quarters",
        section: "requests",
        tone: "urgent",
      };
    }
    if (!context.games.length || missingPlatform) {
      return {
        label: "Profile Setup",
        title: !context.games.length ? "Choose your games" : "Add a platform ID",
        copy: !context.games.length ? "Select the games you play so captains can add you to the right rosters." : "Add Steam, Epic, or PSN details so match rosters and tracker links stay accurate.",
        action: "Edit Profile",
        section: "profile",
        tone: "setup",
      };
    }
    if (checkinMatch) {
      return {
        label: "Match Ready",
        title: "Check in is open",
        copy: `${first(checkinMatch.__league_name, checkinMatch.league_name, "Your competition")} has a match ready for check-in.`,
        action: "Open Competition Centre",
        section: "competitions",
        tone: "urgent",
      };
    }
    if (liveResultMatch) {
      return {
        label: "Live Match",
        title: "Submit or review result",
        copy: `Your match against ${first(liveResultMatch.opponent_name, "TBA")} is live.`,
        action: "Open Competition Centre",
        section: "competitions",
        tone: "urgent",
      };
    }
    if (playerHomeNextMatch()) {
      return {
        label: "Up Next",
        title: "Match scheduled",
        copy: "Your next match is ready to review with opponent, lobby, and check-in details.",
        action: "Open Competition Centre",
        section: "competitions",
        tone: "ready",
      };
    }
    if (!context.memberships.length) {
      return {
        label: "Get Started",
        title: "Join or create a team",
        copy: "Find an open competition or create a team from Captain Quarters when you are ready.",
        action: "Find Comps",
        section: "find-tournaments",
        tone: "setup",
      };
    }
    return {
      label: "Status",
      title: "No action needed",
      copy: context.applications.length ? "Your portal is up to date. Keep an eye on matches and competition notices." : "Your team links are ready. Competitions will appear once entered.",
      action: context.applications.length ? "View Competitions" : "Find Comps",
      section: context.applications.length ? "competitions" : "find-tournaments",
      tone: "ready",
    };
  }

  function profileReadinessItems(account: AnyRow, selected: AnyRow, memberships = playerMemberships()) {
    const steamId = first(account.steam_id, account.steam_epic_id, selected.steam_id, selected.steam_epic_id);
    const epicId = first(account.epic_id, selected.epic_id);
    const ps4Id = first(account.ps4_id, selected.ps4_id);
    const xboxId = first(account.xbox_id, selected.xbox_id);
    const games = playerGameTitles(account).length ? playerGameTitles(account) : playerGameTitles(selected);
    const tracker = first(account.player_tracker_link, selected.player_tracker_link, rocketLeagueTrackerLink(steamId, epicId, ps4Id, xboxId));
    return [
      {
        key: "identity",
        label: "Player identity",
        done: Boolean(first(account.in_game_name, account.display_name, selected.in_game_name, selected.player_name)),
        hint: "Alias or display name is set.",
        action: "Edit Profile",
        section: "profile",
        editProfile: true,
      },
      {
        key: "games",
        label: "Games selected",
        done: games.length > 0,
        hint: games.length ? games.join(", ") : "Choose at least one game you are available for.",
        action: "Edit Profile",
        section: "profile",
        editProfile: true,
      },
      {
        key: "platform",
        label: "Platform ID",
        done: Boolean(first(steamId, epicId, ps4Id)),
        hint: first(steamId && "Steam linked", epicId && "Epic linked", ps4Id && "PSN linked", "Add Steam, Epic, or PSN details."),
        action: "Edit Profile",
        section: "profile",
        editProfile: true,
      },
      {
        key: "tracker",
        label: "Tracker link",
        done: Boolean(tracker),
        hint: tracker ? "Tracker link is ready." : "A tracker link can be generated from platform details.",
        action: "Edit Profile",
        section: "profile",
        editProfile: true,
      },
      {
        key: "team",
        label: "Team membership",
        done: memberships.length > 0,
        hint: memberships.length ? `${memberships.length} linked team${memberships.length === 1 ? "" : "s"}.` : "Accept an invite or create a captain team.",
        action: memberships.length ? "Captain Quarters" : "Find Comps",
        section: memberships.length ? "settings" : "find-tournaments",
      },
      {
        key: "email",
        label: "Email verified",
        done: Boolean(account.email_verified),
        hint: account.email_verified ? "Email is verified." : "Verify email to keep account recovery reliable.",
        action: "Profile",
        section: "profile",
      },
    ];
  }

  function renderProfileReadiness(account: AnyRow, selected: AnyRow, memberships = playerMemberships()) {
    const items = profileReadinessItems(account, selected, memberships);
    const complete = items.filter((item) => item.done).length;
    return `
      <section class="player-readiness-card">
        <div class="player-readiness-head">
          <div>
            <span>Profile Readiness</span>
            <h3>${esc(complete)} / ${esc(items.length)} complete</h3>
          </div>
          <strong>${esc(Math.round((complete / items.length) * 100))}%</strong>
        </div>
        <div class="player-readiness-list">
          ${items.map((item) => `
            <button class="${item.done ? "is-done" : ""}" type="button" data-section-jump="${esc(item.section)}" ${item.editProfile ? `data-readiness-edit-profile="${esc(item.key)}"` : ""}>
              <span>${item.done ? "OK" : "!"}</span>
              <div><strong>${esc(item.label)}</strong><small>${esc(item.hint)}</small></div>
              <em>${esc(item.action)}</em>
            </button>
          `).join("")}
        </div>
      </section>
    `;
  }

  function renderStats() {
    const target = $("overviewStats");
    if (target) target.remove();
  }

  function renderNavBadges() {
    const counts: Record<string, number> = {
      teams: playerMemberships().length,
      requests: list(dashboard.rosterInvites).filter((invite) => clean(invite.status).toLowerCase() === "pending").length
        + list(dashboard.tradeRequests).filter((request) => ["pending", "open", "submitted"].includes(clean(request.status || "pending").toLowerCase())).length,
    };
    document.querySelectorAll<HTMLElement>(".team-manager-nav [data-section]").forEach((button) => {
      const section = button.dataset.section || "";
      const count = counts[section] || 0;
      let badge = button.querySelector<HTMLElement>(".team-nav-badge");
      if (!count) {
        badge?.remove();
        return;
      }
      if (!badge) {
        badge = document.createElement("span");
        badge.className = "team-nav-badge";
        button.appendChild(badge);
      }
      badge.textContent = String(count > 99 ? "99+" : count);
    });
  }

  function filteredRoster() {
    if (activeViewMode === "player") {
      const player = selectedPlayer();
      return player ? [player] : [];
    }
    const q = [($<HTMLInputElement>("teamRosterGlobalSearch")?.value || ""), ($<HTMLInputElement>("teamRosterSearchInput")?.value || "")]
      .join(" ").toLowerCase().trim();
    const game = $<HTMLSelectElement>("teamRosterGameFilter")?.value || "ALL";
    const access = $<HTMLSelectElement>("teamRosterAccessFilter")?.value || "ALL";
    const status = $<HTMLSelectElement>("teamRosterStatusFilter")?.value || "ALL";
    return rosterDraft.filter((player) => {
      const games = list(player.games).map(clean);
      const haystack = [player.player_name, player.in_game_name, player.role, player.email, playerPlatformId(player), playerSteamId(player), playerEpicId(player), player.access_level, ...games].join(" ").toLowerCase();
      return (!q || haystack.includes(q))
        && (game === "ALL" || games.includes(game))
        && (access === "ALL" || accessLevel(player.access_level) === access)
        && (status === "ALL" || rosterStatus(player.status) === status);
    });
  }

  function fillRosterFilters() {
    const gameSelect = $<HTMLSelectElement>("teamRosterGameFilter");
    if (!gameSelect) return;
    const current = gameSelect.value || "ALL";
    const games = [...new Set([
      ...list(dashboard.availableGameTitles).map(clean),
      ...rosterDraft.flatMap((player) => list(player.games).map(clean)),
      ...list(dashboard.applications).map((app) => clean(app.game_title)),
    ].filter(Boolean))].sort();
    gameSelect.innerHTML = `<option value="ALL">All Games</option>${games.map((game) => `<option value="${esc(game)}">${esc(game)}</option>`).join("")}`;
    gameSelect.value = current === "ALL" || games.includes(current) ? current : "ALL";
  }

  function availableGames() {
    return uniqueGames([
      ...list(dashboard.availableGameTitles).map(clean),
      ...list(dashboard.gameRosters).map((gameRoster) => clean(gameRoster.game_title)),
      ...list(dashboard.applications).map((app) => clean(app.game_title)),
      ...list(dashboard.openCompetitions).map((item) => clean(item.game_title)),
      ...playerMemberships().map((team) => clean(team.platform || team.team_platform)),
      clean(dashboard.account?.platform),
      ...playerGameTitles(dashboard.account || {}),
      ...rosterDraft.flatMap((player) => list(player.games).map(clean)),
    ]).sort((a, b) => a.localeCompare(b));
  }

  function fillCaptainGameSelect() {
    const createSelect = $<HTMLSelectElement>("captainTeamPlatform");
    const games = availableGames();
    const fallbackGames = games.length ? games : ["Rocket League"];
    if (createSelect) {
      const current = createSelect.value;
      createSelect.innerHTML = `<option value="">Select game...</option>${fallbackGames.map((game) => `<option value="${esc(game)}">${esc(game)}</option>`).join("")}`;
      if (current && !fallbackGames.includes(current)) createSelect.insertAdjacentHTML("beforeend", `<option value="${esc(current)}">${esc(current)}</option>`);
      createSelect.value = current || "";
    }
    fillTeamGamePicker();
  }

  function selectedEditTeamGames() {
    return teamGameTitles(selectedCaptainTeam() || dashboard.account || {});
  }

  function selectedGameTitles() {
    const checked = Array.from(document.querySelectorAll<HTMLInputElement>("#platformOptions input[type='checkbox']:checked"))
      .map((input) => input.value);
    return uniqueGames(checked);
  }

  function fillTeamGamePicker() {
    const picker = $("platformOptions");
    const primaryInput = $<HTMLInputElement>("platformInput");
    if (!picker) return;
    const selected = selectedEditTeamGames();
    const games = uniqueGames([...availableGames(), ...selected, "Rocket League"]).sort((a, b) => a.localeCompare(b));
    picker.innerHTML = games.map((game) => {
      const checked = selected.some((item) => item.toLowerCase() === game.toLowerCase());
      return `
        <label class="team-game-picker-option">
          <input type="checkbox" value="${esc(game)}" ${checked ? "checked" : ""}>
          <span>${esc(game)}</span>
        </label>
      `;
    }).join("");
    const syncPrimaryGame = () => {
      const selectedGames = selectedGameTitles();
      if (primaryInput) primaryInput.value = selectedGames[0] || selected[0] || "";
    };
    picker.querySelectorAll<HTMLInputElement>("input[type='checkbox']").forEach((input) => {
      input.addEventListener("change", syncPrimaryGame);
    });
    syncPrimaryGame();
  }

  function selectableGames(currentGames: string[]) {
    const assigned = new Set(currentGames.map((game) => game.toLowerCase()));
    return availableGames().filter((game) => !assigned.has(game.toLowerCase()));
  }

  function setPlayerGames(player: AnyRow, games: string[]) {
    player.games = uniqueGames(games);
    player.games_json = JSON.stringify(player.games);
  }

  function renderGameTag(game: string, key = "") {
    return `
      <span class="team-game-tag">
        ${esc(game)}
        ${key ? `<button type="button" data-remove-game="${esc(game)}" data-game-player="${esc(key)}" aria-label="Remove ${esc(game)}">x</button>` : ""}
      </span>
    `;
  }

  function renderGameSelectOptions(currentGames: string[]) {
    const options = selectableGames(currentGames);
    return `<option value="">Select game...</option>${options.map((game) => `<option value="${esc(game)}">${esc(game)}</option>`).join("")}`;
  }

  function captainPlayerLookupInputId() {
    return clean($<HTMLInputElement>("captainPlayerBracketrId")?.value).toUpperCase();
  }

  function resetCaptainPlayerLookup(message = "Enter a Bracketr Player ID to preview the player before adding them.") {
    captainPlayerLookup = null;
    captainPlayerLookupId = "";
    const preview = $("captainPlayerLookupPreview");
    if (preview) preview.innerHTML = `<p class="team-empty compact">${esc(message)}</p>`;
    const submit = $<HTMLButtonElement>("captainAddPlayerSubmit");
    if (submit) submit.disabled = true;
  }

  function renderCaptainPlayerLookupPreview() {
    const preview = $("captainPlayerLookupPreview");
    const submit = $<HTMLButtonElement>("captainAddPlayerSubmit");
    if (!preview) return;
    const typedId = captainPlayerLookupInputId();
    const player = captainPlayerLookup;
    const canAdd = Boolean(player
      && typedId
      && typedId === captainPlayerLookupId
      && player.portal_linked
      && !player.already_on_roster
      && !player.pending_invite
      && player.game_match
      && clean(player.status).toLowerCase() !== "inactive");
    if (submit) submit.disabled = !canAdd;
    if (!player || typedId !== captainPlayerLookupId) {
      resetCaptainPlayerLookup();
      return;
    }
    const logo = previewAssetUrl(player.player_logo_path);
    const status = player.already_on_roster
      ? "Already on roster"
      : player.pending_invite
        ? "Invite pending"
        : !player.portal_linked
          ? "Portal not linked"
          : !list(player.games).length
            ? "No games selected"
            : !player.game_match
              ? "No shared team game"
              : clean(player.status).toLowerCase() === "inactive"
                ? "Inactive"
                : "Ready to add";
    const detailBits = [
      player.bracketr_player_id,
      list(player.games).length ? `Games ${list(player.games).map(clean).join(", ")}` : "",
      player.email,
      player.steam_id ? `Steam ${player.steam_id}` : "",
      player.epic_id ? `Epic ${player.epic_id}` : "",
      player.ps4_id ? `PSN ${player.ps4_id}` : "",
    ].filter(Boolean);
    preview.innerHTML = `
      <article class="captain-player-preview ${canAdd ? "is-ready" : "is-blocked"}">
        <span class="registry-avatar">${logo ? `<img src="${esc(logo)}" alt="">` : esc(initials(first(player.in_game_name, player.player_name, "P")))}</span>
        <div>
          <strong>${esc(first(player.in_game_name, player.player_name, "Player"))}</strong>
          <small>${esc(detailBits.join(" / ") || "Registered player")}</small>
        </div>
        ${statusPill(status)}
      </article>
    `;
  }

  async function lookupCaptainPlayerById() {
    const id = captainPlayerLookupInputId();
    window.clearTimeout(captainPlayerLookupTimer);
    if (!id) {
      resetCaptainPlayerLookup();
      return;
    }
    const preview = $("captainPlayerLookupPreview");
    const submit = $<HTMLButtonElement>("captainAddPlayerSubmit");
    if (submit) submit.disabled = true;
    if (preview) preview.innerHTML = `<p class="team-empty compact">Looking up ${esc(id)}...</p>`;
    try {
      const teamId = selectedCaptainTeam()?.team_account_id || dashboard.account?.selected_team_account_id || "";
      const result = await api(`/api/team-portal/captain-teams/current/player-lookup/${encodeURIComponent(id)}?team_account_id=${encodeURIComponent(String(teamId || ""))}`);
      if (id !== captainPlayerLookupInputId()) return;
      captainPlayerLookup = result.player || null;
      captainPlayerLookupId = id;
      renderCaptainPlayerLookupPreview();
    } catch (error) {
      captainPlayerLookup = null;
      captainPlayerLookupId = id;
      if (preview) preview.innerHTML = `<p class="team-empty compact">${esc((error as Error).message || "Player not found.")}</p>`;
      if (submit) submit.disabled = true;
    }
  }

  function renderRoster() {
    const panel = document.querySelector<HTMLElement>('[data-panel="roster"]');
    const overviewOnly = activeSection === "roster" && activeViewMode === "player";
    panel?.classList.toggle("hidden", overviewOnly);
    if (overviewOnly) return;
    fillRosterFilters();
    const rows = filteredRoster();
    const isCaptainView = activeViewMode === "manager";
    setText("rosterPanelTitle", isCaptainView ? "Captain Roster" : "My Player Records");
    setText("rosterPanelCopy", isCaptainView
      ? "Manage the roster records, roles, game assignments, and player access for teams you captain."
      : "Your player profile can appear on different teams across different competitions.");
    setText("teamRosterShowing", rows.length ? `Showing ${rows.length} of ${rosterDraft.length} records` : "Showing 0 records");
    const target = $("rosterRows");
    if (!target) return;
    target.innerHTML = rows.length ? rows.map((player) => {
      const index = rosterDraft.indexOf(player);
      const key = playerKey(player, index);
      const games = list(player.games).map(clean).filter(Boolean);
      return `
        <article class="registry-row ${key === selectedPlayerId ? "selected" : ""}" data-player-id="${esc(key)}">
          <input type="checkbox" aria-label="Select ${esc(first(player.in_game_name, player.player_name, "Player"))}">
          <div class="registry-identity"><span class="registry-avatar">${esc(initials(first(player.in_game_name, player.player_name)))}</span><span><strong>${esc(first(player.in_game_name, player.player_name, "Player"))}</strong><small>${esc(first(player.team_name, playerRecordLabel(player), player.email, "No linked team"))}</small></span></div>
          <span class="team-game-cell">${games.length ? games.map((game) => `<span class="registry-pill blue">${esc(game)}</span>`).join("") : `<span class="registry-pill">No game</span>`}</span>
          ${statusPill(accessLevel(player.access_level))}
          ${statusPill(rosterStatus(player.status))}
          <span>${esc(first(playerPlatformId(player), "Generating"))}</span>
          <button class="registry-icon-btn" type="button" data-view-player="${esc(key)}">View</button>
          <button class="registry-icon-btn" type="button" data-edit-player="${esc(key)}">...</button>
        </article>
      `;
    }).join("") : `<p class="team-empty">No player records found.</p>`;
    renderPlayerDetail();
  }

  function selectedPlayer() {
    return rosterDraft.find((player, index) => playerKey(player, index) === selectedPlayerId) || filteredRoster()[0] || rosterDraft[0];
  }

  function renderPlayerDetail() {
    const panel = $("teamPlayerDetailPanel");
    if (!panel) return;
    const player = selectedPlayer();
    if (!player) {
      panel.innerHTML = `<section class="registry-detail-section"><h3>No player selected</h3><p class="team-empty">Player records appear here once this account is linked to a roster.</p></section>`;
      return;
    }
    const index = rosterDraft.indexOf(player);
    selectedPlayerId = playerKey(player, index);
    const games = list(player.games).map(clean).filter(Boolean);
    panel.innerHTML = `
      <article class="registry-detail-card">
        <div class="registry-detail-title">
          <span class="registry-avatar" style="width:58px;height:58px">${esc(initials(first(player.in_game_name, player.player_name)))}</span>
          <div>
            <h2>${esc(first(player.in_game_name, player.player_name, "Player"))}</h2>
            <p>${esc(playerRecordLabel(player))}</p>
            <div class="registry-chip-row">${statusPill(accessLevel(player.access_level))}${statusPill(rosterStatus(player.status))}</div>
          </div>
        </div>
        <div class="registry-detail-tabs"><button class="active" type="button">Overview</button><button type="button">Games</button><button type="button">History</button></div>
        <section class="registry-detail-section"><h3>Player Info</h3><div class="registry-detail-list">
          <div><span>Team</span><strong>${esc(first(player.team_name, "No team linked"))}</strong></div>
          <div><span>Team Role</span><strong>${esc(first(player.role, "Player"))}</strong></div>
          <div><span>Portal Access</span><strong>${esc(accessLevel(player.access_level) === "manager" ? "Captain" : "Player")}</strong></div>
          <div><span>Market Status</span><strong>${esc(tradeStatusLabel(player.trade_status))}</strong></div>
          <div><span>Games</span><strong>${esc(games.join(", ") || "Not assigned")}</strong></div>
          <div><span>Platform ID</span><strong>${esc(first(playerPlatformId(player), "Generating"))}</strong></div>
          <div><span>Steam ID</span><strong>${esc(first(playerSteamId(player), "Not supplied"))}</strong></div>
          <div><span>Epic ID</span><strong>${esc(first(playerEpicId(player), "Not supplied"))}</strong></div>
          <div><span>Email</span><strong>${esc(first(player.email, "Not linked"))}</strong></div>
          <div><span>Status</span><strong>${esc(rosterStatus(player.status))}</strong></div>
        </div></section>
        <section class="registry-detail-section">
          <h3>Game Assignments</h3>
          <div class="team-game-assignment">
            <select data-game-select="${esc(selectedPlayerId)}" aria-label="Select game for ${esc(first(player.in_game_name, player.player_name, "player"))}">
              ${renderGameSelectOptions(games)}
            </select>
            <button class="registry-icon-btn team-game-add-btn" type="button" data-add-game="${esc(selectedPlayerId)}" aria-label="Add game">+</button>
          </div>
          <div class="team-game-chip-row">
            ${games.length ? games.map((game) => renderGameTag(game, selectedPlayerId)).join("") : `<span class="team-empty compact">No games assigned.</span>`}
          </div>
        </section>
        <section class="registry-detail-section"><h3>Access Rules</h3><p class="team-detail-copy">${accessLevel(player.access_level) === "manager" ? "Captain access can update roster records, game assignments, team settings, and submit team requests." : "Player access can view linked teams, competitions, matches, and roster status without team management controls."}</p></section>
        <section class="registry-detail-section"><div class="registry-actions"><button class="brk-btn primary manager-only" type="button" data-edit-player="${esc(selectedPlayerId)}">Edit Record</button><button class="brk-btn" type="button" data-section-jump="requests">Captain Quarters</button></div></section>
      </article>
    `;
  }

  function buildGameRosters() {
    const grouped = new Map<string, AnyRow[]>();
    rosterDraft.forEach((player, index) => {
      const cleanPlayer = cleanPlayerForSave(player, index);
      list(player.games).map(clean).filter(Boolean).forEach((game) => {
        if (!grouped.has(game)) grouped.set(game, []);
        grouped.get(game)?.push(cleanPlayer);
      });
    });
    return [...grouped.entries()].map(([game_title, roster]) => ({ game_title, roster }));
  }

  function cleanPlayerForSave(player: AnyRow, index: number) {
    const games = list(player.games).map(clean).filter(Boolean);
    return {
      player_name: clean(player.player_name),
      in_game_name: clean(player.in_game_name),
      role: clean(player.role),
      steam_epic_id: playerSteamId(player),
      steam_id: playerSteamId(player),
      epic_id: playerEpicId(player),
      ps4_id: clean(player.ps4_id),
      xbox_id: clean(player.xbox_id),
      rocket_league_mmr_1v1: clean(player.rocket_league_mmr_1v1),
      rocket_league_mmr_2v2: clean(player.rocket_league_mmr_2v2),
      rocket_league_mmr_3v3: clean(player.rocket_league_mmr_3v3),
      player_tracker_link: clean(player.player_tracker_link),
      bracketr_player_id: playerPlatformId(player),
      access_level: accessLevel(player.access_level),
      games,
      games_json: JSON.stringify(games),
      email: clean(player.email).toLowerCase(),
      status: rosterStatus(player.status),
      trade_status: tradeStatus(player.trade_status),
      sort_order: index,
    };
  }

  async function saveProfile(messageId = "rosterMessage") {
    rosterDraft = rosterDraft.map(cleanPlayerForSave).filter((player) => clean(player.in_game_name));
    const account = dashboard.account || {};
    const form = new FormData();
    form.append("team_name", first($<HTMLInputElement>("teamNameInput")?.value, account.team_name));
    form.append("short_name", first($<HTMLInputElement>("shortNameInput")?.value, account.short_name));
    form.append("contact_name", first($<HTMLInputElement>("contactNameInput")?.value, account.contact_name));
    form.append("discord", $<HTMLInputElement>("discordInput")?.value || account.discord || "");
    form.append("region", $<HTMLSelectElement>("regionInput")?.value || "");
    const gameTitles = selectedGameTitles();
    form.append("platform", gameTitles[0] || account.platform || "");
    form.append("game_titles_json", JSON.stringify(gameTitles.length ? gameTitles : teamGameTitles(account)));
    form.append("bio", $<HTMLTextAreaElement>("bioInput")?.value || account.bio || "");
    form.append("website_url", $<HTMLInputElement>("websiteInput")?.value || account.website_url || "");
    form.append("social_x", $<HTMLInputElement>("socialXInput")?.value || account.social_x || "");
    form.append("social_youtube", $<HTMLInputElement>("socialYoutubeInput")?.value || account.social_youtube || "");
    form.append("availability_note", $<HTMLTextAreaElement>("availabilityInput")?.value || account.availability_note || "");
    form.append("preferred_contact", $<HTMLInputElement>("preferredContactInput")?.value || account.preferred_contact || "");
    form.append("rosterJson", JSON.stringify(rosterDraft));
    form.append("gameRostersJson", JSON.stringify(buildGameRosters()));

    const logo = $<HTMLInputElement>("logoInput")?.files?.[0];
    const banner = $<HTMLInputElement>("bannerInput")?.files?.[0];
    if (logo) form.append("logo", logo);
    if (banner) form.append("banner", banner);

    setMessage(messageId, "Saving...");
    const result = await api("/api/team-portal/update-profile", { method: "POST", body: form });
    dashboard = result.dashboard || dashboard;
    revokePreviewUrl(selectedLogoPreviewUrl);
    revokePreviewUrl(selectedBannerPreviewUrl);
    selectedLogoPreviewUrl = "";
    selectedBannerPreviewUrl = "";
    const logoInput = $<HTMLInputElement>("logoInput");
    const bannerInput = $<HTMLInputElement>("bannerInput");
    if (logoInput) logoInput.value = "";
    if (bannerInput) bannerInput.value = "";
    hydrateRoster();
    setMessage(messageId, result.message || "Saved.", "ok");
    render();
  }

  async function saveCaptainTeamProfile(messageId = "settingsMessage") {
    const team = selectedCaptainTeam();
    const teamId = Number(team?.team_account_id || dashboard.account?.selected_team_account_id || 0);
    if (!teamId) return saveProfile(messageId);

    const form = new FormData();
    form.append("team_account_id", String(teamId));
    form.append("team_name", first($<HTMLInputElement>("teamNameInput")?.value, team?.team_name));
    form.append("short_name", first($<HTMLInputElement>("shortNameInput")?.value, team?.short_name));
    form.append("contact_name", first($<HTMLInputElement>("contactNameInput")?.value, team?.contact_name));
    form.append("discord", $<HTMLInputElement>("discordInput")?.value || team?.discord || "");
    form.append("region", $<HTMLSelectElement>("regionInput")?.value || "");
    const gameTitles = selectedGameTitles();
    form.append("platform", gameTitles[0] || team?.platform || team?.team_platform || "");
    form.append("game_titles_json", JSON.stringify(gameTitles.length ? gameTitles : teamGameTitles(team || {})));
    form.append("bio", $<HTMLTextAreaElement>("bioInput")?.value || team?.bio || "");
    form.append("website_url", $<HTMLInputElement>("websiteInput")?.value || team?.website_url || "");
    form.append("social_x", $<HTMLInputElement>("socialXInput")?.value || team?.social_x || "");
    form.append("social_youtube", $<HTMLInputElement>("socialYoutubeInput")?.value || team?.social_youtube || "");
    form.append("availability_note", $<HTMLTextAreaElement>("availabilityInput")?.value || team?.availability_note || "");
    form.append("preferred_contact", $<HTMLInputElement>("preferredContactInput")?.value || team?.preferred_contact || "");

    const logo = $<HTMLInputElement>("logoInput")?.files?.[0];
    const banner = $<HTMLInputElement>("bannerInput")?.files?.[0];
    if (logo) form.append("logo", logo);
    if (banner) form.append("banner", banner);

    setMessage(messageId, "Saving...");
    const result = await api(`/api/team-portal/captain-teams/${encodeURIComponent(String(teamId))}`, { method: "POST", body: form });
    dashboard = result.dashboard || dashboard;
    revokePreviewUrl(selectedLogoPreviewUrl);
    revokePreviewUrl(selectedBannerPreviewUrl);
    selectedLogoPreviewUrl = "";
    selectedBannerPreviewUrl = "";
    const logoInput = $<HTMLInputElement>("logoInput");
    const bannerInput = $<HTMLInputElement>("bannerInput");
    if (logoInput) logoInput.value = "";
    if (bannerInput) bannerInput.value = "";
    hydrateRoster();
    setMessage(messageId, result.message || "Team updated.", "ok");
    render();
  }

  function renderMatchSummary(match: AnyRow) {
    const opponentRegion = clean(match.team_is_team1 ? match.team2_region : match.team1_region);
    return `
      <div class="team-match-summary">
        <strong>${esc(match.__league_name || match.league_name || "Competition")}</strong>
        <span>${esc(match.round_name || "Match")} ${match.match_number ? `#${esc(match.match_number)}` : ""}</span>
        <span>Opponent: ${esc(match.opponent_name || "TBA")}${opponentRegion ? ` (${esc(opponentRegion)})` : ""}</span>
        <span>${esc(formatMatchSlot(match))}</span>
        ${statusPill(match.status || matchState(match))}
      </div>
    `;
  }

  function matchTeamLabel(match: AnyRow, side: "team1" | "team2") {
    const name = side === "team1" ? match.team1_name : match.team2_name;
    const region = clean(side === "team1" ? match.team1_region : match.team2_region);
    return `${first(name, side === "team1" ? "Team 1" : "Team 2")}${region ? ` (${region})` : ""}`;
  }

  function formatMatchSlot(match: AnyRow) {
    const slotDate = clean(match.slot_date);
    const slotTime = clean(match.slot_time);
    if (slotDate && slotTime) return formatDate(`${slotDate}T${slotTime}`);
    if (slotDate) return formatDate(`${slotDate}T00:00:00`);
    if (slotTime) return slotTime;
    return formatDate(match.scheduled_at || match.match_time || match.start_time);
  }

  function matchStartTime(match: AnyRow) {
    const explicit = clean(match.match_starts_at);
    if (explicit) {
      const ms = new Date(explicit).getTime();
      if (Number.isFinite(ms)) return ms;
    }
    const slotDate = clean(match.slot_date);
    const slotTime = clean(match.slot_time).match(/\d{1,2}:\d{2}/)?.[0] || "";
    if (slotDate && slotTime) {
      const ms = new Date(`${slotDate}T${slotTime}:00`).getTime();
      if (Number.isFinite(ms)) return ms;
    }
    return 0;
  }

  function formatDuration(ms: number) {
    const totalSeconds = Math.max(0, Math.floor(Math.abs(ms) / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours) return `${hours}h ${minutes}m`;
    if (minutes) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  function matchCounterText(startMs: number) {
    if (!startMs) return "No round time assigned";
    const delta = Date.now() - startMs;
    return delta >= 0 ? `Started ${formatDuration(delta)} ago` : `Starts in ${formatDuration(delta)}`;
  }

  function updateMatchCounters() {
    document.querySelectorAll<HTMLElement>("[data-match-counter]").forEach((el) => {
      const startMs = Number(el.dataset.matchStart || 0);
      el.textContent = matchCounterText(startMs);
      el.dataset.state = startMs && Date.now() >= startMs ? "elapsed" : "pending";
    });
    document.querySelectorAll<HTMLElement>("[data-checkin-window-counter]").forEach((el) => {
      const opensMs = Number(el.dataset.checkinOpens || 0);
      const closesMs = Number(el.dataset.checkinCloses || 0);
      const now = Date.now();
      if (!opensMs || !closesMs) el.textContent = "Match time required";
      else if (now < opensMs) el.textContent = `Opens in ${formatDuration(opensMs - now)}`;
      else if (now <= closesMs) el.textContent = `Closes in ${formatDuration(closesMs - now)}`;
      else el.textContent = "Check-in closed";
    });
  }

  function matchTeamCheckin(match: AnyRow, side: "team1" | "team2") {
    const checked = side === "team1" ? Boolean(match.team1_checked_in) : Boolean(match.team2_checked_in);
    const at = clean(side === "team1" ? match.team1_checked_in_at : match.team2_checked_in_at);
    if (checked && at) return `Checked in - ${at}`;
    return checked ? "Checked in" : "Not checked in";
  }

  function matchMaps(match: AnyRow) {
    return safeJson(match.map_selections || match.map_selections_json)
      .map((map) => clean(map.name || map.map || map))
      .filter(Boolean);
  }

  function opponentRoster(match: AnyRow) {
    return list(match.team_is_team1 ? match.team2_roster : match.team1_roster);
  }

  function rosterPlayerName(player: AnyRow) {
    return first(player.in_game_name, player.display_name, player.player_name, player.name, "Unnamed player");
  }

  function renderOpponentLineup(match: AnyRow) {
    const roster = opponentRoster(match);
    if (!roster.length) return `<p class="team-empty compact">No opposition lineup supplied yet.</p>`;
    return `
      <ul class="team-match-lineup">
        ${roster.map((player) => `
          <li>
            <span class="team-lineup-avatar">${esc(initials(rosterPlayerName(player)))}</span>
            <div>
              <strong>${esc(rosterPlayerName(player))}</strong>
              <small>${esc(first(player.role, player.access_level, "Player"))}${playerPlatformId(player) ? ` - ${esc(playerPlatformId(player))}` : ""}</small>
            </div>
          </li>
        `).join("")}
      </ul>
    `;
  }

  function renderMatchInfoPanel(title: string, rows: [string, unknown][]) {
    return `
      <section class="team-match-info-panel">
        <h4>${esc(title)}</h4>
        ${rows.map(([label, value]) => `
          <div class="team-match-info-row">
            <span>${esc(label)}</span>
            <strong>${label === "Counter" ? value : esc(first(value, "Not supplied"))}</strong>
          </div>
        `).join("")}
      </section>
    `;
  }

  function renderLeagueThread(match: AnyRow) {
    const messages = list(match.league_messages).slice(-8);
    return `
      <section class="team-league-thread">
        <div class="team-thread-head">
          <div>
            <h4>League Admin Chat</h4>
            <span>League notices and messages to admins</span>
          </div>
        </div>
        <div class="team-thread-list">
          ${messages.length ? messages.map((item) => `
            <article class="team-thread-message ${clean(item.notice_type) === "team_message" ? "from-team" : ""}">
              <div>
                <strong>${esc(first(item.title, item.notice_type, "League notice"))}</strong>
                <small>${esc(first(item.actor_name, "League Admin"))} - ${esc(formatDate(item.created_at))}</small>
              </div>
              <p>${esc(item.message)}</p>
            </article>
          `).join("") : `<p class="team-empty compact">No league notices or admin messages yet.</p>`}
        </div>
        <form class="team-thread-form" data-league-message="${esc(match.league_id)}">
          <input name="message" type="text" maxlength="1000" placeholder="Message league admins..." required />
          <button class="brk-btn primary" type="submit">Send</button>
        </form>
      </section>
    `;
  }

  function renderMatches() {
    const target = $("matchesList");
    if (!target) return;
    const applications = list(dashboard.applications);
    const normalizedSearch = matchCompetitionSearch.toLowerCase();
    const filteredApplications = applications.filter((app) => {
      const rows = competitionMatches(app);
      const states = rows.map(matchState);
      const textMatches = !normalizedSearch || [app.league_name, app.game_title, app.format].some((value) => clean(value).toLowerCase().includes(normalizedSearch));
      const gameMatches = matchCompetitionGame === "all" || clean(app.game_title) === matchCompetitionGame;
      const statusMatches = matchCompetitionStatus === "all"
        || (matchCompetitionStatus === "live" && states.includes("live"))
        || (matchCompetitionStatus === "upcoming" && states.includes("upcoming"))
        || (matchCompetitionStatus === "completed" && rows.length > 0 && states.every((state) => state === "completed"))
        || (matchCompetitionStatus === "action" && rows.some((match) => Boolean(match.match_checkin_is_open) && !match.current_team_checked_in));
      return textMatches && gameMatches && statusMatches;
    }).sort((a, b) => {
      if (matchCompetitionSort === "name") return clean(a.league_name).localeCompare(clean(b.league_name));
      if (matchCompetitionSort === "matches") return competitionMatches(b).length - competitionMatches(a).length;
      const nextA = nextPlayableMatch(competitionMatches(a));
      const nextB = nextPlayableMatch(competitionMatches(b));
      return (nextA ? matchStartTime(nextA) : Number.MAX_SAFE_INTEGER) - (nextB ? matchStartTime(nextB) : Number.MAX_SAFE_INTEGER);
    });
    const preferredApp = filteredApplications.find((app) => competitionKey(app) === selectedMatchCompetitionId)
      || filteredApplications.find((app) => list(app.matches).length)
      || filteredApplications[0]
      || applications[0]
      || null;
    if (preferredApp) selectedMatchCompetitionId = competitionKey(preferredApp);
    const isOpenLadderCompetition = clean(preferredApp?.format).toLowerCase() === "open_ladder";
    const ladderTeams = isOpenLadderCompetition
      ? list(preferredApp?.ladder_teams).filter((team) => clean(team.ladder_status || "active").toLowerCase() !== "removed")
      : [];
    const ownLadderTeamId = clean(preferredApp?.created_team_id);
    const selectedLadderTeam = ladderTeams.find((team) => clean(team.id) === selectedMatchLadderTeamId)
      || ladderTeams.find((team) => clean(team.id) === ownLadderTeamId)
      || ladderTeams[0]
      || null;
    if (selectedLadderTeam) selectedMatchLadderTeamId = clean(selectedLadderTeam.id);
    const scopedAll = preferredApp ? competitionMatches(preferredApp).sort((a, b) => (matchStartTime(a) || Number.MAX_SAFE_INTEGER) - (matchStartTime(b) || Number.MAX_SAFE_INTEGER)
      || clean(a.scheduled_at || a.match_time).localeCompare(clean(b.scheduled_at || b.match_time))) : [];
    const matches = scopedAll
      .filter((match) => activeMatchFilter === "all" || matchState(match) === activeMatchFilter)
      .sort((a, b) => (matchStartTime(a) || Number.MAX_SAFE_INTEGER) - (matchStartTime(b) || Number.MAX_SAFE_INTEGER)
        || clean(a.scheduled_at || a.match_time).localeCompare(clean(b.scheduled_at || b.match_time)));
    const selected = matches.find((match) => matchWorkspaceKey(match) === selectedMatchId)
      || nextPlayableMatch(matches)
      || matches[0]
      || scopedAll.find((match) => matchWorkspaceKey(match) === selectedMatchId)
      || null;
    if (selected) selectedMatchId = matchWorkspaceKey(selected);
    const counts = {
      all: scopedAll.length,
      live: scopedAll.filter((match) => matchState(match) === "live").length,
      upcoming: scopedAll.filter((match) => matchState(match) === "upcoming").length,
      completed: scopedAll.filter((match) => matchState(match) === "completed").length,
    };
    const visibleGroups = [
      ["live", "Live"],
      ["upcoming", "Upcoming"],
      ["completed", "Completed"],
    ];
    const queue = matches.length ? visibleGroups.map(([state, title]) => {
      const rows = matches.filter((match) => matchState(match) === state);
      if (!rows.length && activeMatchFilter !== "all") return "";
      return `
        <section class="player-match-group">
          <div class="player-match-group-head"><h3>${esc(title)}</h3><span>${esc(rows.length)}</span></div>
          ${rows.length ? rows.map(renderMatchWorkspaceRow).join("") : `<p class="team-empty compact">No ${esc(title.toLowerCase())} found.</p>`}
        </section>
      `;
    }).join("") : `<p class="team-empty">No matches found for this filter.</p>`;
    const ladderQueue = isOpenLadderCompetition ? renderMatchWorkspaceLadder(preferredApp || {}, ladderTeams, selectedLadderTeam) : "";
    const allPortalMatches = applications.flatMap(competitionMatches);
    const liveFocus = allPortalMatches.filter((match) => matchState(match) === "live").length;
    const upcomingFocus = allPortalMatches.filter((match) => matchState(match) === "upcoming").length;
    const checkinFocus = allPortalMatches.filter((match) => Boolean(match.match_checkin_is_open) && !match.current_team_checked_in).length;
    target.innerHTML = `
      <section class="player-match-command" aria-label="Competitions and matches workspace">
        <header class="player-match-page-head">
          <div>
            <span>Player Portal</span>
            <h1>Competition Centre</h1>
            <p>Track your entries, upcoming fixtures, match check-ins and results.</p>
            <div class="player-match-inline-stats" aria-label="Competition summary">
              <span><small>Live</small><b class="live">${liveFocus}</b></span>
              <span><small>Upcoming</small><b class="upcoming">${upcomingFocus}</b></span>
              <span><small>Check-in open</small><b class="checkin">${checkinFocus}</b></span>
            </div>
          </div>
          <button class="team-primary-btn" type="button" data-section-jump="find-tournaments">Find Competition</button>
        </header>
        <div class="player-match-toolbar">
          <label class="player-match-search"><span aria-hidden="true">⌕</span><input id="matchCompetitionSearch" type="search" value="${esc(matchCompetitionSearch)}" placeholder="Search competitions..." aria-label="Search competitions"></label>
          <select id="matchCompetitionGame" aria-label="Filter by game"><option value="all">All Games</option>${[...new Set(applications.map((app) => clean(app.game_title)).filter(Boolean))].map((game) => `<option value="${esc(game)}" ${game === matchCompetitionGame ? "selected" : ""}>${esc(game)}</option>`).join("")}</select>
          <select id="matchCompetitionStatus" aria-label="Filter by status"><option value="all">All Statuses</option><option value="live" ${matchCompetitionStatus === "live" ? "selected" : ""}>Live</option><option value="upcoming" ${matchCompetitionStatus === "upcoming" ? "selected" : ""}>Upcoming</option><option value="action" ${matchCompetitionStatus === "action" ? "selected" : ""}>Requires Action</option><option value="completed" ${matchCompetitionStatus === "completed" ? "selected" : ""}>Completed</option></select>
          <select id="matchCompetitionSort" aria-label="Sort competitions"><option value="next">Sort: Next Match</option><option value="name" ${matchCompetitionSort === "name" ? "selected" : ""}>Competition Name</option><option value="matches" ${matchCompetitionSort === "matches" ? "selected" : ""}>Most Matches</option></select>
        </div>
        ${!filteredApplications.length ? `<div class="player-match-filter-empty" role="status"><span>No competitions match these filters.</span><button type="button" data-reset-match-filters>Reset filters</button></div>` : ""}
      <section class="match-workspace captain-layout ${matchCompetitionColumnCollapsed ? "competition-column-collapsed" : ""}">
        <aside class="captain-column match-competition-column ${matchCompetitionColumnCollapsed ? "is-collapsed" : ""}">
          <button class="match-competition-collapse" type="button" data-match-competition-collapse aria-label="${matchCompetitionColumnCollapsed ? "Expand competitions" : "Collapse competitions"}" title="${matchCompetitionColumnCollapsed ? "Expand competitions" : "Collapse competitions"}">
            <span aria-hidden="true">${matchCompetitionColumnCollapsed ? "›" : "‹"}</span>
            <strong>Competitions</strong>
          </button>
          <div class="match-competition-column-content">
          <div class="captain-column-head">
            <div><h3>My Competitions</h3><p>${filteredApplications.length} entered</p></div>
            <button type="button" data-section-jump="find-tournaments" aria-label="Find competitions">+</button>
          </div>
          <div class="match-competition-list">
            ${filteredApplications.length ? filteredApplications.map((app) => {
              const rows = competitionMatches(app);
              const live = rows.filter((match) => matchState(match) === "live").length;
              const upcoming = rows.filter((match) => matchState(match) === "upcoming").length;
              const completed = rows.length > 0 && rows.every((match) => matchState(match) === "completed");
              return `
                <button class="captain-comp-row ${competitionKey(app) === selectedMatchCompetitionId ? "selected" : ""}" type="button" data-match-competition-select="${esc(competitionKey(app))}" aria-current="${competitionKey(app) === selectedMatchCompetitionId ? "true" : "false"}">
                  <span class="registry-avatar">${esc(initials(app.league_name || "C"))}</span>
                  <div>
                    <strong>${esc(app.league_name || "Competition")}</strong>
                    <small>${esc(first(app.game_title, "Game"))} · ${esc(openCompetitionType(app.format))}</small>
                  </div>
                  <div>
                    ${statusPill(live ? "live" : upcoming ? "upcoming" : completed ? "completed" : "no matches")}
                    <small>${rows.length} match${rows.length === 1 ? "" : "es"}</small>
                  </div>
                </button>
              `;
            }).join("") : `<div class="player-match-empty"><strong>No competitions match your search.</strong><p>Try another search or reset your filters.</p><button type="button" data-reset-match-filters>Reset Filters</button></div>`}
          </div>
          <button class="player-view-all" type="button" data-section-jump="find-tournaments">View all competitions →</button>
          </div>
        </aside>

        <section class="captain-column match-queue-column">
          <div class="captain-column-head">
            <div class="player-selected-comp"><span class="registry-avatar large">${esc(initials(preferredApp?.league_name || "C"))}</span><div><small>${esc(first(preferredApp?.game_title, "Competition"))}</small><h3>${esc(preferredApp?.league_name || "Match Queue")}</h3><p>${esc(openCompetitionType(preferredApp?.format))} · ${esc(first(preferredApp?.competition_stage, "Entered"))} · ${competitionMatches(preferredApp || {}).length} matches</p></div></div>
            ${preferredApp ? `<div class="team-inline-actions">
              ${isOpenLadderCompetition ? `<button class="team-primary-btn" type="button" data-open-competition="${esc(competitionKey(preferredApp))}" data-open-competition-tab="challenges">Ladder Challenges</button>` : ""}
              <button class="team-secondary-btn" type="button" data-open-competition="${esc(competitionKey(preferredApp))}">Open Competition Hub ↗</button>
            </div>` : ""}
          </div>
          ${isOpenLadderCompetition ? "" : `<div class="captain-card-tabs match-state-tabs" role="tablist" aria-label="Match state filter">
            ${[
              ["all", "All", counts.all],
              ["live", "Live", counts.live],
              ["upcoming", "Upcoming", counts.upcoming],
              ["completed", "Completed", counts.completed],
            ].map(([key, label, count]) => `
              <button class="${activeMatchFilter === key ? "is-active" : ""}" type="button" data-match-workspace-filter="${esc(String(key))}">${esc(String(label))} ${esc(count)}</button>
            `).join("")}
          </div>`}
          <div class="match-workspace-list ${isOpenLadderCompetition ? "is-ladder" : ""}">${isOpenLadderCompetition ? ladderQueue : queue}</div>
        </section>

        <aside class="captain-column match-detail-column">
          ${isOpenLadderCompetition ? renderMatchWorkspaceLadderDetail(preferredApp || {}, selectedLadderTeam) : selected ? renderMatchWorkspaceDetail(selected) : `
            <div class="captain-detail-head"><div><span class="registry-avatar large">M</span><div><h3>No match selected</h3><p>Matches will appear here once scheduled.</p></div></div></div>
            <p class="team-empty compact">No match details are available for this filter.</p>
          `}
        </aside>
      </section>
      </section>
    `;
    $("matchCompetitionSearch")?.addEventListener("input", (event) => {
      matchCompetitionSearch = (event.target as HTMLInputElement).value;
      window.clearTimeout(discoverySearchTimer);
      discoverySearchTimer = window.setTimeout(renderMatches, 180);
    });
    $("matchCompetitionGame")?.addEventListener("change", (event) => { matchCompetitionGame = (event.target as HTMLSelectElement).value; renderMatches(); });
    $("matchCompetitionStatus")?.addEventListener("change", (event) => { matchCompetitionStatus = (event.target as HTMLSelectElement).value; renderMatches(); });
    $("matchCompetitionSort")?.addEventListener("change", (event) => { matchCompetitionSort = (event.target as HTMLSelectElement).value; renderMatches(); });
    updateMatchCounters();
  }

  function renderMatchWorkspaceLadder(app: AnyRow, teams: AnyRow[], selectedTeam: AnyRow | null) {
    const ownTeamId = clean(app.created_team_id);
    const activeTeams = teams.filter((team) => clean(team.ladder_status || "active").toLowerCase() === "active");
    return `
      <div class="player-ladder-head">
        <div><small>Live standings</small><h3>Open Ladder</h3><p>Select a team to view details and challenge times.</p></div>
        <span>${esc(activeTeams.length)} active</span>
      </div>
      ${activeTeams.length < 2 ? `<div class="player-match-action ready">
        <span>Challenge availability</span>
        <strong>Waiting for another active team</strong>
        <small>Your team is on the ladder. Challenge creation unlocks as soon as an eligible opponent joins above your current position.</small>
        <button class="team-secondary-btn" type="button" data-open-competition="${esc(competitionKey(app))}" data-open-competition-tab="challenges">View Ladder Challenges</button>
      </div>` : ""}
      <div class="player-ladder-list">
        ${teams.length ? teams.map((team, index) => {
          const teamId = clean(team.id);
          const own = teamId === ownTeamId;
          const selected = teamId === clean(selectedTeam?.id);
          const inactive = clean(team.ladder_status || "active").toLowerCase() !== "active";
          return `<button class="player-ladder-row ${own ? "is-own-team" : ""} ${selected ? "is-selected" : ""} ${inactive ? "is-inactive" : ""}" type="button" data-match-ladder-team="${esc(teamId)}">
            <b>#${esc(team.seed || index + 1)}</b>
            <span class="registry-avatar">${team.logo_path ? `<img src="${esc(assetPath(team.logo_path))}" alt="">` : esc(initials(first(team.team_name, team.short_name, "T")))}</span>
            <span><strong>${esc(first(team.team_name, team.short_name, "Team"))}${own ? `<em>Your team</em>` : ""}</strong><small>${esc(inactive ? "Inactive" : "Active ladder team")}</small></span>
            <i>View</i>
          </button>`;
        }).join("") : `<p class="team-empty">No teams have joined this ladder yet.</p>`}
      </div>`;
  }

  function renderMatchWorkspaceLadderDetail(app: AnyRow, team: AnyRow | null) {
    if (!team) return `<div class="captain-detail-head"><div><span class="registry-avatar large">L</span><div><h3>No team selected</h3><p>Select a ladder team to view its details.</p></div></div></div>`;
    const teamId = Number(team.id || 0);
    const ownTeamId = Number(app.created_team_id || 0);
    const own = teamId === ownTeamId;
    const challenges = competitionChallenges(app).filter((challenge) => (
      Number(challenge.challenger_team_id || 0) === teamId
      || Number(challenge.defender_team_id || 0) === teamId
    ));
    const currentChallenge = challenges.find((challenge) => ["pending", "accepted"].includes(clean(challenge.state || challenge.status).toLowerCase()))
      || challenges[0]
      || null;
    const slots = list(currentChallenge?.proposed_slots);
    const lineup = list(team.lineup);
    const ownRank = Number(app.ladder_rank || 0);
    const opponentRank = Number(team.seed || 0);
    const challengeRange = Math.max(1, Number(app.open_ladder_challenge_range || 5) || 5);
    const challengeDays = Math.max(1, Number(app.challenge_window_days || 7) || 7);
    const challengeStart = clean(app.challenge_window_start_time || "00:00");
    const challengeEnd = clean(app.challenge_window_end_time || "23:59");
    const teamActive = clean(team.ladder_status || "active").toLowerCase() === "active";
    const ownActive = clean(app.ladder_status || "active").toLowerCase() === "active";
    const hasPendingOrActive = competitionChallenges(app).some((challenge) => ["pending", "accepted"].includes(clean(challenge.state || challenge.status).toLowerCase()));
    const closeMode = clean(app.open_ladder_close_mode || "manual").toLowerCase();
    const closeAt = new Date(clean(app.open_ladder_close_at)).getTime();
    const stage = clean(app.competition_stage).toLowerCase();
    const offersClosed = closeMode === "closed"
      || ["playoffs", "completed", "archived"].includes(stage)
      || (closeMode === "scheduled" && Number.isFinite(closeAt) && closeAt <= Date.now());
    let challengeReason = "";
    if (own) challengeReason = "This is your team.";
    else if (!ownActive) challengeReason = "Your team is not active on this ladder.";
    else if (!teamActive) challengeReason = "This team is not active on this ladder.";
    else if (offersClosed) challengeReason = "This ladder is no longer accepting challenge offers.";
    else if (!ownRank || !opponentRank) challengeReason = "Both teams need a valid ladder position.";
    else if (opponentRank >= ownRank) challengeReason = "You can only challenge a team ranked above you.";
    else if (ownRank - opponentRank > challengeRange) challengeReason = `This team is outside your ${challengeRange}-position challenge range.`;
    else if (hasPendingOrActive) challengeReason = "Resolve your current ladder challenge before issuing another.";
    const canChallenge = !challengeReason;
    return `
      <div class="captain-detail-head player-ladder-detail-head">
        <div>
          <span class="registry-avatar large">${team.logo_path ? `<img src="${esc(assetPath(team.logo_path))}" alt="">` : esc(initials(first(team.team_name, team.short_name, "T")))}</span>
          <div><h3>${esc(first(team.team_name, team.short_name, "Team"))}</h3><p>${own ? "Your ladder team" : "Ladder opponent"}</p></div>
        </div>
        ${statusPill(clean(team.ladder_status || "active").toLowerCase() === "active" ? "active" : "inactive")}
      </div>
      <div class="captain-detail-list player-ladder-detail-stats">
        <div><span>Position</span><strong>#${esc(team.seed || "—")}</strong></div>
        <div><span>Status</span><strong>${esc(clean(team.ladder_status || "active").toLowerCase() === "active" ? "Active" : "Inactive")}</strong></div>
        <div><span>Team ID</span><strong>${esc(team.id || "Not set")}</strong></div>
      </div>
      <section class="player-ladder-lineup">
        <header>
          <div><small>Registered lineup</small><h4>Team roster</h4></div>
          <span>${esc(lineup.length)} player${lineup.length === 1 ? "" : "s"}</span>
        </header>
        <ul aria-label="Registered team lineup">
          ${lineup.length ? lineup.map((player, index) => `<li>
            <b class="player-lineup-slot">${esc(index + 1)}</b>
            <span class="team-lineup-avatar">${esc(initials(rosterPlayerName(player)))}</span>
            <span class="player-lineup-identity"><strong>${esc(rosterPlayerName(player))}</strong><small>Registered player</small></span>
            <em>${esc(first(player.role, "Player"))}</em>
          </li>`).join("") : `<li class="team-empty compact">No competition lineup has been submitted.</li>`}
        </ul>
      </section>
      <section class="player-ladder-challenge-times">
        <div><small>Challenge schedule</small><h4>${currentChallenge ? esc(clean(currentChallenge.status || currentChallenge.state || "pending").replaceAll("_", " ")) : "No active challenge"}</h4></div>
        ${slots.length ? `<div class="player-ladder-time-list">${slots.map((slot, index) => `<article><b>${index + 1}</b><span><strong>${esc(formatDate(first(slot.date, slot.starts_at, "")))}</strong><small>${esc(first(slot.time, ""))} ${esc(first(slot.timezone, app.competition_timezone, app.timezone, ""))}</small></span></article>`).join("")}</div>` : `<p class="team-empty compact">${own ? "Select another team to review its challenge status." : "No challenge times have been proposed with this team."}</p>`}
        ${canChallenge ? `
          <div class="player-ladder-eligibility can-challenge"><strong>Eligible to challenge</strong><span>#${esc(ownRank)} can challenge #${esc(opponentRank)}</span></div>
          <form class="player-ladder-card-form" data-ladder-challenge-form data-competition-key="${esc(competitionKey(app))}">
            <input name="defender_team_id" type="hidden" value="${esc(teamId)}">
            <p class="team-empty compact">Choose times within the next ${esc(challengeDays)} calendar day${challengeDays === 1 ? "" : "s"}, between ${esc(challengeStart)} and ${esc(challengeEnd)} ${esc(first(app.competition_timezone, app.timezone, ""))}.</p>
            <label>Option 1<input name="slot_1" type="datetime-local" required></label>
            <label>Option 2<input name="slot_2" type="datetime-local" required></label>
            <label>Option 3<input name="slot_3" type="datetime-local" required></label>
            <label class="is-wide">Message<input name="message" maxlength="1000" placeholder="Optional note to the defending team"></label>
            <button class="team-primary-btn is-wide" type="submit">Issue Challenge</button>
          </form>` : `
          <div class="player-ladder-eligibility cannot-challenge"><strong>${own ? "Your ladder entry" : "Cannot challenge this team"}</strong><span>${esc(challengeReason)}</span></div>
          <button class="team-secondary-btn" type="button" data-open-competition="${esc(competitionKey(app))}" data-open-competition-tab="challenges">View Ladder Challenges</button>`}
      </section>`;
  }

  function matchWorkspaceKey(match: AnyRow) {
    return clean(match.id || match.match_id || [match.league_id, match.__application_id, match.round_name, match.match_number, match.team1_id, match.team2_id].filter(Boolean).join("-"));
  }

  function renderMatchWorkspaceRow(match: AnyRow) {
    const key = matchWorkspaceKey(match);
    const action = matchActionSummary(match);
    const selected = key === selectedMatchId;
    const state = matchState(match);
    return `
      <button class="captain-player-row match-workspace-row ${selected ? "selected" : ""} is-${esc(state)}" type="button" data-match-workspace-select="${esc(key)}" aria-pressed="${selected}">
        <span class="registry-avatar">${esc(initials(first(match.opponent_name, match.team1_name, match.team2_name, "M")))}</span>
        <span class="match-row-opponent"><strong>${esc(first(match.opponent_name, "Opponent TBA"))}</strong><small>${esc(first(match.round_name, "Match"))} · Best of ${esc(match.best_of || "TBA")}</small></span>
        <span class="match-row-schedule"><small>Match date &amp; time</small><time>${esc(formatMatchSlot(match))}</time></span>
        <span class="match-row-action"><small>${esc(action.label)}</small><strong>${esc(action.copy)}</strong></span>
        ${statusPill(state)}
      </button>
    `;
  }

  function matchTabSeenKey(match: AnyRow, tab: "lobby" | "chat") {
    const accountId = first(dashboard.account?.id, dashboard.account?.team_account_id, "player");
    return `bracketr:match-tab-seen:${accountId}:${matchWorkspaceKey(match)}:${tab}`;
  }

  function matchTabSeenSignature(match: AnyRow, tab: "lobby" | "chat") {
    try {
      return window.localStorage.getItem(matchTabSeenKey(match, tab)) || "";
    } catch {
      return "";
    }
  }

  function markMatchTabSeen(match: AnyRow, tab: "lobby" | "chat", signature: string) {
    try {
      window.localStorage.setItem(matchTabSeenKey(match, tab), signature);
    } catch {}
  }

  function renderMatchWorkspaceDetail(match: AnyRow) {
    const action = matchActionSummary(match);
    const startMs = matchStartTime(match);
    const messages = list(match.match_messages).slice(-50);
    const ownSide: "team1" | "team2" = match.team_is_team1 ? "team1" : "team2";
    const opponentSide: "team1" | "team2" = ownSide === "team1" ? "team2" : "team1";
    const ownCheckedIn = Boolean(match[`${ownSide}_checked_in`]);
    const opponentCheckedIn = Boolean(match[`${opponentSide}_checked_in`]);
    const ownTeamName = first(match[`${ownSide}_name`], "Your Team");
    const opponentTeamName = first(match[`${opponentSide}_name`], match.opponent_name, "Opponent");
    const checkinOpensMs = new Date(clean(match.match_checkin_opens_at)).getTime() || 0;
    const checkinClosesMs = new Date(clean(match.match_checkin_closes_at)).getTime() || 0;
    const checkinWindowMinutes = Math.max(1, Number(match.match_checkin_window_minutes || 30) || 30);
    const lobbyValues = [
      clean(match.lobby_username || match.lobby_name),
      clean(match.lobby_password),
      clean(match.server_region || match.region),
      matchMaps(match).join("|"),
      formatMatchSlot(match),
      ownCheckedIn ? "own-ready" : "own-waiting",
      opponentCheckedIn ? "opponent-ready" : "opponent-waiting",
    ];
    const lobbySignature = lobbyValues.join("::");
    const lobbyHasDetails = lobbyValues.slice(0, 4).some((value) => value && !/^(tbd|not ready|not supplied|not assigned)$/i.test(value));
    const lastMessage = messages[messages.length - 1] || {};
    const chatSignature = messages.length
      ? [messages.length, lastMessage.id, lastMessage.created_at, lastMessage.message].map(clean).join("::")
      : "";
    const lobbyUnread = activeMatchDetailTab !== "lobby"
      && lobbyHasDetails
      && matchTabSeenSignature(match, "lobby") !== lobbySignature;
    const chatUnread = activeMatchDetailTab !== "chat"
      && Boolean(chatSignature)
      && matchTabSeenSignature(match, "chat") !== chatSignature;
    if (activeMatchDetailTab === "lobby") markMatchTabSeen(match, "lobby", lobbySignature);
    if (activeMatchDetailTab === "chat" && chatSignature) markMatchTabSeen(match, "chat", chatSignature);
    const assignedAdmins = list(match.assigned_match_admins);
    const adminNames = assignedAdmins.length
      ? assignedAdmins.map((admin) => first(admin.displayName, admin.display_name, admin.name, "Match Admin"))
      : [first(match.observer_name, match.observer, "Match admin not assigned")];
    const participants = [
      ...adminNames.map((name) => ({ name, role: name === "Match admin not assigned" ? "Awaiting assignment" : "Match Admin", tone: "admin" })),
      { name: first(match.team1_captain, match.team1_name, "Team 1 captain"), role: `${first(match.team1_name, "Team 1")} Captain`, tone: "captain" },
      { name: first(match.team2_captain, match.team2_name, "Team 2 captain"), role: `${first(match.team2_name, "Team 2")} Captain`, tone: "captain" },
    ];
    return `
      <div class="captain-detail-head">
        <div>
          <span class="registry-avatar large">${esc(initials(first(match.opponent_name, match.team1_name, match.team2_name, "M")))}</span>
          <div><h3>${esc(first(match.opponent_name, "Opponent TBA"))}</h3><p>${esc(first(match.__league_name, match.league_name, "Competition"))}</p></div>
        </div>
        ${statusPill(matchState(match))}
      </div>
      <div class="match-detail-tabs" role="tablist" aria-label="Match information">
        <button class="${activeMatchDetailTab === "details" ? "is-active" : ""}" type="button" role="tab" aria-selected="${activeMatchDetailTab === "details"}" data-match-detail-tab="details">Game Details</button>
        <button class="${activeMatchDetailTab === "lobby" ? "is-active" : ""} ${lobbyUnread ? "has-notification" : ""}" type="button" role="tab" aria-selected="${activeMatchDetailTab === "lobby"}" data-match-detail-tab="lobby">Lobby${lobbyUnread ? `<span class="match-tab-alert" aria-label="Lobby details updated">!</span>` : ""}</button>
        <button class="${activeMatchDetailTab === "chat" ? "is-active" : ""} ${chatUnread ? "has-notification" : ""}" type="button" role="tab" aria-selected="${activeMatchDetailTab === "chat"}" data-match-detail-tab="chat">Chat${chatUnread ? `<span class="match-tab-alert" aria-label="New chat message">!</span>` : ""}</button>
      </div>
      <div class="match-detail-tab-panel">
        ${activeMatchDetailTab === "details" ? `
          <div class="player-match-action ${esc(action.tone)}" aria-live="polite">
            <span>${esc(action.label)}</span>
            <strong>${esc(action.copy)}</strong>
            <div class="player-match-versus"><b>${esc(first(match.team1_name, "Your Team"))}</b><strong>${esc(match.team1_score ?? "–")} - ${esc(match.team2_score ?? "–")}</strong><b>${esc(first(match.team2_name, match.opponent_name, "Opponent"))}</b></div>
            <small>Best of ${esc(match.best_of || "TBA")} · ${esc(formatMatchSlot(match))}</small>
          </div>
          <div class="captain-detail-list">
            <div><span>Round</span><strong>${esc(first(match.round_name, "Match"))}</strong></div>
            <div><span>Counter</span><strong><span class="team-counter-value" data-match-counter data-match-start="${esc(startMs)}">${esc(matchCounterText(startMs))}</span></strong></div>
            <div><span>Check-in</span><strong>${esc(match.match_checkin_state || "not open")}</strong></div>
            <div><span>Best of</span><strong>${esc(match.best_of || "N/A")}</strong></div>
            <div><span>Map</span><strong>${esc(matchMaps(match)[0] || "Not assigned")}</strong></div>
          </div>` : ""}
        ${activeMatchDetailTab === "lobby" ? `
          <section class="match-lobby-panel">
            <div class="match-tab-intro"><small>Private match access</small><h4>Lobby &amp; Game Details</h4><p>Lobby credentials are only visible to this match room.</p></div>
            <section class="match-lobby-checkin">
              <div class="match-lobby-checkin-head">
                <div><small>Match check-in · ${esc(checkinWindowMinutes)} min window</small><h4>${ownCheckedIn && opponentCheckedIn ? "Both teams are ready" : "Waiting for teams"}</h4></div>
                <span class="${ownCheckedIn && opponentCheckedIn ? "is-ready" : ""}">${ownCheckedIn && opponentCheckedIn ? "Ready" : esc(first(match.match_checkin_state, "Not open").replaceAll("_", " "))}</span>
              </div>
              <div class="match-lobby-checkin-timer">
                <span><small>Opens</small><strong>${esc(match.match_checkin_opens_at ? formatDate(match.match_checkin_opens_at) : "Match time required")}</strong></span>
                <b data-checkin-window-counter data-checkin-opens="${esc(checkinOpensMs)}" data-checkin-closes="${esc(checkinClosesMs)}">${checkinOpensMs && checkinClosesMs
                  ? (Date.now() < checkinOpensMs ? `Opens in ${esc(formatDuration(checkinOpensMs - Date.now()))}` : Date.now() <= checkinClosesMs ? `Closes in ${esc(formatDuration(checkinClosesMs - Date.now()))}` : "Check-in closed")
                  : "Match time required"}</b>
              </div>
              <div class="match-lobby-checkin-teams">
                <article class="${ownCheckedIn ? "is-checked" : "is-waiting"}">
                  <span class="registry-avatar">${esc(initials(ownTeamName))}</span>
                  <div><small>Your team</small><strong>${esc(ownTeamName)}</strong><em>${esc(matchTeamCheckin(match, ownSide))}</em></div>
                  <b>${ownCheckedIn ? "✓" : "!"}</b>
                </article>
                <article class="${opponentCheckedIn ? "is-checked" : "is-waiting"}">
                  <span class="registry-avatar">${esc(initials(opponentTeamName))}</span>
                  <div><small>Opponent</small><strong>${esc(opponentTeamName)}</strong><em>${esc(matchTeamCheckin(match, opponentSide))}</em></div>
                  <b>${opponentCheckedIn ? "✓" : "…"}</b>
                </article>
              </div>
              ${!ownCheckedIn && match.match_checkin_is_open
                ? `<button class="team-primary-btn match-lobby-checkin-action" type="button" data-match-checkin="${esc(match.id)}">Check In Your Team</button>`
                : `<p class="match-lobby-checkin-note">${ownCheckedIn
                  ? "Your check-in is confirmed. This panel will update when the opponent checks in."
                  : `Check-in is ${esc(first(match.match_checkin_state, "not open").replaceAll("_", " "))}. You can check in here when the window opens.`}</p>`}
            </section>
            <div class="captain-detail-list">
              <div><span>Lobby name</span><strong>${esc(first(match.lobby_username, match.lobby_name, "Not ready"))}</strong></div>
              <div><span>Password</span><strong>${esc(first(match.lobby_password, "Not ready"))}</strong></div>
              <div><span>Server / Region</span><strong>${esc(first(match.server_region, match.region, "Not supplied"))}</strong></div>
              <div><span>Map</span><strong>${esc(matchMaps(match)[0] || "Not assigned")}</strong></div>
              <div><span>Scheduled</span><strong>${esc(formatMatchSlot(match))}</strong></div>
            </div>
          </section>` : ""}
        ${activeMatchDetailTab === "chat" ? `
          <section class="match-room-panel">
            <div class="match-tab-intro"><small>Private match room</small><h4>Admin &amp; Captain Chat</h4><p>Shared only with the assigned match admin and both captains.</p></div>
            <div class="match-room-participants" aria-label="Room participants">
              ${participants.map((person) => `<div class="is-${esc(person.tone)}"><span>${esc(initials(person.name))}</span><p><strong>${esc(person.name)}</strong><small>${esc(person.role)}</small></p></div>`).join("")}
            </div>
            <div class="match-room-feed" aria-live="polite">
              ${messages.length ? messages.map((item) => `
                <article class="${clean(item.author_role) === "captain" ? "from-captain" : "from-admin"}">
                  <header><strong>${esc(first(item.author_name, item.author_role === "admin" ? "Match Admin" : "Captain"))}</strong><time>${esc(formatDate(item.created_at))}</time></header>
                  <p>${esc(item.message)}</p>
                </article>`).join("") : `<p class="team-empty compact">No messages yet. Use this room for match coordination.</p>`}
            </div>
            <form class="match-room-form" data-match-message="${esc(match.id)}">
              <textarea name="message" maxlength="1000" rows="2" placeholder="Message the match admin and other captain..." required>${esc(matchChatDrafts.get(String(match.id)) || "")}</textarea>
              <button class="team-primary-btn" type="submit">Send</button>
            </form>
          </section>` : ""}
      </div>
    `;
  }

  function nextPlayableMatch(matches = allMatches()) {
    return matches
      .filter((match) => matchState(match) !== "completed")
      .sort((a, b) => (matchStartTime(a) || Number.MAX_SAFE_INTEGER) - (matchStartTime(b) || Number.MAX_SAFE_INTEGER))[0] || null;
  }

  function matchPrepItems(match: AnyRow | null) {
    if (!match) {
      return [
        { label: "Find a competition", value: "No fixture queued", tone: "ready" },
        { label: "Team status", value: `${playerMemberships().length} linked teams`, tone: "ready" },
        { label: "Portal checks", value: "Keep profile ready", tone: "ready" },
      ];
    }
    const maps = matchMaps(match);
    return [
      {
        label: "Check-in",
        value: match.current_team_checked_in ? "Checked in" : first(match.match_checkin_state, "Not open"),
        tone: Boolean(match.match_checkin_is_open) && !match.current_team_checked_in ? "urgent" : "ready",
      },
      {
        label: "Lobby",
        value: match.lobby_username || match.lobby_password ? "Details available" : "Waiting on lobby",
        tone: match.lobby_username || match.lobby_password ? "ready" : "pending",
      },
      {
        label: "Maps",
        value: maps.length ? maps.slice(0, 2).join(", ") : "Not assigned",
        tone: maps.length ? "ready" : "pending",
      },
    ];
  }

  function renderMatchPrepPanel(matches: AnyRow[]) {
    const next = nextPlayableMatch(matches);
    const action = next ? matchActionSummary(next) : null;
    return `
      <section class="player-match-prep">
        <div class="player-match-prep-main">
          <span>Match Prep</span>
          <h3>${esc(next ? first(next.opponent_name, "Opponent TBA") : "No active fixture")}</h3>
          <p>${esc(next ? `${first(next.__league_name, next.league_name, "Competition")} - ${formatMatchSlot(next)}` : "Once your team has an assigned match, its prep details will sit here first.")}</p>
          ${next ? `<strong class="${esc(action?.tone || "ready")}">${esc(action?.label || "Scheduled")}</strong>` : ""}
        </div>
        <div class="player-match-prep-list">
          ${matchPrepItems(next).map((item) => `
            <article class="${esc(item.tone)}">
              <span>${esc(item.label)}</span>
              <strong>${esc(item.value)}</strong>
            </article>
          `).join("")}
        </div>
      </section>
    `;
  }

  function matchActionSummary(match: AnyRow) {
    if (Boolean(match.match_checkin_is_open) && !match.current_team_checked_in) {
      return { label: "Check-in open", copy: "Your team still needs to check in for this match.", tone: "urgent" };
    }
    if (matchState(match) === "live" && !match.result_submission) {
      return { label: "Live", copy: "Review lobby, chat, and submit the result when ready.", tone: "urgent" };
    }
    if (match.current_team_checked_in) {
      return { label: "Ready", copy: "Your team is checked in. Keep lobby details handy.", tone: "ready" };
    }
    if (matchState(match) === "completed") {
      return { label: "Completed", copy: `Final score ${first(match.team1_score, "0")}-${first(match.team2_score, "0")}.`, tone: "done" };
    }
    return { label: "Scheduled", copy: "Watch for check-in and lobby details before match time.", tone: "ready" };
  }

  function renderMatchCard(match: AnyRow) {
    const state = matchState(match);
    const canCheckIn = Boolean(match.match_checkin_is_open) && !match.current_team_checked_in;
    const startMs = matchStartTime(match);
    const action = matchActionSummary(match);
    return `
      <article class="team-manager-card team-match-card">
        <div class="team-card-head">
          <div>${renderMatchSummary(match)}</div>
          <div class="team-inline-actions">
            ${canCheckIn ? `<button class="team-primary-btn" type="button" data-match-checkin="${esc(match.id)}">Check In</button>` : ""}
            ${match.current_team_checked_in ? statusPill("checked in") : ""}
          </div>
        </div>
        <div class="player-match-action ${esc(action.tone)}">
          <span>${esc(action.label)}</span>
          <strong>${esc(action.copy)}</strong>
          <small>${esc(first(match.__league_name, match.league_name, "Competition"))} - ${esc(formatMatchSlot(match))}</small>
        </div>
        <div class="team-match-detail-grid">
          <span><strong>${esc(matchTeamLabel(match, "team1"))}</strong>${esc(match.team1_score ?? "-")}</span>
          <span><strong>${esc(matchTeamLabel(match, "team2"))}</strong>${esc(match.team2_score ?? "-")}</span>
          <span>Check-in: <strong>${esc(match.match_checkin_state || "not open")}</strong></span>
          <span>Best of: <strong>${esc(match.best_of || "N/A")}</strong></span>
        </div>
        <div class="team-match-sheet">
          ${renderMatchInfoPanel("Match Details", [
            ["Round", first(match.round_name, "Match")],
            ["Match", match.match_number ? `#${match.match_number}` : ""],
            ["Scheduled", formatMatchSlot(match)],
            ["Counter", `<span class="team-counter-value" data-match-counter data-match-start="${esc(startMs)}">${esc(matchCounterText(startMs))}</span>`],
            ["Opponent", first(match.opponent_name, "TBA")],
          ])}
          ${renderMatchInfoPanel("Lobby", [
            ["Username", match.lobby_username],
            ["Password", match.lobby_password],
            ["Observer", match.observer_name],
            ["Best of", match.best_of],
          ])}
          <section class="team-match-info-panel">
            <h4>Maps</h4>
            <div class="team-map-list">
              ${matchMaps(match).length ? matchMaps(match).map((map) => `<span>${esc(map)}</span>`).join("") : `<em>No maps assigned yet.</em>`}
            </div>
          </section>
          <section class="team-match-info-panel">
            <h4>Check-in</h4>
            <div class="team-match-info-row"><span>${esc(matchTeamLabel(match, "team1"))}</span><strong>${esc(matchTeamCheckin(match, "team1"))}</strong></div>
            <div class="team-match-info-row"><span>${esc(matchTeamLabel(match, "team2"))}</span><strong>${esc(matchTeamCheckin(match, "team2"))}</strong></div>
            <div class="team-match-info-row"><span>Window</span><strong>${esc(match.match_checkin_state || "not open")}</strong></div>
          </section>
          <section class="team-match-info-panel team-match-lineup-panel">
            <h4>Opposition Lineup</h4>
            ${renderOpponentLineup(match)}
          </section>
        </div>
        ${renderLeagueThread(match)}
        ${state === "live" ? renderResultForm(match) : ""}
      </article>
    `;
  }

  function renderResultForm(match: AnyRow) {
    return `
      <form class="team-result-form" data-result-match="${esc(match.id)}">
        <label>${esc(match.team1_name || "Team 1")} Score<input name="team1_score" type="number" min="0" value="${esc(match.team1_score || 0)}" /></label>
        <label>${esc(match.team2_name || "Team 2")} Score<input name="team2_score" type="number" min="0" value="${esc(match.team2_score || 0)}" /></label>
        <label>Notes<input name="notes" type="text" placeholder="Optional notes" /></label>
        <button class="team-secondary-btn" type="submit">Submit Result</button>
      </form>
    `;
  }

  function renderMyTeamsSummary(memberships: AnyRow[], pendingInvites: AnyRow[]) {
    const captainTeams = memberships.filter(canManageMembership);
    const activeCompetitionCount = memberships.reduce((total, team) => {
      const teamId = Number(team.team_account_id || 0);
      return total + teamApplications(teamId).filter(teamApplicationIsActive).length;
    }, 0);
    const nextAction = pendingInvites.length
      ? { label: "Review Invites", section: "requests", copy: `${pendingInvites.length} team invite${pendingInvites.length === 1 ? "" : "s"} waiting` }
      : captainTeams.length
        ? { label: "Open Captain Quarters", section: "settings", copy: `${captainTeams.length} captain team${captainTeams.length === 1 ? "" : "s"} available` }
        : { label: "Create Team", section: "", copy: "Start a captain team when you are ready" };
    return `
      <section class="player-teams-summary">
        <div>
          <span>Team Portfolio</span>
          <h3>${esc(memberships.length ? `${memberships.length} linked team${memberships.length === 1 ? "" : "s"}` : "No linked teams yet")}</h3>
          <p>${esc(nextAction.copy)}</p>
        </div>
        <div class="player-teams-summary-stats">
          <article><span>Captain</span><strong>${esc(captainTeams.length)}</strong></article>
          <article><span>Invites</span><strong>${esc(pendingInvites.length)}</strong></article>
          <article><span>Active Comps</span><strong>${esc(activeCompetitionCount)}</strong></article>
        </div>
        ${nextAction.section
          ? `<button class="team-secondary-btn" type="button" data-section-jump="${esc(nextAction.section)}">${esc(nextAction.label)}</button>`
          : `<button class="team-primary-btn" type="button" data-open-modal="captainCreateTeamModal">${esc(nextAction.label)}</button>`}
      </section>
    `;
  }

  function renderMyTeams() {
    const target = $("playerTeamsList");
    if (!target) return;
    const memberships = playerMemberships();
    const pendingInvites = list(dashboard.rosterInvites).filter((invite) => clean(invite.status).toLowerCase() === "pending");
    const account = dashboard.account || {};
    const teamCards = memberships.map((team) => {
      const teamId = Number(team.team_account_id || 0);
      const apps = teamApplications(teamId);
      const activeApps = apps.filter(teamApplicationIsActive);
      const teamPlayers = rosterDraft.filter((player) => Number(player.team_account_id || 0) === teamId);
      const isCaptain = canManageMembership(team);
      const games = teamGameTitles(team);
      const nextTeamMatch = activeApps.flatMap((app) => list(app.matches).map((match) => ({ ...match, __league_name: app.league_name })))
        .filter((match) => matchState(match) !== "completed")
        .sort((a, b) => (matchStartTime(a) || Number.MAX_SAFE_INTEGER) - (matchStartTime(b) || Number.MAX_SAFE_INTEGER))[0] || null;
      const visiblePlayers = teamPlayers.slice(0, 5);
      return `
        <article class="team-manager-card player-team-card">
          <div class="team-card-head">
            <div class="player-team-title">
              <span class="registry-avatar">${team.logo_path ? `<img src="${esc(assetPath(team.logo_path))}" alt="">` : esc(initials(teamDisplayName(team)))}</span>
              <div>
                <h3>${esc(teamDisplayName(team))}</h3>
                <p>${esc([team.short_name, games.join(", "), first(team.region, team.team_region)].filter(Boolean).join(" - ") || "Team membership")}</p>
              </div>
            </div>
            ${statusPill(isCaptain ? "captain access" : first(team.role, "player"))}
          </div>
          <div class="team-match-detail-grid">
            <span>Role: <strong>${esc(first(team.role, isCaptain ? "Captain" : "Player"))}</strong></span>
            <span>Roster: <strong>${esc(teamPlayers.length || "Not synced")}</strong></span>
            <span>Active comps: <strong>${esc(activeApps.length)}</strong></span>
            <span>Status: <strong>${esc(team.status || "active")}</strong></span>
          </div>
          <div class="player-team-workspace-grid">
            <section>
              <div class="player-team-subhead"><h4>Roster Preview</h4><span>${esc(teamPlayers.length ? `${teamPlayers.length} linked` : "No linked players")}</span></div>
              <div class="player-team-mini-list">
                ${visiblePlayers.length ? visiblePlayers.map((player) => `
                  <div class="player-team-mini-row">
                    <span class="registry-avatar">${esc(initials(first(player.in_game_name, player.player_name, "P")))}</span>
                    <div><strong>${esc(first(player.in_game_name, player.player_name, "Player"))}</strong><small>${esc(first(player.role, accessLevel(player.access_level) === "manager" ? "Captain" : "Player"))}</small></div>
                    ${statusPill(rosterStatus(player.status))}
                  </div>
                `).join("") : `<p class="team-empty compact">Roster details will appear once this team syncs player records.</p>`}
              </div>
            </section>
            <section>
              <div class="player-team-subhead"><h4>Competition Links</h4><span>${esc(activeApps.length ? `${activeApps.length} active` : "No active comps")}</span></div>
              <div class="player-team-mini-list">
                ${activeApps.length ? activeApps.slice(0, 3).map((app) => `
                  <button class="player-team-comp-row" type="button" data-open-competition="${esc(competitionKey(app))}">
                    <div><strong>${esc(app.league_name || "Competition")}</strong><small>${esc(first(app.game_title, app.format, "Competition"))}</small></div>
                    ${statusPill(app.status || app.competition_stage || "active")}
                  </button>
                `).join("") : `<p class="team-empty compact">Competitions this team enters will appear here.</p>`}
              </div>
            </section>
          </div>
          <div class="player-team-next">
            <span>Next team match</span>
            <strong>${esc(nextTeamMatch ? first(nextTeamMatch.opponent_name, nextTeamMatch.team1_name, nextTeamMatch.team2_name, "Opponent TBA") : "No match scheduled")}</strong>
            <small>${esc(nextTeamMatch ? `${first(nextTeamMatch.__league_name, "Competition")} - ${formatMatchSlot(nextTeamMatch)}` : "Match details will appear after scheduling.")}</small>
          </div>
          <div class="team-inline-actions">
            ${isCaptain ? `<button class="team-primary-btn" type="button" data-player-team-jump="${esc(team.team_account_id || "")}">Open Captain Quarters</button>` : `<button class="team-secondary-btn" type="button" data-section-jump="competitions">View Competitions</button>`}
            <button class="team-secondary-btn" type="button" data-section-jump="competitions">Competition Centre</button>
            <button class="team-secondary-btn" type="button" data-section-jump="requests">Requests</button>
          </div>
        </article>
      `;
    }).join("");
    const inviteCards = pendingInvites.map((invite) => `
      <article class="team-manager-card player-team-card pending">
        <div class="team-card-head">
          <div class="player-team-title">
            <span class="registry-avatar">${esc(initials(first(invite.team_name, "Team")))}</span>
            <div><h3>${esc(invite.team_name || "Team invite")}</h3><p>${esc(first(invite.requester_in_game_name, invite.requester_display_name, "Captain"))} invited you as ${esc(invite.role || "Player")}</p></div>
          </div>
          ${statusPill(invite.status || "pending")}
        </div>
        <div class="team-inline-actions">
          <button class="team-primary-btn" type="button" data-roster-invite-accept="${esc(invite.id)}">Accept</button>
          <button class="team-secondary-btn" type="button" data-roster-invite-deny="${esc(invite.id)}">Deny</button>
        </div>
      </article>
    `).join("");
    target.innerHTML = teamCards || inviteCards
      ? `${renderMyTeamsSummary(memberships, pendingInvites)}${teamCards}${inviteCards ? `<div class="team-request-group"><h3>Pending Invites</h3>${inviteCards}</div>` : ""}`
      : `
        ${renderMyTeamsSummary(memberships, pendingInvites)}
        <article class="team-manager-card player-team-card">
          <div class="team-card-head">
            <div><h3>No teams linked yet</h3><p>${esc(first(account.in_game_name, account.display_name, "This player"))} is ready to join a roster once a captain sends an invite.</p></div>
          </div>
          <div class="team-inline-actions">
            <button class="team-primary-btn" type="button" data-section-jump="find-tournaments">Find Comps</button>
            <button class="team-secondary-btn" type="button" data-open-modal="captainCreateTeamModal">Create Team</button>
          </div>
        </article>
      `;
  }

  function renderProfile() {
    const target = $("playerProfilePanel");
    if (!target) return;
    const account = dashboard.account || {};
    const selected = selectedPlayer() || {};
    const playerName = first(account.in_game_name, account.display_name, selected.in_game_name, selected.player_name, "Player");
    const realName = first(account.player_name, selected.player_name, "Not supplied");
    const bracketrId = first(account.bracketr_player_id, playerPlatformId(selected), "No Bracketr ID yet");
    const steamId = first(account.steam_id, account.steam_epic_id, selected.steam_id, selected.steam_epic_id);
    const epicId = first(account.epic_id, selected.epic_id);
    const ps4Id = first(account.ps4_id, selected.ps4_id);
    const xboxId = first(account.xbox_id, selected.xbox_id);
    const externalId = first(account.player_external_id, selected.player_external_id);
    const generatedTrackerLink = rocketLeagueTrackerLink(steamId, epicId, ps4Id, xboxId);
    const trackerLink = first(account.player_tracker_link, selected.player_tracker_link, generatedTrackerLink);
    const games = playerGameTitles(account).length ? playerGameTitles(account) : playerGameTitles(selected);
    const pendingPhoto = first(account.pending_player_logo_path);
    const pendingBanner = first(account.pending_player_banner_path);
    const approvedPhoto = first(account.player_logo_path, selected.player_logo_path);
    const approvedBanner = first(account.player_banner_path);
    const logoPath = first(pendingPhoto, approvedPhoto);
    const bannerPath = first(pendingBanner, approvedBanner);
    const memberships = playerMemberships();
    const editGames = playerOverviewEditGames(games);
    const applications = list(dashboard.applications);
    const activity = list(dashboard.activity).slice(0, 4);
    const captaincies = memberships.filter((team) => /captain|manager|owner/i.test(first(team.role, team.access_level))).length;
    const joinedYear = new Date(first(account.created_at, selected.created_at, Date.now())).getFullYear();
    const completionTasks = [
      { label: "Add profile photo", done: Boolean(approvedPhoto || pendingPhoto) },
      { label: "Add banner", done: Boolean(approvedBanner || pendingBanner) },
      { label: "Link at least one game ID", done: Boolean(steamId || epicId || ps4Id) },
      { label: "Join or create a team", done: memberships.length > 0 },
      { label: "Connect Epic Games ID", done: Boolean(epicId) },
      { label: "Connect Twitch", done: Boolean(first(account.twitch_url, account.twitchUrl) || connectedAccounts.some((item) => clean(item.provider).toLowerCase() === "twitch")) },
    ];
    const completion = Math.round(completionTasks.filter((item) => item.done).length / completionTasks.length * 100);
    const profileVisibility = clean(account.profile_visibility).toLowerCase() === "private" ? "private" : "public";
    const publicProfileLink = /^BRP-[A-Z0-9]+$/i.test(bracketrId)
      ? `<a class="brk-btn player-public-profile-link" href="/player/${encodeURIComponent(bracketrId)}" target="_blank" rel="noopener noreferrer">↗&nbsp; View Public Profile</a>`
      : "";
    target.innerHTML = `
      <div class="player-profile-v2">
        <section class="player-profile-cover ${pendingBanner ? "has-pending-banner" : ""}" style="${bannerPath ? `--player-banner:url('${esc(assetPath(bannerPath))}')` : ""}">
          <div class="player-profile-cover-shade"></div>
          <div class="player-profile-cover-content">
            <button class="player-profile-photo ${pendingPhoto ? "is-pending" : ""}" type="button" data-open-player-media-library="logo" aria-label="Change profile image">${logoPath ? `<img src="${esc(assetPath(logoPath))}" alt="${esc(playerName)} profile photo">` : esc(initials(playerName))}${pendingPhoto ? `<small>Pending</small>` : ""}<em>Change</em></button>
            <div class="player-profile-title"><div><h2>${esc(playerName)}</h2><button class="player-id-copy" type="button" data-profile-copy="${esc(bracketrId)}" aria-label="Copy player ID">${esc(bracketrId)} <span>▣</span></button></div><p>${esc(realName)}</p><p>${esc(games.join(" · ") || "Choose your games")}</p><div class="player-hero-meta"><span>♔ Captain of ${esc(captaincies)} Teams</span><span>♙ Member of ${esc(memberships.length)} Teams</span><span>▣ Since ${esc(joinedYear)}</span></div></div>
            <div class="player-profile-hero-actions">${publicProfileLink}<button class="brk-btn" type="button" data-open-player-media-library="banner">✎&nbsp; Edit banner</button><button class="brk-btn primary player-edit-primary" type="button" data-toggle-player-edit>✎&nbsp; Edit Profile</button></div>
          </div>
        </section>

        <nav class="player-profile-tabs" aria-label="Profile sections">
          <button type="button" data-profile-tab="overview">Overview</button>
          <button type="button" data-profile-tab="accounts">Accounts</button>
          <button type="button" data-profile-tab="appearance">Appearance</button>
        </nav>
        <div id="playerMediaLibraryModal" class="player-media-library-modal" hidden>
            <div class="player-media-library-dialog" role="dialog" aria-modal="true" aria-labelledby="playerMediaLibraryTitle">
              <div class="player-media-library-head"><div><small>Approved image store</small><h3 id="playerMediaLibraryTitle">Player media library</h3></div><button type="button" data-close-player-media-library aria-label="Close">×</button></div>
              <div class="player-media-library-tabs"><button type="button" data-player-media-kind="logo">Profile images</button><button type="button" data-player-media-kind="banner">Banners</button></div>
              <div class="player-media-library-upload"><input id="playerMediaLibraryFile" type="file" accept="image/png,image/jpeg,image/webp"><button class="brk-btn primary" type="button" data-upload-player-media>Upload new image</button><span id="playerMediaLibraryStatus" role="status"></span></div>
              <div id="playerMediaLibraryGrid" class="player-media-library-grid"></div>
            </div>
        </div>
        <div class="player-profile-tab-workspace">
        <div class="player-profile-tab-panel player-profile-overview" data-profile-panel="overview">
        <div class="player-profile-main-column">
        <div class="player-profile-info-grid">
          <section class="player-profile-panel"><div class="player-profile-panel-head"><div><h3>Identity</h3></div><button class="brk-btn" type="button" data-toggle-player-edit>Edit</button></div>
            <div class="player-profile-data-list">
              <div><span>▣ &nbsp; Alias</span><strong>${esc(playerName)}</strong></div><div><span>♙ &nbsp; Full name</span><strong>${esc(realName)}</strong></div>
              <div><span>⌘ &nbsp; Player ID</span><strong>${esc(bracketrId)} <button data-profile-copy="${esc(bracketrId)}" aria-label="Copy player ID">▣</button></strong></div>
              <div><span>◉ &nbsp; Games</span><strong class="player-game-chips">${games.length ? games.map((game) => `<i>${esc(game)}</i>`).join("") : "Not selected"}</strong></div>
              <div><span>✉ &nbsp; Email</span><strong>${esc(first(account.email, account.contact_email, selected.email, "Not linked"))}</strong></div>
            </div>
          </section>
          <section class="player-profile-panel"><div class="player-profile-panel-head"><div><h3>Game IDs &amp; Links</h3></div><button class="brk-btn" type="button" data-toggle-player-edit>Edit</button></div>
            <div class="player-profile-link-list">
              ${[["Steam ID", steamId, steamId ? `https://steamcommunity.com/profiles/${encodeURIComponent(steamId)}` : ""], ["Epic Username", epicId, ""], ["PlayStation Username", ps4Id, ""], ["Xbox Username", xboxId, ""], ["Tracker Profile", trackerLink, trackerLink]].map(([label, value, href]) => `<div><span>${esc(label)}</span>${href ? `<a href="${esc(href)}" target="_blank" rel="noopener noreferrer" aria-label="${esc(label)} (opens in a new tab)">${esc(value)} <b>↗</b></a>` : value ? `<strong>${esc(value)} <button data-profile-copy="${esc(value)}" aria-label="Copy ${esc(label)}">▣</button></strong>` : `<strong class="is-empty">Not connected &nbsp; <button data-toggle-player-edit>Connect</button></strong>`}</div>`).join("")}
            </div>
          </section>
        </div>
        <section class="player-profile-teams">
          <div class="player-overview-section-head">
            <div><h3>Teams</h3><p>Your current team memberships and assigned roles.</p></div>
            <span>${esc(memberships.length)} linked</span>
          </div>
          <div class="player-profile-team-grid">
            ${memberships.length ? memberships.map((team) => `
              <article class="player-profile-team-card">
                <span class="registry-avatar">${team.logo_path ? `<img src="${esc(assetPath(team.logo_path))}" alt="">` : esc(initials(teamDisplayName(team)))}</span>
                <div>
                  <strong>${esc(teamDisplayName(team))}</strong>
                  <small>${esc(first(team.bracketr_team_id, team.short_name, "No team ID"))} · ${esc(first(team.region, team.organisation_name, "Global"))}</small>
                </div>
                <span class="registry-pill blue">${esc(first(team.role, team.access_level, "Player"))}</span>
                <em>${esc(first(team.status, "Active"))}</em>
                <button class="team-secondary-btn" type="button" data-section-jump="teams">View Team</button>
              </article>
            `).join("") : `<div class="player-profile-empty"><strong>You are not part of a team yet.</strong><p>Join or create a team to enter competitions.</p><button class="brk-btn primary" type="button" data-section-jump="find-tournaments">Find Teams</button></div>`}
          </div>
          ${memberships.length ? `<button class="player-view-all" type="button" data-section-jump="teams">View all teams&nbsp; →</button>` : ""}
        </section>
        </div>
        <aside class="player-profile-utility" aria-label="Profile summary">
          ${completion === 100
            ? `<section class="player-utility-card player-completion-card is-complete" aria-label="Profile complete"><div class="player-complete-tick" aria-hidden="true">✓</div><div><h3>Profile complete</h3><small>You're all set!</small></div></section>`
            : `<section class="player-utility-card player-completion-card"><h3>Profile Completion</h3><div class="player-completion-summary"><div class="player-progress-ring" style="--progress:${completion}" role="progressbar" aria-label="Profile completion" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${completion}"><span>${completion}%</span></div><div><strong>Complete your profile</strong><small>You're almost there!</small></div></div><div class="player-task-list">${completionTasks.map((task) => `<span class="${task.done ? "is-done" : ""}"><i>${task.done ? "✓" : "○"}</i>${esc(task.label)}</span>`).join("")}</div><button type="button" data-profile-tab-jump="accounts">View all tasks&nbsp; →</button></section>`}
          <section class="player-utility-card"><h3>Quick Stats</h3><div class="player-quick-stats"><div><b>♙</b><strong>${memberships.length}</strong><span>Teams</span></div><div><b>♜</b><strong>${applications.length}</strong><span>Competitions</span></div><div><b>♔</b><strong>${captaincies}</strong><span>Captaincies</span></div><div><b>◔</b><strong>${completion}%</strong><span>Profile Complete</span></div></div></section>
          <section class="player-utility-card player-activity-card"><div class="player-utility-head"><h3>Recent Activity</h3><button type="button" data-section-jump="activity">View all&nbsp; →</button></div><div class="player-activity-feed">${activity.length ? activity.map((item) => `<div><i>◌</i><p><strong>${esc(clean(item.action).replaceAll("_", " ") || "Profile activity")}</strong><span>${esc(item.created_at || "Recently")}</span></p></div>`).join("") : `<div class="player-profile-empty"><strong>No recent activity yet.</strong><p>Competition entries and team updates will appear here.</p></div>`}</div></section>
        </aside>
        </div>
        <div class="player-profile-tab-panel" data-profile-panel="appearance" hidden>
        <section class="player-profile-panel player-profile-appearance">
          <div class="player-profile-panel-head"><div><span class="player-profile-section-icon">◐</span><div><h3>Site appearance</h3><p>Choose how Bracketr looks on this device</p></div></div></div>
          <div class="player-theme-picker" role="group" aria-label="Site appearance"><button type="button" data-profile-theme="site"><i></i><span>Site default<small>Follow Bracketr</small></span></button><button type="button" data-profile-theme="dark"><i></i><span>Dark<small>Low-light interface</small></span></button><button type="button" data-profile-theme="light"><i></i><span>Light<small>Bright interface</small></span></button></div>
        </section>
        <section class="player-profile-panel player-profile-privacy">
          <div class="player-profile-panel-head"><div><span class="player-profile-section-icon">◉</span><div><h3>Profile privacy</h3><p>Control what other people can see on your player profile</p></div></div><span class="player-privacy-status is-${esc(profileVisibility)}">${profileVisibility === "private" ? "Private" : "Public"}</span></div>
          <div class="player-privacy-picker" role="radiogroup" aria-label="Profile privacy">
            <button type="button" role="radio" aria-checked="${profileVisibility === "public"}" class="${profileVisibility === "public" ? "is-active" : ""}" data-profile-visibility="public"><strong>Public profile</strong><small>Show your achievements, competition record, teams, games, and connected profile details.</small></button>
            <button type="button" role="radio" aria-checked="${profileVisibility === "private"}" class="${profileVisibility === "private" ? "is-active" : ""}" data-profile-visibility="private"><strong>Private profile</strong><small>Only your player name and profile picture are visible. Everything else is hidden.</small></button>
          </div>
          <p class="player-privacy-message" data-profile-privacy-message role="status"></p>
        </section>
        </div>
        <div class="player-profile-tab-panel" data-profile-panel="accounts" hidden>
        ${renderConnectedAccounts()}
        </div>
        </div>
      </div>
      ${renderPlayerProfileEditModal({ account, selected, playerName, realName, steamId, epicId, ps4Id, xboxId, externalId, trackerLink, generatedTrackerLink, editGames })}
    `;
    const activateProfileTab = (tab: string) => {
      activeProfileTab = ["overview", "accounts", "appearance"].includes(tab) ? tab : "overview";
      target.querySelectorAll<HTMLElement>("[data-profile-tab]").forEach((button) => button.classList.toggle("is-active", button.dataset.profileTab === activeProfileTab));
      target.querySelectorAll<HTMLElement>("[data-profile-panel]").forEach((panel) => {
        const active = panel.dataset.profilePanel === activeProfileTab;
        panel.hidden = !active;
        panel.classList.toggle("is-active", active);
      });
    };
    target.querySelectorAll<HTMLElement>("[data-profile-tab]").forEach((button) => button.addEventListener("click", () => activateProfileTab(button.dataset.profileTab || "overview")));
    target.querySelectorAll<HTMLElement>("[data-profile-tab-jump]").forEach((button) => button.addEventListener("click", () => activateProfileTab(button.dataset.profileTabJump || "overview")));
    target.querySelectorAll<HTMLButtonElement>("[data-profile-copy]").forEach((button) => button.addEventListener("click", async () => {
      await navigator.clipboard.writeText(button.dataset.profileCopy || "");
      const previous = button.innerHTML;
      button.textContent = "Copied";
      window.setTimeout(() => { button.innerHTML = previous; }, 1400);
    }));
    activateProfileTab(activeProfileTab);
    syncProfileThemeButtons();
  }

  function syncProfileThemeButtons() {
    const active = localStorage.getItem("bracketr-theme") || "site";
    document.querySelectorAll<HTMLElement>("[data-profile-theme]").forEach((button) => button.classList.toggle("is-active", button.dataset.profileTheme === active));
  }

  function applyProfileTheme(theme: string) {
    const cleanTheme = ["site", "dark", "light"].includes(theme) ? theme : "site";
    const effective = cleanTheme === "site" ? "dark" : cleanTheme;
    [document.documentElement, document.body].forEach((root) => {
      root.classList.toggle("bracketr-theme-dark", effective === "dark");
      root.classList.toggle("bracketr-theme-light", effective === "light");
      root.classList.toggle("bracketr-theme-site", cleanTheme === "site");
    });
    localStorage.setItem("bracketr-theme", cleanTheme);
    syncProfileThemeButtons();
  }

  function renderConnectedAccounts() {
    const account = dashboard.account || {};
    const definitions = [
      { id: "steam", name: "Steam", purpose: "Add the ID used when competing", icon: "/public/assets/auth-providers/steam.svg", manualId: first(account.steam_id, account.steamId) },
      { id: "epic", name: "Epic Games", purpose: "Add the ID used when competing", icon: "/public/assets/auth-providers/epic-games.svg", manualId: first(account.epic_id, account.epicId) },
      { id: "playstation", name: "PlayStation", purpose: "Add the ID used when competing", icon: "/public/assets/auth-providers/playstation.svg", manualId: first(account.ps4_id, account.ps4Id) },
      { id: "discord", name: "Discord", purpose: "Community identity and sign-in", icon: "/public/assets/auth-providers/discord-official.svg" },
      { id: "twitch", name: "Twitch", purpose: "Add your public channel link", icon: "/public/assets/auth-providers/twitch-official.svg", manualId: first(account.twitch_url, account.twitchUrl) },
      { id: "kick", name: "Kick", purpose: "Add your public channel link", icon: "/public/assets/auth-providers/kick.svg", manualId: first(account.kick_url, account.kickUrl) },
      { id: "youtube", name: "YouTube", purpose: "Add your public channel link", icon: "/public/assets/auth-providers/youtube.svg", manualId: first(account.youtube_url, account.youtubeUrl) },
    ];
    const byProvider = new Map(connectedAccounts.map((item) => [clean(item.provider).toLowerCase(), item]));
    const identityCount = definitions.filter((provider) => byProvider.has(provider.id) || provider.manualId).length;
    return `
      <section class="player-connected-accounts">
        <div class="player-overview-section-head">
          <div><h3>Connected Accounts</h3><p>The same player platforms and channels shown on your dashboard.</p></div>
          <span>${esc(identityCount)} added</span>
        </div>
        <div class="player-connected-grid">
          ${definitions.map((provider) => {
            const linked = byProvider.get(provider.id);
            const configured = Boolean(oauthProviders[provider.id]);
            const identity = first(linked?.provider_username, linked?.provider_email, linked?.provider_user_id, provider.manualId);
            const added = Boolean(linked || provider.manualId);
            return `
              <article class="player-connected-card ${added ? "is-connected" : ""}">
                <span class="player-provider-mark ${esc(provider.id)}"><img src="${esc(provider.icon)}" alt=""></span>
                <div><strong>${esc(provider.name)}</strong><small>${esc(identity || provider.purpose)}</small></div>
                ${linked
                  ? `<button class="team-secondary-btn" type="button" data-disconnect-provider="${esc(provider.id)}">Disconnect</button>`
                  : configured
                    ? `<a class="team-secondary-btn" href="/api/auth/${esc(provider.id)}/start?mode=connect&next=${encodeURIComponent("/dashboard?view=user&section=profile")}">${provider.manualId ? "Verify" : "Connect"}</a>`
                    : `<button class="team-secondary-btn" type="button" data-toggle-player-edit>${provider.manualId ? "Edit" : "Add"}</button>`}
              </article>
            `;
          }).join("")}
        </div>
        <p class="player-connected-note">Player IDs and channel links are shared with the dashboard and your player profile.</p>
      </section>
    `;
  }

  function renderCompetitions() {
    const target = $("competitionsList");
    if (!target) return;
    const applications = list(dashboard.applications);
    if (!applications.length) {
      target.innerHTML = `
        <article class="team-manager-card team-competition-card">
          <div class="team-card-head">
            <div><h3>No competitions yet</h3><p>Find open tournaments or ladders, then enter with a team you captain.</p></div>
          </div>
          <div class="team-inline-actions">
            <button class="team-primary-btn" type="button" data-section-jump="find-tournaments">Find Comps</button>
            <button class="team-secondary-btn" type="button" data-open-modal="captainCreateTeamModal">Create Team</button>
          </div>
        </article>
      `;
      return;
    }
    const activeApps = applications.filter(teamApplicationIsActive);
    const inactiveApps = applications.filter((app) => !teamApplicationIsActive(app));
    const renderGroup = (title: string, apps: AnyRow[], empty: string) => `
      <section class="player-competition-group">
        <div class="player-competition-group-head"><h3>${esc(title)}</h3><span>${esc(apps.length)}</span></div>
        ${apps.length ? apps.map(renderCompetitionCard).join("") : `<p class="team-empty compact">${esc(empty)}</p>`}
      </section>
    `;
    target.innerHTML = [
      renderGroup("Active Competitions", activeApps, "No active competitions are linked right now."),
      inactiveApps.length ? renderGroup("Inactive / Archived", inactiveApps, "No inactive competitions.") : "",
    ].join("");
  }

  function competitionNextAction(app: AnyRow, matches: AnyRow[], activeChallenges: AnyRow[]) {
    const checkinMatch = matches.find((match) => Boolean(match.match_checkin_is_open) && !match.current_team_checked_in);
    const liveMatch = matches.find((match) => matchState(match) === "live" && !match.result_submission);
    const nextMatch = matches.find((match) => matchState(match) !== "completed");
    if (!app.checked_in && ["checkin", "check-in", "registration_closed"].includes(clean(app.competition_stage).toLowerCase())) {
      return { label: "Competition check-in", copy: "Check in for this competition before matches begin.", section: "", action: "Check In", button: "checkin" };
    }
    if (activeChallenges.length) {
      return { label: "Challenge pending", copy: `${activeChallenges.length} challenge${activeChallenges.length === 1 ? "" : "s"} need attention.`, section: "competition-hub", action: "Open Challenges", tab: "challenges" };
    }
    if (checkinMatch) {
      return { label: "Match check-in open", copy: `Check in for ${first(checkinMatch.opponent_name, "your next opponent")}.`, section: "competitions", action: "Open Competition Centre" };
    }
    if (liveMatch) {
      return { label: "Live match", copy: "Review lobby, chat, and result submission.", section: "competitions", action: "Open Competition Centre" };
    }
    if (nextMatch) {
      return { label: "Next match", copy: `${first(nextMatch.opponent_name, "TBA")} - ${formatMatchSlot(nextMatch)}`, section: "competition-hub", action: "Open Hub", tab: "matches" };
    }
    return { label: "Up to date", copy: "Open the hub for ladder, rules, roster, and broadcast workflow.", section: "competition-hub", action: "Open Hub" };
  }

  function renderCompetitionCard(app: AnyRow) {
      const matches = list(app.matches);
      const activeChallenges = competitionChallenges(app).filter((item) => item.state === "pending");
      const teamRank = app.ladder_rank || app.seed || app.current_rank || "";
      const nextAction = competitionNextAction(app, matches, activeChallenges);
      const roster = list(app.roster);
      const teamName = first(app.team_name, selectedCaptainTeam()?.team_name, dashboard.account?.team_name, "Team");
      return `
      <article class="team-manager-card team-competition-card">
        <div class="team-card-head">
          <div><h3>${esc(app.league_name || "Competition")}</h3><p>${esc(teamName)} - ${esc(app.game_title || "Game")} - ${esc(openCompetitionType(app.format))}</p></div>
          ${statusPill(app.status || app.competition_stage || "pending")}
        </div>
        <div class="team-match-detail-grid">
          <span>Stage: <strong>${esc(app.competition_stage || "registration")}</strong></span>
          <span>Rank: <strong>${esc(teamRank ? `#${teamRank}` : "Not ranked")}</strong></span>
          <span>Challenges: <strong>${esc(activeChallenges.length)}</strong></span>
          <span>Matches: <strong>${esc(matches.length)}</strong></span>
        </div>
        <div class="player-competition-workspace">
          <section>
            <span>${esc(nextAction.label)}</span>
            <strong>${esc(nextAction.copy)}</strong>
          </section>
          <section>
            <span>Registered roster</span>
            <strong>${esc(roster.length ? roster.slice(0, 4).map(rosterPlayerName).join(", ") : "No roster snapshot")}</strong>
          </section>
        </div>
        <div class="team-inline-actions">
          ${app.checked_in ? statusPill("checked in") : `<button class="team-secondary-btn" type="button" data-competition-checkin="${esc(app.id)}">Competition Check-in</button>`}
          ${nextAction.section === "competitions"
            ? `<button class="team-primary-btn" type="button" data-section-jump="competitions">Open Competition Centre</button>`
            : `<button class="team-primary-btn" type="button" data-open-competition="${esc(competitionKey(app))}" ${nextAction.tab ? `data-open-competition-tab="${esc(nextAction.tab)}"` : ""}>Open Hub</button>`}
          <a class="team-secondary-link" href="/viewer?leagueId=${esc(app.league_id)}">Tournament Page</a>
        </div>
      </article>
    `;
  }

  function openCompetitionType(value: unknown) {
    const format = clean(value).toLowerCase();
    if (format === "open_ladder") return "Open Ladder";
    if (format === "round_robin") return "Round Robin";
    if (format === "single_elim") return "Single Elim";
    if (format === "double_elim") return "Double Elim";
    return format ? format.replaceAll("_", " ") : "Tournament";
  }

  function openCompetitionAppliedApp(item: AnyRow) {
    return list(dashboard.applications).find((app) => String(app.league_id || "") === String(item.league_id || item.id || "")) || null;
  }

  function fillOpenCompetitionFilters() {
    const gameSelect = $<HTMLSelectElement>("teamTournamentGameFilter");
    if (gameSelect) {
      const current = gameSelect.value !== "ALL" ? gameSelect.value : (params.get("compGame") || "ALL");
      const games = [...new Set(list(dashboard.openCompetitions).map((item) => clean(item.game_title)).filter(Boolean))].sort();
      gameSelect.innerHTML = `<option value="ALL">All Games</option>${games.map((game) => `<option value="${esc(game)}">${esc(game)}</option>`).join("")}`;
      gameSelect.value = current === "ALL" || games.includes(current) ? current : "ALL";
    }
    const typeSelect = $<HTMLSelectElement>("teamTournamentTypeFilter");
    if (typeSelect && typeSelect.value === "ALL" && params.get("compFormat")) typeSelect.value = params.get("compFormat") || "ALL";
    const statusSelect = $<HTMLSelectElement>("teamTournamentStatusFilter");
    if (statusSelect && statusSelect.value === "ALL" && params.get("compStatus")) statusSelect.value = params.get("compStatus") || "ALL";
    const regionSelect = $<HTMLSelectElement>("teamTournamentRegionFilter");
    if (regionSelect) {
      const current = regionSelect.value || params.get("compRegion") || "ALL";
      const regions = [...new Set(list(dashboard.openCompetitions).map((item) => first(item.region, item.team_region)).filter(Boolean))].sort();
      regionSelect.innerHTML = `<option value="ALL">All Regions</option>${regions.map((region) => `<option value="${esc(region)}">${esc(region)}</option>`).join("")}`;
      regionSelect.value = regions.includes(current) ? current : "ALL";
    }
  }

  const competitionDeadline = (item: AnyRow) => new Date(first(item.applications_close_at, item.registration_closes_at, item.entries_close_at) || 0).getTime();
  const competitionIsClosing = (item: AnyRow) => { const deadline = competitionDeadline(item); return deadline > Date.now() && deadline - Date.now() <= 7 * 86400000; };
  const competitionIsEntered = (item: AnyRow) => Boolean(openCompetitionAppliedApp(item) || item.application_id);
  const competitionIsEligible = (item: AnyRow) => captainTeamsForCompetition(item).length > 0;
  const competitionRegion = (item: AnyRow) => first(item.region, item.team_region, item.area, "Global");

  function filteredOpenCompetitions() {
    const search = clean($<HTMLInputElement>("teamTournamentSearchInput")?.value).toLowerCase();
    const game = $<HTMLSelectElement>("teamTournamentGameFilter")?.value || "ALL";
    const type = $<HTMLSelectElement>("teamTournamentTypeFilter")?.value || "ALL";
    const region = $<HTMLSelectElement>("teamTournamentRegionFilter")?.value || "ALL";
    const status = $<HTMLSelectElement>("teamTournamentStatusFilter")?.value || "ALL";
    return list(dashboard.openCompetitions).filter((item) => {
      const format = clean(item.format).toLowerCase();
      const haystack = [
        item.league_name,
        item.game_title,
        item.format,
        item.public_summary,
        item.tournament_info,
      ].join(" ").toLowerCase();
      return (!search || haystack.includes(search))
        && (game === "ALL" || clean(item.game_title) === game)
        && (type === "ALL" || (type === "open_ladder" ? format === "open_ladder" : format !== "open_ladder"))
        && (region === "ALL" || competitionRegion(item) === region)
        && (status === "ALL" || (status === "eligible" && competitionIsEligible(item)) || (status === "closing" && competitionIsClosing(item)) || (status === "entered" && competitionIsEntered(item)));
    });
  }

  function captainTeamsForCompetition(item: AnyRow, teams = playerMemberships().filter(canManageMembership)) {
    const itemGame = clean(item.game_title).toLowerCase();
    return teams.filter((team) => {
      const games = teamGameTitles(team).map((game) => game.toLowerCase());
      return !itemGame || !games.length || games.includes(itemGame);
    });
  }

  function discoveryCompetitionId(item: AnyRow) {
    return clean(item.league_id || item.id);
  }

  function discoveryRoster(teamAccountId: string) {
    return list(dashboard.players).filter((player) =>
      clean(player.team_account_id) === clean(teamAccountId)
      && rosterStatus(player.status) === "active"
      && clean(player.id)
    );
  }

  function discoveryEntryState(item: AnyRow) {
    const leagueKey = discoveryCompetitionId(item);
    const teams = captainTeamsForCompetition(item);
    const teamId = discoveryEntryTeams[leagueKey] || clean(teams[0]?.team_account_id);
    discoveryEntryTeams[leagueKey] = teamId;
    const roster = discoveryRoster(teamId);
    const required = Math.max(0, Number(item.team_size || 0) || 0);
    const stateKey = `${leagueKey}:${teamId}`;
    if (!discoveryEntryPlayers.has(stateKey)) {
      discoveryEntryPlayers.set(stateKey, new Set(roster.slice(0, required || roster.length).map((player) => clean(player.id))));
    }
    return { leagueKey, teams, teamId, roster, required, selected: discoveryEntryPlayers.get(stateKey) || new Set<string>() };
  }

  function renderRecommendedCompetition(item: AnyRow) {
    const { leagueKey, teams, teamId, roster, required, selected } = discoveryEntryState(item);
    const tab = discoveryEntryTabs[leagueKey] || "enter";
    const expanded = Boolean(discoveryEntryExpanded[leagueKey]);
    const applied = competitionIsEntered(item);
    const rulesAccepted = discoveryRulesAccepted.has(leagueKey);
    const applicationUrl = item.application_url || `/apply?leagueId=${leagueKey}`;
    const entryRules = first(item.rules_text, "The organiser has not published additional rules. Standard Bracketr competition conduct and fair-play requirements apply.");
    const entryRuleSections: Array<{ id: string; title: string; lines: string[] }> = [];
    const entryRuleIntro: string[] = [];
    let entryRuleSection: { id: string; title: string; lines: string[] } | null = null;
    entryRules.split(/\r?\n/).forEach((line) => {
      const markdownHeading = line.match(/^\s{0,3}#{1,6}\s+(.+?)\s*#*\s*$/);
      const numberedHeading = line.match(/^\s*(\d+(?:\.\d+)*)\.\s+(.+?)\s*$/);
      if (markdownHeading || numberedHeading) {
        const title = markdownHeading ? markdownHeading[1].trim() : `${numberedHeading![1]}. ${numberedHeading![2].trim()}`;
        entryRuleSection = { id: `entry-rule-${leagueKey}-${entryRuleSections.length + 1}`, title, lines: [] };
        entryRuleSections.push(entryRuleSection);
      } else if (entryRuleSection) {
        entryRuleSection.lines.push(line);
      } else {
        entryRuleIntro.push(line);
      }
    });
    const entryRuleCopy = (value: string) => esc(value.trim()).replace(/\n/g, "<br>");
    const tabPanel = tab === "info"
      ? `<dl class="competition-entry-info">
          <div><dt>Game</dt><dd>${esc(item.game_title || "Not set")}</dd></div>
          <div><dt>Format</dt><dd>${esc(openCompetitionType(item.format))}</dd></div>
          <div><dt>Team size</dt><dd>${esc(item.team_size || "Flexible")}</dd></div>
          <div><dt>Region</dt><dd>${esc(competitionRegion(item))}</dd></div>
          <div><dt>Starts</dt><dd>${esc(formatDate(first(item.starts_at, item.start_date)))}</dd></div>
          <div><dt>Entries close</dt><dd>${esc(formatDate(item.applications_close_at))}</dd></div>
          ${item.prize_pool ? `<div><dt>Prize pool</dt><dd>${esc(item.prize_pool)}</dd></div>` : ""}
          ${item.max_teams ? `<div><dt>Maximum teams</dt><dd>${esc(item.max_teams)}</dd></div>` : ""}
        </dl>
        <div class="competition-entry-copy"><h5>About</h5><p>${esc(first(item.public_summary, item.tournament_info, "Competition registration is open."))}</p></div>`
      : applied
      ? `<div class="competition-entry-success">${statusPill(item.application_status || "entered")}<strong>Your team has already entered this competition.</strong></div>`
      : !teams.length
      ? `<div class="competition-entry-empty"><strong>Add a team before entering</strong><p>Create your team and roster in Captain Quarters, then return here to select the competing lineup.</p><button class="team-primary-btn" type="button" data-section-jump="settings">Add My Team</button></div>`
      : `<form class="competition-entry-form" data-competition-entry-form="${esc(leagueKey)}">
          <div class="competition-entry-heading">
            <div><small>Team Entry</small><h5>Build your competition lineup</h5></div>
            <p>Choose your team, select the players competing, then accept the rules.</p>
          </div>
          <label><span class="competition-entry-step">1</span><span>Choose team</span>
            <select data-entry-team="${esc(leagueKey)}">
              ${teams.map((team) => `<option value="${esc(team.team_account_id)}" ${clean(team.team_account_id) === teamId ? "selected" : ""}>${esc(team.team_name || "Team")}</option>`).join("")}
            </select>
          </label>
          <fieldset>
            <legend><span class="competition-entry-step">2</span><span>Select lineup</span><em>${esc(selected.size)} / ${esc(required || roster.length)} selected</em></legend>
            <div class="competition-lineup-list">
              ${roster.length ? roster.map((player) => `<label><input type="checkbox" data-entry-player="${esc(leagueKey)}" value="${esc(player.id)}" ${selected.has(clean(player.id)) ? "checked" : ""}><span><strong>${esc(first(player.in_game_name, player.display_name, player.player_name, "Player"))}</strong><small>${esc(first(player.role, player.bracketr_player_id, "Player"))}</small></span></label>`).join("") : `<p>No active players are on this team yet.</p>`}
            </div>
          </fieldset>
          <p class="competition-entry-message" data-entry-message="${esc(leagueKey)}"></p>
          <footer class="competition-entry-footer">
            <div class="competition-rules-state ${rulesAccepted ? "is-accepted" : ""}">
              <span>${rulesAccepted ? "✓" : "3"}</span>
              <div><strong>${rulesAccepted ? "Rules accepted" : "Review competition rules"}</strong><small>${rulesAccepted ? "You can now submit this lineup." : "Read to the bottom of the rules to continue."}</small></div>
            </div>
            <button class="team-secondary-btn" type="button" data-open-entry-rules="${esc(leagueKey)}">${rulesAccepted ? "Read Rules Again" : "Review Rules"}</button>
            <button class="team-primary-btn" type="submit" ${!roster.length || !rulesAccepted || (required > 0 && selected.size !== required) ? "disabled" : ""}>Submit Team Entry</button>
            <a class="competition-full-application-link" href="${esc(applicationUrl)}">Full application form</a>
          </footer>
        </form>`;
    const rulesModal = `
      <div class="competition-rules-modal" data-entry-rules-modal="${esc(leagueKey)}" hidden>
        <section role="dialog" aria-modal="true" aria-labelledby="entryRulesTitle${esc(leagueKey)}">
          <header><div><small>Competition Rules</small><h3 id="entryRulesTitle${esc(leagueKey)}">${esc(item.league_name || "Competition")}</h3></div><button type="button" data-close-entry-rules aria-label="Close rules">×</button></header>
          <div class="competition-rules-reader-layout${entryRuleSections.length ? "" : " is-single"}">
            ${entryRuleSections.length ? `<nav aria-label="Rules sections">
              <button class="is-active" type="button" data-entry-rule-target="entryRulesDocument${esc(leagueKey)}"><span>Full view</span><small>All rules</small></button>
              ${entryRuleSections.map((section, index) => `<button type="button" data-entry-rule-target="${section.id}"><small>${String(index + 1).padStart(2, "0")}</small><span>${esc(section.title)}</span></button>`).join("")}
            </nav>` : ""}
            <div id="entryRulesDocument${esc(leagueKey)}" class="competition-rules-scroll" data-entry-rules-scroll tabindex="0">
              ${entryRuleIntro.some((line) => line.trim()) ? `<div class="competition-rules-intro">${entryRuleCopy(entryRuleIntro.join("\n"))}</div>` : ""}
              ${entryRuleSections.length ? entryRuleSections.map((section) => `<section id="${section.id}" class="competition-rule-section"><h4>${esc(section.title)}</h4><div>${entryRuleCopy(section.lines.join("\n"))}</div></section>`).join("") : `<div>${entryRuleCopy(entryRules)}</div>`}
              <div class="competition-rules-end"><span>End of competition rules</span><small>You may confirm once you have reached this point.</small></div>
            </div>
          </div>
          <footer><span data-entry-rules-status>Scroll to the bottom to enable confirmation.</span><button class="team-primary-btn" type="button" data-accept-entry-rules="${esc(leagueKey)}" disabled>I’ve Read the Rules</button></footer>
        </section>
      </div>`;
    return `<article class="competition-recommendation-card ${leagueKey === recommendedLeagueId ? "is-intended" : ""}" data-recommended-competition="${esc(leagueKey)}">
      <header>
        <span class="team-discovery-game-mark">${esc(initials(item.game_title || "Game"))}</span>
        <div><small>${esc(item.game_title || "Game")} · ${esc(openCompetitionType(item.format))}</small><h4>${esc(item.league_name || "Competition")}</h4><p>${esc(first(item.public_summary, "Recommended for you."))}</p></div>
        <div class="competition-recommendation-actions">
          <em>${leagueKey === recommendedLeagueId ? "Your selected competition" : competitionIsClosing(item) ? "Closing Soon" : "Eligible"}</em>
          <button type="button" data-toggle-entry-details="${esc(leagueKey)}" aria-expanded="${expanded}" aria-controls="competitionEntryDetails${esc(leagueKey)}">${expanded ? "Hide details" : "View details"}</button>
        </div>
      </header>
      ${expanded ? `<div id="competitionEntryDetails${esc(leagueKey)}" class="competition-recommendation-details">
        <nav aria-label="${esc(item.league_name || "Competition")} entry options">
          <button class="${tab === "enter" ? "is-active" : ""}" type="button" data-entry-tab="enter" data-entry-league="${esc(leagueKey)}">${applied ? "Entry" : "Enter Team"}</button>
          <button type="button" data-open-entry-rules="${esc(leagueKey)}">Rules</button>
          <button class="${tab === "info" ? "is-active" : ""}" type="button" data-entry-tab="info" data-entry-league="${esc(leagueKey)}">Competition Info</button>
        </nav>
        <section>${tabPanel}</section>
      </div>` : ""}
      ${rulesModal}
    </article>`;
  }

  function renderDiscoverySummary(items: AnyRow[]) {
    const captainTeams = playerMemberships().filter(canManageMembership);
    const entered = items.filter((item) => Boolean(openCompetitionAppliedApp(item) || item.application_id)).length;
    const eligible = items.filter((item) => captainTeamsForCompetition(item, captainTeams).length > 0).length;
    const closing = items.filter(competitionIsClosing).length;
    const markup = `
      <h3>Ready to Join</h3>
      <ul><li class="is-ready"><span>✓</span>Profile Complete</li><li class="is-ready"><span>✓</span>${esc(captainTeams.length)} Eligible Team${captainTeams.length === 1 ? "" : "s"}</li><li class="${closing ? "is-urgent" : "is-ready"}"><span>${closing ? "◷" : "✓"}</span>${esc(closing)} Registration${closing === 1 ? "" : "s"} Closing Soon</li></ul>
      <button type="button" data-section-jump="settings">View My Teams <span>›</span></button>
      <small>${esc(items.length)} open · ${esc(eligible)} eligible · ${esc(entered)} entered</small>
    `;
    const readyCard = $("competitionReadyCard");
    if (readyCard) readyCard.innerHTML = markup;
    return "";
  }

  function renderOpenCompetitions() {
    const target = $("openCompetitionsList");
    if (!target) return;
    fillOpenCompetitionFilters();
    const filtered = filteredOpenCompetitions();
    const allFiltered = [...filtered];
    const tabMatches = (item: AnyRow) => discoveryTab === "all"
      || (discoveryTab === "eligible" && competitionIsEligible(item))
      || (discoveryTab === "closing" && competitionIsClosing(item))
      || (discoveryTab === "recent" && Date.now() - new Date(first(item.created_at, item.published_at) || 0).getTime() <= 30 * 86400000)
      || (discoveryTab === "entries" && competitionIsEntered(item));
    let items = allFiltered.filter(tabMatches);
    items.sort((a, b) => discoverySort === "closing" ? competitionDeadline(a) - competitionDeadline(b)
      : discoverySort === "starting" ? new Date(first(a.starts_at, a.start_date) || 0).getTime() - new Date(first(b.starts_at, b.start_date) || 0).getTime()
      : discoverySort === "name" ? clean(a.league_name).localeCompare(clean(b.league_name))
      : discoverySort === "relevant" ? Number(competitionIsEligible(b)) - Number(competitionIsEligible(a))
      : new Date(first(b.created_at, b.published_at, b.id) || 0).getTime() - new Date(first(a.created_at, a.published_at, a.id) || 0).getTime());
    const tabDefinitions = [["all", "All Competitions"], ["eligible", "Eligible"], ["closing", "Closing Soon"], ["recent", "Recently Added"], ["entries", "My Entries"]];
    const tabCount = (id: string) => allFiltered.filter((item) => id === "all" || (id === "eligible" && competitionIsEligible(item)) || (id === "closing" && competitionIsClosing(item)) || (id === "recent" && Date.now() - new Date(first(item.created_at, item.published_at) || 0).getTime() <= 30 * 86400000) || (id === "entries" && competitionIsEntered(item))).length;
    const intended = allFiltered.find((item) => discoveryCompetitionId(item) === recommendedLeagueId);
    const recommendations = [
      ...(intended ? [intended] : []),
      ...allFiltered.filter((item) => item !== intended && !competitionIsEntered(item) && competitionIsEligible(item)).sort((a, b) => Number(competitionIsClosing(b)) - Number(competitionIsClosing(a))),
    ].slice(0, 2);
    const pageSize = 10;
    const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
    discoveryPage = Math.min(discoveryPage, pageCount);
    const pageItems = items.slice((discoveryPage - 1) * pageSize, discoveryPage * pageSize);
    const rows = pageItems.length ? pageItems.map((item) => {
      const appliedApp = openCompetitionAppliedApp(item);
      const applied = Boolean(appliedApp || item.application_id);
      const deadline = competitionDeadline(item);
      const daysLeft = deadline > Date.now() ? Math.ceil((deadline - Date.now()) / 86400000) : 0;
      return `
        <article class="competition-directory-row ${applied ? "is-entered" : ""}">
          <span class="team-discovery-game-mark">${esc(initials(item.game_title || "Game"))}</span>
          <div class="competition-directory-identity"><span>${esc(item.game_title || "Game")} · ${esc(openCompetitionType(item.format))}</span><h3>${esc(item.league_name || "Competition")}</h3><p>${esc(first(item.public_summary, item.tournament_info, "Competition registration is open."))}</p></div>
          <dl><div><dt>Starts</dt><dd>${esc(formatDate(first(item.starts_at, item.start_date)))}</dd></div><div><dt>Entries close</dt><dd>${esc(formatDate(item.applications_close_at))}${competitionIsClosing(item) ? `<small>${esc(daysLeft <= 1 ? "Closing tomorrow" : `${daysLeft} days left`)}</small>` : ""}</dd></div><div><dt>Team</dt><dd>${esc(item.team_size || "Flexible")}</dd></div><div><dt>Region</dt><dd>${esc(competitionRegion(item))}</dd></div></dl>
          <div class="competition-directory-status">${statusPill(applied ? "entered" : competitionIsClosing(item) ? "closing soon" : "open")}</div>
          <div class="competition-directory-actions">${applied && appliedApp ? `<button class="team-secondary-btn" type="button" data-open-competition="${esc(competitionKey(appliedApp))}">Open Hub</button><span class="competition-entered-label">Already Entered</span>` : `<a class="team-secondary-link" href="${esc(item.public_url || `/viewer?leagueId=${encodeURIComponent(item.league_id || item.id)}`)}">Open Hub</a><a class="team-primary-btn" href="${esc(item.application_url || `/apply?leagueId=${item.league_id || item.id}`)}">Apply</a>`}</div>
        </article>
      `;
    }).join("") : `<section class="competition-empty"><h3>No competitions match these filters</h3><p>Try changing your game, region, format, or status filters.</p><button class="team-secondary-btn" type="button" data-reset-discovery>Reset Filters</button></section>`;
    const recommendedMarkup = recommendations.length ? `<section class="competition-recommended competition-entry-recommended"><div class="competition-section-head"><div><h3>Recommended For You</h3><p>${intended ? "Continue the competition entry you selected." : "Based on your linked teams and activity."}</p></div><button type="button" data-discovery-tab="all">View All</button></div><div>${recommendations.map(renderRecommendedCompetition).join("")}</div></section>` : "";
    target.innerHTML = `${renderDiscoverySummary(allFiltered)}${recommendedMarkup}<section class="competition-directory"><div class="competition-directory-toolbar"><nav>${tabDefinitions.map(([id, label]) => `<button class="${discoveryTab === id ? "is-active" : ""}" type="button" data-discovery-tab="${id}">${label}<span>${tabCount(id)}</span></button>`).join("")}</nav><label>Sort by<select id="competitionDiscoverySort"><option value="recent" ${discoverySort === "recent" ? "selected" : ""}>Recently Added</option><option value="closing" ${discoverySort === "closing" ? "selected" : ""}>Closing Soon</option><option value="starting" ${discoverySort === "starting" ? "selected" : ""}>Starting Soon</option><option value="name" ${discoverySort === "name" ? "selected" : ""}>Alphabetical</option><option value="relevant" ${discoverySort === "relevant" ? "selected" : ""}>Most Relevant</option></select></label></div><div class="competition-directory-list">${rows}</div><footer class="competition-pagination"><span>Showing ${items.length ? (discoveryPage - 1) * pageSize + 1 : 0} to ${Math.min(discoveryPage * pageSize, items.length)} of ${items.length} competitions</span><div>${Array.from({ length: pageCount }, (_, index) => `<button class="${discoveryPage === index + 1 ? "is-active" : ""}" type="button" data-discovery-page="${index + 1}">${index + 1}</button>`).join("")}</div></footer></section>`;
    if (intended && !intendedCompetitionFocused) {
      intendedCompetitionFocused = true;
      requestAnimationFrame(() => {
        target.querySelector(`[data-recommended-competition="${CSS.escape(recommendedLeagueId)}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  }

  function competitionChallenges(app: AnyRow | null) {
    const ladderChallenges = list(app?.ladder_challenges);
    if (ladderChallenges.length) {
      return ladderChallenges.map((challenge) => ({
        ...challenge,
        state: clean(challenge.status || "pending").toLowerCase(),
      }));
    }
    const matches = competitionMatches(app);
    return matches.filter((match) => clean(match.challenge_status || match.ladder_challenge_status || match.challenge_id || match.source).toLowerCase().includes("challenge")
      || clean(match.status).toLowerCase() === "challenge_pending"
      || clean(match.challenge_status)).map((match) => ({
        ...match,
        state: ["accepted", "scheduled", "live", "completed"].includes(matchState(match)) ? "accepted" : "pending",
      }));
  }

  function renderCompetitionStat(label: string, value: unknown, sub: unknown, icon: string) {
    return `
      <article class="registry-stat-card">
        <span class="registry-stat-icon">${teamIcon(clean(icon))}</span>
        <div><small>${esc(label)}</small><strong>${esc(value)}</strong><small>${esc(sub)}</small></div>
      </article>
    `;
  }

  function renderCompetitionHub() {
    const target = $("teamCompetitionHub");
    if (!target) return;
    const app = selectedCompetition();
    if (!app) {
      target.innerHTML = `<p class="team-empty">Open a competition to view its team hub.</p>`;
      return;
    }
    const matches = competitionMatches(app);
    const requests = competitionBroadcastRequests(app);
    const challenges = competitionChallenges(app);
    const completed = matches.filter((match) => matchState(match) === "completed");
    const upcoming = matches.filter((match) => matchState(match) !== "completed");
    const tabs = [
      ["overview", "Overview"],
      ["ladder", "Ladder"],
      ["challenges", "Challenges"],
      ["matches", "Matches"],
      ["results", "Results"],
      ["broadcast", "Broadcast"],
      ["rules", "Rules"],
    ];
    target.innerHTML = `
      <section class="team-hub-hero">
        <div>
          <button class="team-hub-back" type="button" data-section-jump="competitions">Back to Competitions</button>
          <span class="team-eyebrow">${esc(app.format || "Competition")}</span>
          <h2>${esc(app.league_name || "Competition")}</h2>
          <p>${esc(app.game_title || "Game")} - ${esc(app.competition_stage || "registration")}</p>
        </div>
        <div class="team-inline-actions">
          ${app.checked_in ? statusPill("checked in") : `<button class="team-secondary-btn" type="button" data-competition-checkin="${esc(app.id)}">Competition Check-in</button>`}
          <a class="team-secondary-link" href="/viewer?leagueId=${esc(app.league_id)}">Public Page</a>
          ${clean(app.format).toLowerCase() === "open_ladder" ? `<a class="team-secondary-link" href="/viewer/matches?leagueId=${esc(app.league_id)}">Upcoming Matches</a>` : ""}
        </div>
      </section>
      <section class="team-hub-tabs">
        ${tabs.map(([id, label]) => `<button class="${activeCompetitionTab === id ? "is-active" : ""}" type="button" data-competition-tab="${esc(id)}">${esc(label)}</button>`).join("")}
      </section>
      <section class="team-hub-panel">
        ${activeCompetitionTab === "overview" ? renderHubOverview(app, matches, challenges, requests) : ""}
        ${activeCompetitionTab === "ladder" ? renderHubLadder(app, challenges) : ""}
        ${activeCompetitionTab === "challenges" ? renderHubChallenges(app, challenges) : ""}
        ${activeCompetitionTab === "matches" ? renderHubMatches(app, upcoming) : ""}
        ${activeCompetitionTab === "results" ? renderHubResults(completed) : ""}
        ${activeCompetitionTab === "broadcast" ? renderHubBroadcast(app, matches, requests) : ""}
        ${activeCompetitionTab === "rules" ? renderHubRules(app) : ""}
      </section>
    `;
    if (activeCompetitionTab === "rules") bindHubRulesReader();
  }

  function renderHubOverview(app: AnyRow, matches: AnyRow[], challenges: AnyRow[], requests: AnyRow[]) {
    const rank = app.ladder_rank || app.seed || app.current_rank || "N/A";
    const wins = matches.filter((match) => String(match.winner_id || "") && [String(match.team1_id || ""), String(match.team2_id || "")].includes(String(match.winner_id || ""))).length;
    const pendingChallenge = challenges.find((item) => item.state === "pending");
    const nextMatch = matches.find((match) => matchState(match) !== "completed");
    return `
      <div class="registry-stat-grid team-portal-stat-grid">
        ${renderCompetitionStat("Rank", rank === "N/A" ? "N/A" : `#${rank}`, "Current ladder position", "rank")}
        ${renderCompetitionStat("Record", `${wins}-${Math.max(0, matches.filter((match) => matchState(match) === "completed").length - wins)}`, "Completed matches", "record")}
        ${renderCompetitionStat("Challenges", challenges.filter((item) => item.state === "pending").length, "Awaiting acceptance", "challenges")}
        ${renderCompetitionStat("Matches", matches.length, "Linked to this competition", "matches")}
        ${renderCompetitionStat("Broadcast", requests.length ? requests[0].status || "requested" : "Not requested", "broadcast")}
      </div>
      <div class="team-hub-overview-grid">
        <article class="team-manager-card">
          <h3>Next Action</h3>
          ${pendingChallenge ? `<p>Challenge waiting: ${esc(first(pendingChallenge.opponent_name, pendingChallenge.team1_name, pendingChallenge.team2_name, "Opponent"))}</p><button class="team-primary-btn" type="button" data-competition-tab="challenges">View Challenges</button>` : ""}
          ${!pendingChallenge && nextMatch ? `<p>Accepted match against ${esc(first(nextMatch.opponent_name, "TBA"))}.</p><button class="team-primary-btn" type="button" data-competition-tab="matches">Open Match</button>` : ""}
          ${!pendingChallenge && !nextMatch ? `<p>No immediate action. Check the ladder for teams you can challenge.</p><button class="team-primary-btn" type="button" data-competition-tab="ladder">View Ladder</button>` : ""}
        </article>
        <article class="team-manager-card">
          <h3>Competition Workflow</h3>
          <div class="team-hub-flow"><span>Ladder</span><span>Challenges</span><span>Matches</span><span>Results</span><span>Broadcast</span></div>
        </article>
      </div>
    `;
  }

  function renderHubLadder(app: AnyRow, challenges: AnyRow[]) {
    const roster = list(app.roster);
    const isOpenLadder = clean(app.format).toLowerCase() === "open_ladder";
    const hasLeft = clean(app.ladder_status).toLowerCase() === "left";
    return `
      <article class="team-manager-card">
        <div class="team-card-head">
          <div><h3>Ladder</h3><p>View the current team position and challenge context.</p></div>
          <div class="team-inline-actions">
            ${isOpenLadder && !hasLeft ? `<button class="team-secondary-btn" type="button" data-leave-ladder="${esc(app.id || app.application_id)}">Leave Ladder</button>` : ""}
            ${isOpenLadder && hasLeft && Number(app.applications_open || 0) === 1 ? `<button class="team-primary-btn" type="button" data-rejoin-ladder="${esc(app.id || app.application_id)}">Rejoin Ladder</button>` : ""}
            <a class="team-secondary-link" href="/viewer/ladder?leagueId=${esc(app.league_id)}">Full Ladder</a>
          </div>
        </div>
        <div class="team-match-detail-grid">
          <span>Your rank: <strong>${esc(hasLeft ? "Inactive archive" : app.ladder_rank || app.seed || app.current_rank || "Not ranked")}</strong></span>
          <span>Team: <strong>${esc(app.team_name || dashboard.account?.team_name || "Team")}</strong></span>
          <span>Game: <strong>${esc(app.game_title || "Game")}</strong></span>
          <span>Status: <strong>${esc(hasLeft ? "Left ladder" : "Active")}</strong></span>
        </div>
        <h4>Registered Lineup</h4>
        <ul class="team-match-lineup">
          ${roster.length ? roster.map((player) => `<li><span class="team-lineup-avatar">${esc(initials(rosterPlayerName(player)))}</span><div><strong>${esc(rosterPlayerName(player))}</strong><small>${esc(first(player.role, "Player"))}</small></div></li>`).join("") : `<li>No roster submitted.</li>`}
        </ul>
      </article>
    `;
  }

  function renderHubChallenges(app: AnyRow, challenges: AnyRow[]) {
    const ownTeamId = Number(app.created_team_id || 0);
    const ownRank = Number(app.ladder_rank || 0);
    const range = Math.max(1, Number(app.open_ladder_challenge_range || 5) || 5);
    const challengeDays = Math.max(1, Number(app.challenge_window_days || 7) || 7);
    const challengeStart = clean(app.challenge_window_start_time || "00:00");
    const challengeEnd = clean(app.challenge_window_end_time || "23:59");
    const challengeWindowCopy = `Within the next ${challengeDays} calendar day${challengeDays === 1 ? "" : "s"}, from ${challengeStart} to ${challengeEnd} ${first(app.competition_timezone, app.timezone, "")}.`;
    const responseHours = Math.max(1, Math.min(168, Number(app.open_ladder_response_hours || 48) || 48));
    const rematchCooldownHours = Math.max(0, Math.min(720, Number(app.open_ladder_rematch_cooldown_hours || 0) || 0));
    const eligible = list(app.ladder_teams).filter((team) => (
      clean(team.ladder_status || "active").toLowerCase() === "active"
      && Number(team.id || 0) !== ownTeamId
      && Number(team.seed || 0) < ownRank
      && ownRank - Number(team.seed || 0) <= range
    ));
    const hasPendingOrActive = challenges.some((challenge) => ["pending", "accepted"].includes(clean(challenge.state || challenge.status).toLowerCase()));
    return `
      <div class="team-two-column">
        <article class="team-manager-card">
          <h3>Challenges Sent & Received</h3>
          <div class="team-mini-list">
            ${challenges.length ? challenges.map((challenge) => `
              <div class="team-request-item">
                <strong>${esc(first(challenge.opponent_name, challenge.team1_name, challenge.team2_name, "Opponent"))}</strong>
                <span>${esc(formatMatchSlot(challenge))}</span>
                ${statusPill(challenge.state)}
                ${clean(challenge.state) === "pending" && Number(challenge.proposer_team_id || challenge.challenger_team_id) !== ownTeamId ? `
                  <div class="team-inline-actions">
                    ${list(challenge.proposed_slots).map((slot, index) => {
                      const expired = Boolean(slot.starts_at) && new Date(slot.starts_at).getTime() <= Date.now();
                      return `<button class="team-primary-btn" type="button" data-ladder-accept="${esc(challenge.id)}" data-slot-index="${index}" ${expired ? "disabled title=\"This proposed time has passed\"" : ""}>${expired ? "Expired" : "Accept"} ${esc(formatDate(first(slot.date, "")))} ${esc(first(slot.time, ""))} ${esc(first(slot.timezone, app.competition_timezone, app.timezone, ""))}</button>`;
                    }).join("")}
                    <button class="team-secondary-btn" type="button" data-ladder-decline="${esc(challenge.id)}">Decline</button>
                  </div>
                  <details>
                    <summary>Propose different times</summary>
                    <form class="team-stream-form" data-ladder-counter-form="${esc(challenge.id)}">
                      <p class="team-empty compact">${esc(challengeWindowCopy)}</p>
                      <label>Option 1<input name="slot_1" type="datetime-local" required></label>
                      <label>Option 2<input name="slot_2" type="datetime-local" required></label>
                      <label>Option 3<input name="slot_3" type="datetime-local" required></label>
                      <label>Message<input name="message" maxlength="1000" placeholder="Optional note"></label>
                      <button class="team-primary-btn" type="submit">Send Counter-offer</button>
                    </form>
                  </details>` : ""}
                ${clean(challenge.state) === "pending" && Number(challenge.proposer_team_id || challenge.challenger_team_id) === ownTeamId ? `<span>Waiting for the other captain.</span><button class="team-secondary-btn" type="button" data-ladder-cancel="${esc(challenge.id)}">Withdraw Current Offer</button>` : ""}
                ${list(challenge.events).length ? `<details><summary>Challenge timeline</summary><div class="team-mini-list">${list(challenge.events).map((event) => `<div class="team-request-item"><strong>${esc(clean(event.event_type || "updated").replaceAll("_", " "))}</strong><span>${esc(formatDate(event.created_at))}</span></div>`).join("")}</div></details>` : ""}
              </div>
            `).join("") : `<p class="team-empty">No open challenge records are linked yet.</p>`}
          </div>
        </article>
        <article class="team-manager-card">
          <h3>Create Challenge</h3>
          <p class="team-detail-copy">Challenge an active team up to ${esc(range)} position${range === 1 ? "" : "s"} above you and propose three future match times in ${esc(first(app.competition_timezone, app.timezone, "the competition timezone"))}. Each offer has a ${esc(responseHours)}-hour response window.${rematchCooldownHours ? ` The same pairing has a ${esc(rematchCooldownHours)}-hour cooldown after completion.` : ""}</p>
          ${hasPendingOrActive ? `<p class="team-empty">Resolve your current ladder challenge before creating another.</p>` : eligible.length ? `
            <form class="team-stream-form" data-ladder-challenge-form>
              <label>Opponent<select name="defender_team_id" required>${eligible.map((team) => `<option value="${esc(team.id)}">#${esc(team.seed)} ${esc(team.team_name || "Team")}</option>`).join("")}</select></label>
              <p class="team-empty compact">${esc(challengeWindowCopy)}</p>
              <label>Option 1<input name="slot_1" type="datetime-local" required></label>
              <label>Option 2<input name="slot_2" type="datetime-local" required></label>
              <label>Option 3<input name="slot_3" type="datetime-local" required></label>
              <label>Message<input name="message" maxlength="1000" placeholder="Optional note to the defending team"></label>
              <button class="team-primary-btn" type="submit">Send Challenge</button>
            </form>` : `<p class="team-empty">There are no eligible teams within your challenge range right now.</p>`}
          <a class="team-secondary-link" href="/viewer/ladder?leagueId=${esc(app.league_id)}">View Public Ladder</a>
        </article>
      </div>
    `;
  }

  function renderHubMatches(app: AnyRow, matches: AnyRow[]) {
    return matches.length ? matches.map((match) => renderTeamHubMatchCard(match)).join("") : `<p class="team-empty">No accepted or upcoming matches yet.</p>`;
  }

  function renderTeamHubMatchCard(match: AnyRow) {
    const startMs = matchStartTime(match);
    const requests = list(match.broadcast_requests);
    return `
      <article class="team-manager-card team-match-card team-hub-match-card">
        <div class="team-card-head">
          <div>${renderMatchSummary(match)}</div>
          <div class="team-inline-actions">
            <button class="team-secondary-btn" type="button" data-broadcast-request="${esc(match.id)}" data-request-kind="broadcast">Request Broadcast</button>
            <button class="team-secondary-btn" type="button" data-broadcast-request="${esc(match.id)}" data-request-kind="shoutcast">Request Shoutcast</button>
          </div>
        </div>
        <div class="team-match-detail-grid">
          <span>Scheduled: <strong>${esc(formatMatchSlot(match))}</strong></span>
          <span>Counter: <strong><span data-match-counter data-match-start="${esc(startMs)}">${esc(matchCounterText(startMs))}</span></strong></span>
          <span>Broadcast: <strong>${esc(match.streamers?.length ? match.streamers.map((item: AnyRow) => first(item.name, item.streamer_name, "Streamer")).join(", ") : "Not assigned")}</strong></span>
          <span>Casters: <strong>${esc(match.commentators?.length ? match.commentators.map((item: AnyRow) => first(item.name, item.display_name, "Caster")).join(", ") : "Not assigned")}</strong></span>
        </div>
        <form class="team-stream-form" data-team-stream-match="${esc(match.id)}">
          <input name="stream_url" type="url" placeholder="Team stream URL" value="${esc(first(match.team_stream_url, ""))}" />
          <button class="team-secondary-btn" type="submit">Save Team Stream</button>
        </form>
        ${requests.length ? `<div class="team-mini-list">${requests.map((request) => `<div class="team-request-item"><strong>${esc(request.request_kind || "broadcast")}</strong><span>${esc(request.stream_url || request.notes || "Requested")}</span>${statusPill(request.status || "pending")}</div>`).join("")}</div>` : ""}
      </article>
    `;
  }

  function renderHubResults(matches: AnyRow[]) {
    return matches.length ? matches.map((match) => `
      <article class="team-manager-card">
        <div class="team-card-head">
          <div>${renderMatchSummary(match)}</div>
          ${statusPill(match.result_submission?.status || match.status || "completed")}
        </div>
        <div class="team-match-detail-grid">
          <span>${esc(match.team1_name || "Team 1")}: <strong>${esc(match.team1_score ?? "-")}</strong></span>
          <span>${esc(match.team2_name || "Team 2")}: <strong>${esc(match.team2_score ?? "-")}</strong></span>
          <span>Winner: <strong>${esc(String(match.winner_id || "") === String(match.team1_id || "") ? match.team1_name : String(match.winner_id || "") === String(match.team2_id || "") ? match.team2_name : "Pending")}</strong></span>
          <span>Submitted: <strong>${esc(match.result_submission ? "Yes" : "No")}</strong></span>
        </div>
      </article>
    `).join("") : `<p class="team-empty">Completed match results will appear here.</p>`;
  }

  function renderHubBroadcast(app: AnyRow, matches: AnyRow[], requests: AnyRow[]) {
    const accepted = matches.filter((match) => matchState(match) !== "completed");
    return `
      <div class="team-two-column">
        <article class="team-manager-card">
          <h3>Broadcast Requests</h3>
          <div class="team-mini-list">
            ${requests.length ? requests.map((request) => `
              <div class="team-request-item">
                <strong>${esc(request.request_kind || "broadcast")}</strong>
                <span>${esc(first(request.match_label, request.stream_url, request.notes, "Match request"))}</span>
                ${statusPill(request.status || "pending")}
              </div>
            `).join("") : `<p class="team-empty">No broadcast or shoutcast requests yet.</p>`}
          </div>
        </article>
        <article class="team-manager-card">
          <h3>Match Stream Setup</h3>
          <p class="team-detail-copy">${esc(app.stream_info || "Request a broadcast or save your own team stream once a match has been accepted.")}</p>
          <div class="team-mini-list">
            ${accepted.length ? accepted.map((match) => `<div class="team-request-item"><strong>${esc(first(match.opponent_name, "Opponent"))}</strong><span>${esc(formatMatchSlot(match))}</span><button class="team-secondary-btn" type="button" data-competition-tab="matches">Open</button></div>`).join("") : `<p class="team-empty">No accepted matches are ready for broadcast requests.</p>`}
          </div>
        </article>
      </div>
    `;
  }

  function renderHubRules(app: AnyRow) {
    const rules = first(app.rules_text, app.tournament_info, "Rules have not been published for this competition yet.");
    const sections: Array<{ id: string; title: string; level: number; lines: string[] }> = [];
    const introLines: string[] = [];
    let currentSection: { id: string; title: string; level: number; lines: string[] } | null = null;
    rules.split(/\r?\n/).forEach((line) => {
      const markdownHeading = line.match(/^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/);
      const numberedHeading = line.match(/^\s*(\d+(?:\.\d+)*)\.\s+(.+?)\s*$/);
      if (markdownHeading || numberedHeading) {
        const title = markdownHeading ? markdownHeading[2].trim() : `${numberedHeading![1]}. ${numberedHeading![2].trim()}`;
        const level = markdownHeading ? markdownHeading[1].length : numberedHeading![1].split(".").length + 1;
        currentSection = { id: `team-rule-section-${sections.length + 1}`, title, level, lines: [] };
        sections.push(currentSection);
      } else if (currentSection) {
        currentSection.lines.push(line);
      } else {
        introLines.push(line);
      }
    });
    const ruleCopy = (value: string) => esc(value.trim()).replace(/\n/g, "<br>");
    const documentMarkup = `
      ${introLines.some((line) => line.trim()) ? `<div class="team-hub-rules-intro">${ruleCopy(introLines.join("\n"))}</div>` : ""}
      ${sections.length ? sections.map((section) => `
        <section id="${section.id}" class="team-hub-rule-section">
          <h${Math.min(section.level + 1, 6)}>${esc(section.title)}</h${Math.min(section.level + 1, 6)}>
          ${section.lines.some((line) => line.trim()) ? `<div>${ruleCopy(section.lines.join("\n"))}</div>` : ""}
        </section>`).join("") : `<div>${ruleCopy(rules)}</div>`}
    `;
    return `
      <article class="team-hub-rules-reader">
        <header><span>Rules</span><h3>${esc(app.league_name || "Competition")} Rules</h3></header>
        <div class="team-hub-rules-layout${sections.length ? "" : " is-single"}">
          ${sections.length ? `<nav aria-label="Rules sections">
            <button class="is-active" type="button" data-team-rule-target="teamRulesDocument"><span>Full view</span><small>All rules</small></button>
            ${sections.map((section, index) => `<button type="button" data-team-rule-target="${section.id}"><small>${String(index + 1).padStart(2, "0")}</small><span>${esc(section.title)}</span></button>`).join("")}
          </nav>` : ""}
          <div id="teamRulesDocument" class="team-hub-rules-document" tabindex="0">${documentMarkup}</div>
        </div>
      </article>`;
  }

  function bindHubRulesReader() {
    const reader = document.querySelector<HTMLElement>(".team-hub-rules-reader");
    const documentEl = reader?.querySelector<HTMLElement>(".team-hub-rules-document");
    if (!reader || !documentEl) return;
    const cards = Array.from(reader.querySelectorAll<HTMLElement>("[data-team-rule-target]"));
    const sections = Array.from(reader.querySelectorAll<HTMLElement>(".team-hub-rule-section"));
    const setActive = (targetId: string, keepVisible = true) => {
      const active = cards.find((card) => card.dataset.teamRuleTarget === targetId);
      cards.forEach((card) => card.classList.toggle("is-active", card === active));
      if (keepVisible && active) active.scrollIntoView({ behavior: "smooth", block: "nearest" });
    };
    cards.forEach((card) => card.addEventListener("click", () => {
      const targetId = card.dataset.teamRuleTarget || "";
      const target = document.getElementById(targetId);
      if (!target) return;
      setActive(targetId, false);
      if (target === documentEl) documentEl.scrollTo({ top: 0, behavior: "smooth" });
      else target.scrollIntoView({ behavior: "smooth", block: "start" });
    }));
    let frame = 0;
    documentEl.addEventListener("scroll", () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const documentTop = documentEl.getBoundingClientRect().top;
        const readingLine = documentEl.scrollTop + 18;
        let activeId = "teamRulesDocument";
        sections.forEach((section) => {
          const sectionTop = section.getBoundingClientRect().top - documentTop + documentEl.scrollTop;
          if (sectionTop <= readingLine) activeId = section.id;
        });
        setActive(activeId);
      });
    }, { passive: true });
  }

  function renderRequests() {
    const leagueSelect = $<HTMLSelectElement>("requestLeagueId");
    if (leagueSelect) {
      const current = leagueSelect.value;
      const applications = list(dashboard.applications);
      leagueSelect.innerHTML = `<option value="">Select competition...</option>` + applications.map((app) => `<option value="${esc(app.league_id)}">${esc(app.league_name || "Competition")}</option>`).join("");
      leagueSelect.value = current;
    }
    renderMarketSelect();
    const target = $("requestsList");
    if (!target) return;
    const invites = list(dashboard.rosterInvites);
    const tradeRequests = list(dashboard.tradeRequests);
    const pendingInvites = invites.filter((invite) => clean(invite.status).toLowerCase() === "pending");
    const handledInvites = invites.filter((invite) => clean(invite.status).toLowerCase() !== "pending");
    const pendingAdmin = tradeRequests.filter((request) => ["pending", "open", "submitted"].includes(clean(request.status || "pending").toLowerCase()));
    const requestHistory = [
      ...handledInvites.map((invite) => ({ ...invite, __kind: "invite" })),
      ...tradeRequests.filter((request) => !pendingAdmin.includes(request)).map((request) => ({ ...request, __kind: "admin" })),
    ];
    const renderGroup = (title: string, count: number, body: string, empty: string) => `
      <section class="team-request-group request-inbox-group">
        <div class="request-inbox-head"><h3>${esc(title)}</h3><span>${esc(count)}</span></div>
        ${body || `<p class="team-empty compact">${esc(empty)}</p>`}
      </section>
    `;
    target.innerHTML = [
      renderRequestInboxSummary(pendingInvites.length, pendingAdmin.length, requestHistory.length),
      renderGroup("Needs You", pendingInvites.length, pendingInvites.map(renderRosterInviteRequest).join(""), "No roster invites need your response."),
      renderGroup("Pending Admin", pendingAdmin.length, pendingAdmin.map(renderAdminRequestCard).join(""), "No admin requests are waiting for review."),
      renderGroup("History", requestHistory.length, requestHistory.slice(0, 12).map((item) => item.__kind === "invite" ? renderRosterInviteRequest(item) : renderAdminRequestCard(item)).join(""), "No completed request history yet."),
    ].join("");
  }

  function renderRequestInboxSummary(needsYou: number, pendingAdmin: number, history: number) {
    const headline = needsYou
      ? `${needsYou} invite${needsYou === 1 ? "" : "s"} need a response`
      : pendingAdmin
        ? `${pendingAdmin} admin request${pendingAdmin === 1 ? "" : "s"} waiting`
        : "Requests are clear";
    const copy = needsYou
      ? "Accept, deny, or block team invites from the Needs You queue."
      : pendingAdmin
        ? "Submitted roster and competition requests will update here when admins review them."
        : "New team invites and admin request updates will appear here.";
    return `
      <section class="request-inbox-summary">
        <div>
          <span>Request Inbox</span>
          <h3>${esc(headline)}</h3>
          <p>${esc(copy)}</p>
        </div>
        <div class="request-inbox-summary-stats">
          <article class="${needsYou ? "urgent" : ""}"><span>Needs You</span><strong>${esc(needsYou)}</strong></article>
          <article><span>Pending Admin</span><strong>${esc(pendingAdmin)}</strong></article>
          <article><span>History</span><strong>${esc(history)}</strong></article>
        </div>
      </section>
    `;
  }

  function renderRosterInviteRequest(invite: AnyRow) {
    const requester = first(invite.requester_in_game_name, invite.requester_display_name, invite.requester_player_name, "Captain");
    const pending = clean(invite.status).toLowerCase() === "pending";
    return `
      <article class="team-request-item request-inbox-card ${pending ? "needs-action" : ""}">
        <div>
          <strong>${esc(invite.team_name || "Team invite")}</strong>
          <span>${esc(requester)} invited you as ${esc(invite.role || "Player")}${invite.platform ? ` - ${esc(invite.platform)}` : ""}</span>
        </div>
        ${statusPill(invite.status || "pending")}
        ${invite.deny_message ? `<p>${esc(invite.deny_message)}</p>` : ""}
        ${pending ? `
          <div class="team-inline-actions">
            <button class="team-primary-btn" type="button" data-roster-invite-accept="${esc(invite.id)}">Accept</button>
            <button class="team-secondary-btn" type="button" data-roster-invite-deny="${esc(invite.id)}">Deny</button>
            <button class="team-secondary-btn danger" type="button" data-roster-invite-block="${esc(invite.id)}">Deny & Block</button>
          </div>
        ` : ""}
      </article>
    `;
  }

  function renderAdminRequestCard(request: AnyRow) {
    return `
      <article class="team-request-item request-inbox-card">
        <div>
          <strong>${esc(first(request.in_game_name, request.player_name, request.request_type, "Request"))}</strong>
          <span>${esc(first(request.league_name, "Competition"))} - ${esc(first(request.request_type, "request"))}</span>
        </div>
        ${statusPill(request.status || "pending")}
        ${request.reason ? `<p>${esc(request.reason)}</p>` : ""}
        ${request.admin_reason ? `<p>${esc(request.admin_reason)}</p>` : ""}
      </article>
    `;
  }

  function marketRowsForSource(source = "") {
    if (source === "free_agent") return list(playerMarket.freeAgents).map((player) => ({ ...player, __source: source }));
    if (source === "trade_listed") return list(playerMarket.tradeListed).map((player) => ({ ...player, __source: source }));
    if (source === "my_trade_list") return list(playerMarket.myTradeListed).map((player) => ({ ...player, __source: source }));
    return [];
  }

  function marketPlayerLabel(player: AnyRow) {
    const name = first(player.in_game_name, player.display_name, player.player_name, player.email, "Player");
    const team = first(player.team_name, player.short_name, player.__source === "free_agent" ? "Free agent" : "");
    return team ? `${name} - ${team}` : name;
  }

  function renderMarketSelect() {
    const source = $<HTMLSelectElement>("requestSource")?.value || "manual";
    const select = $<HTMLSelectElement>("requestMarketPlayer");
    if (!select) return;
    const rows = marketRowsForSource(source);
    select.disabled = source === "manual";
    select.innerHTML = `<option value="">${source === "manual" ? "Manual entry" : "Select player..."}</option>` + rows.map((player, index) => (
      `<option value="${esc(String(index))}">${esc(marketPlayerLabel(player))}</option>`
    )).join("");
  }

  function applySelectedMarketPlayer() {
    const source = $<HTMLSelectElement>("requestSource")?.value || "manual";
    const index = Number($<HTMLSelectElement>("requestMarketPlayer")?.value ?? -1);
    const player = marketRowsForSource(source)[index];
    if (!player) return;
    const type = $<HTMLSelectElement>("requestType");
    if (type && source === "free_agent") type.value = "add";
    if (type && source === "trade_listed") type.value = "trade";
    ($<HTMLInputElement>("requestPlayerName") || {}).value = clean(player.player_name || player.display_name);
    ($<HTMLInputElement>("requestInGameName") || {}).value = first(player.in_game_name, player.display_name, player.player_name);
    ($<HTMLInputElement>("requestRole") || {}).value = clean(player.role);
    ($<HTMLInputElement>("requestSteamEpicId") || {}).value = clean(player.steam_epic_id);
    const reason = $<HTMLTextAreaElement>("requestReason");
    if (reason && !reason.value) {
      reason.value = source === "trade_listed"
        ? `Requesting trade discussion for ${marketPlayerLabel(player)}.`
        : `Requesting to add free agent ${marketPlayerLabel(player)}.`;
    }
  }

  function selectedCaptainTeam() {
    const account = dashboard.account || {};
    const memberships = playerMemberships();
    const preserved = memberships.find((team) => captainTeamKey(team) === selectedCaptainTeamKey);
    if (preserved) return preserved;
    const selectedId = Number(account.selected_team_account_id || 0);
    const selected = memberships.find((team) => Number(team.team_account_id || 0) === selectedId)
      || memberships.find(canManageMembership)
      || memberships[0]
      || null;
    if (selected) selectedCaptainTeamKey = captainTeamKey(selected);
    return selected;
  }

  function captainRosterRows() {
    const team = selectedCaptainTeam();
    if (team?.registry_only) {
      const registryId = Number(team.registry_team_id || 0);
      return rosterDraft.filter((player) => Number(player.registry_team_id || 0) === registryId);
    }
    const teamId = Number(team?.team_account_id || dashboard.account?.selected_team_account_id || 0);
    return rosterDraft.filter((player) => !teamId || Number(player.team_account_id || 0) === teamId);
  }

  function captainPendingInvites(teamId: number) {
    return list(dashboard.teamRosterInvites)
      .filter((invite) => Number(invite.team_account_id || 0) === Number(teamId || 0))
      .filter((invite) => clean(invite.status).toLowerCase() === "pending");
  }

  function teamApplications(teamId: number) {
    return list(dashboard.applications).filter((app) => Number(app.team_account_id || 0) === Number(teamId || 0));
  }

  function teamApplicationIsActive(app: AnyRow) {
    const stage = clean(app.competition_stage).toLowerCase();
    const status = clean(app.status).toLowerCase();
    if (Number(app.is_archived || app.isArchived || 0) === 1) return false;
    if (["completed", "complete", "finished", "archived", "cancelled", "canceled"].includes(stage)) return false;
    if (["rejected", "withdrawn", "left", "removed", "cancelled", "canceled"].includes(status)) return false;
    return true;
  }

  function rosterPersonKey(player: AnyRow = {}) {
    return first(player.player_account_id, player.account_id, player.id, player.bracketr_player_id, player.platform_id, player.in_game_name, player.player_name).toLowerCase();
  }

  function rosterPersonName(player: AnyRow = {}) {
    return first(player.in_game_name, player.display_name, player.player_name, "Player");
  }

  function selectedCaptainCompetition() {
    const team = selectedCaptainTeam();
    const teamId = Number(team?.team_account_id || dashboard.account?.selected_team_account_id || 0);
    const apps = teamApplications(teamId);
    return selectedCaptainCompetitionId ? apps.find((app) => competitionKey(app) === selectedCaptainCompetitionId) || null : null;
  }

  function captainCompetitionRoster(app: AnyRow | null) {
    return list(app?.roster).filter((player) => rosterPersonName(player) !== "Player" || rosterPersonKey(player));
  }

  function captainAvailableRosterForCompetition(app: AnyRow | null) {
    const compKeys = new Set(captainCompetitionRoster(app).map(rosterPersonKey).filter(Boolean));
    return captainRosterRows()
      .filter((player) => rosterStatus(player.status) !== "inactive")
      .filter((player) => {
        const key = rosterPersonKey(player);
        return key && !compKeys.has(key);
      });
  }

  function captainRosterOption(player: AnyRow, index: number) {
    const key = rosterPersonKey(player) || `player-${index}`;
    return `<option value="${esc(key)}">${esc(rosterPersonName(player))}${player.role ? ` - ${esc(player.role)}` : ""}</option>`;
  }

  function renderCaptainRosterRequestModal(app: AnyRow | null) {
    if (!captainRosterRequestOpen || !app) return "";
    const compRoster = captainCompetitionRoster(app);
    const availableRoster = captainAvailableRosterForCompetition(app);
    const mode = captainRosterRequestMode;
    const canSubmit = mode === "add"
      ? availableRoster.length > 0
      : mode === "remove"
        ? compRoster.length > 0
        : availableRoster.length > 0 && compRoster.length > 0;
    return `
      <section class="team-modal captain-roster-request-modal" aria-hidden="false">
        <div class="team-modal-backdrop" data-close-captain-roster-request></div>
        <form class="team-modal-card wide captain-roster-request-card" data-captain-roster-request-form>
          <header class="team-modal-head">
            <div>
              <h2>Edit Competition Roster</h2>
              <p>${esc(first(app.league_name, "Competition"))} - requests are sent to competition admins for approval.</p>
            </div>
            <button class="ghost-btn" type="button" data-close-captain-roster-request>Close</button>
          </header>
          <div class="captain-roster-request-summary">
            <article><span>Competition</span><strong>${esc(first(app.league_name, "Competition"))}</strong></article>
            <article><span>Game</span><strong>${esc(first(app.game_title, "Not set"))}</strong></article>
            <article><span>Current Lineup</span><strong>${esc(compRoster.length)}</strong></article>
            <article><span>Available Team Players</span><strong>${esc(availableRoster.length)}</strong></article>
          </div>
          <label class="captain-request-mode">Request Type
            <select id="captainRosterRequestType">
              <option value="add" ${mode === "add" ? "selected" : ""}>Add player</option>
              <option value="remove" ${mode === "remove" ? "selected" : ""}>Remove player</option>
              <option value="replace" ${mode === "replace" ? "selected" : ""}>Swap / replace player</option>
            </select>
          </label>
          <div class="captain-roster-request-grid">
            <section class="${mode === "remove" ? "is-muted" : ""}">
              <div class="captain-subhead"><h4>Team Players Not In Comp</h4><span>${esc(availableRoster.length)}</span></div>
              <select id="captainRosterAddPlayer" ${mode === "remove" || !availableRoster.length ? "disabled" : ""}>
                ${availableRoster.length ? availableRoster.map(captainRosterOption).join("") : `<option value="">No team players available to add</option>`}
              </select>
              <p>For add or swap, choose from players already on your team but not in this competition lineup.</p>
            </section>
            <section class="${mode === "add" ? "is-muted" : ""}">
              <div class="captain-subhead"><h4>Competition Lineup</h4><span>${esc(compRoster.length)}</span></div>
              <select id="captainRosterRemovePlayer" ${mode === "add" || !compRoster.length ? "disabled" : ""}>
                ${compRoster.length ? compRoster.map(captainRosterOption).join("") : `<option value="">No competition lineup players found</option>`}
              </select>
              <p>For remove or swap, choose the player currently registered in this tournament or league lineup.</p>
            </section>
          </div>
          <label>Reason / Notes
            <textarea id="captainRosterRequestReason" rows="4" placeholder="Explain why this lineup change should be approved."></textarea>
          </label>
          ${captainRosterRequestMessage ? `<p class="team-message" data-tone="${captainRosterRequestMessage.startsWith("Request") ? "ok" : "error"}">${esc(captainRosterRequestMessage)}</p>` : `<p class="team-message">Admin approval is required before this lineup changes.</p>`}
          <div class="team-inline-actions">
            <button class="team-secondary-btn" type="button" data-close-captain-roster-request>Cancel</button>
            <button class="team-primary-btn" type="submit" ${canSubmit ? "" : "disabled"}>Submit Request</button>
          </div>
        </form>
      </section>
    `;
  }

  function renderTeamCompetitionRows(apps: AnyRow[], active: boolean) {
    if (!apps.length) return `<p class="team-empty compact">${active ? "No active competitions." : "No archived competitions."}</p>`;
    return apps.map((app) => `
      <article class="captain-comp-row ${competitionKey(app) === selectedCaptainCompetitionId ? "selected" : ""}" role="button" tabindex="0" data-captain-comp="${esc(competitionKey(app))}">
        <div>
          <strong>${esc(app.league_name || "Competition")}</strong>
          <small>${esc(first(app.game_title, app.format, "Game"))}</small>
        </div>
        <div>
          ${statusPill(app.status || app.competition_stage || (active ? "active" : "archived"))}
          ${active ? `<button class="brk-btn" type="button" data-open-captain-roster-request="${esc(competitionKey(app))}">Edit Roster</button>` : ""}
          ${active ? `<button class="brk-btn" type="button" data-request-leave-comp="${esc(app.league_id || "")}">Request Leave</button>` : ""}
        </div>
      </article>
    `).join("");
  }

  function renderCaptainRequests(teamId: number, pendingInvites: AnyRow[], apps: AnyRow[]) {
    const teamRequests = list(dashboard.tradeRequests).filter((request) => !teamId || Number(request.team_account_id || 0) === Number(teamId || 0));
    const activeApps = apps.filter(teamApplicationIsActive);
    return `
      <section class="captain-comp-section is-panel">
        <div class="captain-subhead"><h4>Roster Invites</h4><span>${esc(pendingInvites.length ? `${pendingInvites.length} pending` : "No pending invites")}</span></div>
        <div class="captain-comp-list">
          ${pendingInvites.length ? pendingInvites.map((invite) => `
            <div class="captain-player-row pending-invite">
              <span class="registry-avatar">${esc(initials(first(invite.in_game_name, invite.display_name, invite.player_name, "P")))}</span>
              <span><strong>${esc(first(invite.in_game_name, invite.display_name, invite.player_name, "Player"))}</strong><small>${esc(first(invite.bracketr_player_id, invite.email, "Invite sent"))}</small></span>
              ${statusPill(invite.role || "Player")}
              ${statusPill("pending")}
            </div>
          `).join("") : `<p class="team-empty compact">No roster invites are waiting on players.</p>`}
        </div>
      </section>
      <section class="captain-comp-section is-panel">
        <div class="captain-subhead"><h4>Admin Requests</h4><span>${esc(teamRequests.length)}</span></div>
        <div class="captain-comp-list">
          ${teamRequests.length ? teamRequests.map(renderAdminRequestCard).join("") : `<p class="team-empty compact">No admin roster or dispute requests yet.</p>`}
        </div>
      </section>
      <section class="captain-comp-section muted">
        <div class="captain-subhead"><h4>Quick Request</h4><span>${esc(activeApps.length ? `${activeApps.length} comps` : "No active comps")}</span></div>
        <p class="team-detail-copy">Roster changes are handled from the Comps tab. Select a competition, then use Edit Roster to request add, remove, or swap approval.</p>
        <button class="brk-btn primary" type="button" data-captain-center-tab="competitions">Open Comps</button>
      </section>
    `;
  }

  function renderCaptainSettingsSummary(team: AnyRow | null, account: AnyRow, rows: AnyRow[], activeApps: AnyRow[], canManage: boolean, canDeleteTeam: boolean, teamId: number) {
    const source = team || account;
    return `
      <section class="captain-comp-section is-panel">
        <div class="captain-subhead"><h4>Team Settings</h4><span>${esc(canManage ? "Editable" : "View only")}</span></div>
        <div class="captain-detail-list compact">
          <div><span>Team</span><strong>${esc(team ? teamDisplayName(team) : first(account.team_name, "No team"))}</strong></div>
          <div><span>Game</span><strong>${esc(teamGameLabel(source))}</strong></div>
          <div><span>Region</span><strong>${esc(first(source.region, source.team_region, "Not set"))}</strong></div>
          <div><span>Roster</span><strong>${esc(rows.length)}</strong></div>
          <div><span>Active comps</span><strong>${esc(activeApps.length)}</strong></div>
        </div>
        <div class="captain-detail-actions">
          ${canManage ? `<button class="brk-btn" type="button" data-open-modal="captainEditTeamModal">Edit Team Info</button>` : ""}
          ${canManage && canDeleteTeam ? `<button class="brk-btn danger delete-team-only" type="button" data-delete-captain-team="${esc(teamId)}">Delete Team</button>` : ""}
        </div>
      </section>
    `;
  }

  function renderCaptainCommandStrip(rows: AnyRow[], pendingInvites: AnyRow[], activeApps: AnyRow[], canManage: boolean) {
    const nextMatch = activeApps.flatMap((app) => list(app.matches).map((match) => ({ ...match, __league_name: app.league_name })))
      .filter((match) => matchState(match) !== "completed")
      .sort((a, b) => (matchStartTime(a) || Number.MAX_SAFE_INTEGER) - (matchStartTime(b) || Number.MAX_SAFE_INTEGER))[0] || null;
    const activePlayers = rows.filter((player) => rosterStatus(player.status) !== "removed").length;
    const needsAction = pendingInvites.length
      ? { tab: "requests", label: "Review invites", copy: `${pendingInvites.length} roster invite${pendingInvites.length === 1 ? "" : "s"} pending` }
      : nextMatch
        ? { tab: "competitions", label: "Prep match", copy: `${first(nextMatch.__league_name, nextMatch.league_name, "Competition")} - ${formatMatchSlot(nextMatch)}` }
        : activeApps.length
          ? { tab: "competitions", label: "Check comps", copy: `${activeApps.length} active competition${activeApps.length === 1 ? "" : "s"}` }
          : { tab: "competitions", label: "Enter comps", copy: "No active competitions yet" };
    return `
      <section class="captain-command-strip">
        <article>
          <span>Roster</span>
          <strong>${esc(activePlayers)}</strong>
          <small>${esc(pendingInvites.length ? `${pendingInvites.length} pending invites` : canManage ? "Ready to manage" : "View access")}</small>
        </article>
        <article>
          <span>Competitions</span>
          <strong>${esc(activeApps.length)}</strong>
          <small>${esc(nextMatch ? `Next: ${first(nextMatch.opponent_name, "Opponent TBA")}` : "No fixture queued")}</small>
        </article>
        <button class="${pendingInvites.length ? "urgent" : ""}" type="button" data-captain-center-tab="${esc(needsAction.tab)}">
          <span>Next Captain Action</span>
          <strong>${esc(needsAction.label)}</strong>
          <small>${esc(needsAction.copy)}</small>
        </button>
      </section>
    `;
  }

  function renderCaptainQuarters() {
    const target = $("captainQuartersWorkspace");
    if (!target) return;
    const account = dashboard.account || {};
    const memberships = playerMemberships();
    const team = selectedCaptainTeam();
    const teamId = Number(team?.team_account_id || account.selected_team_account_id || 0);
    const rows = captainRosterRows();
    const pendingInvites = captainPendingInvites(teamId);
    const selected = selectedPlayerId
      ? rows.find((player, index) => playerKey(player, index) === selectedPlayerId) || null
      : null;
    const isPlayerAccount = clean(account.account_type) === "player";
    const isLegacyTeamSession = !isPlayerAccount && Number(account.id || 0) === Number(teamId || 0);
    const canManage = isLegacyTeamSession || canManageMembership(team);
    const canDeleteRole = isLegacyTeamSession || clean(team?.role).toLowerCase() === "captain";
    const apps = teamApplications(teamId);
    const activeApps = apps.filter(teamApplicationIsActive);
    const inactiveApps = apps.filter((app) => !teamApplicationIsActive(app));
    const selectedCompetitionDetail = selectedCaptainCompetitionId
      ? apps.find((app) => competitionKey(app) === selectedCaptainCompetitionId) || null
      : null;
    const selectedCompetitionRoster = captainCompetitionRoster(selectedCompetitionDetail);
    const selectedCompetitionAvailable = captainAvailableRosterForCompetition(selectedCompetitionDetail);
    const canDeleteTeam = Boolean(teamId && canDeleteRole && activeApps.length === 0);
    const isRocketLeagueTeam = clean(teamGameLabel(team || account)).toLowerCase().includes("rocket league");
    const detailTitle = selected
      ? first(selected.in_game_name, selected.player_name, "Player")
      : selectedCompetitionDetail
        ? first(selectedCompetitionDetail.league_name, "Competition")
        : (team ? teamDisplayName(team) : first(account.team_name, "No team selected"));
    const detailSub = selected
      ? playerRecordLabel(selected)
      : selectedCompetitionDetail
        ? first(selectedCompetitionDetail.game_title, selectedCompetitionDetail.format, "Competition details")
        : (team ? "Team information" : "Create a team to begin.");
    target.innerHTML = `
      <section class="captain-layout">
        <aside class="captain-column captain-team-list">
          <div class="captain-column-head">
            <div><h3>Teams</h3><p>All teams you belong to. Captain-access teams can be managed here.</p></div>
            <button class="brk-btn primary" type="button" data-open-modal="captainCreateTeamModal">+</button>
          </div>
          <div class="captain-search"><input placeholder="Search teams..." data-captain-team-search /></div>
          <div class="captain-list">
            ${memberships.length ? memberships.map((item) => {
              const active = captainTeamKey(item) === captainTeamKey(team || {});
              return `
                <button class="captain-list-row ${active ? "selected" : ""} ${item.registry_only ? "registry-only" : ""}" type="button" data-captain-team-key="${esc(captainTeamKey(item))}" title="${esc(item.registry_only ? `Roster managed by ${item.organisation_name || "the organisation"}` : "Open team")}">
                  <span class="registry-avatar">${esc(initials(first(item.team_name, item.short_name, "Team")))}</span>
                  <span><strong>${esc(teamDisplayName(item))}</strong><small>${esc([item.bracketr_team_id, teamGameLabel(item), item.registry_only ? "Organisation roster" : canManageMembership(item) ? "Captain access" : first(item.role, "Player")].filter(Boolean).join(" - "))}</small></span>
                  ${statusPill(item.status || "active")}
                </button>
              `;
            }).join("") : `<p class="team-empty compact">No teams yet.</p>`}
          </div>
        </aside>

        <section class="captain-column captain-roster-list">
          <div class="captain-column-head">
            <div><h3>${esc(team ? teamDisplayName(team) : first(account.team_name, "Team Workspace"))}</h3><p>${captainCenterTab === "players" ? "Uncapped roster. Captains and co-captains can edit." : captainCenterTab === "competitions" ? "Competitions this team has entered." : captainCenterTab === "requests" ? "Roster invites and admin request state." : "Team identity and setup summary."}</p></div>
            ${canManage && captainCenterTab === "players" ? `<button class="brk-btn primary" type="button" data-open-modal="captainAddPlayerModal">Add Player</button>` : ""}
          </div>
          ${renderCaptainCommandStrip(rows, pendingInvites, activeApps, canManage)}
          <div class="captain-card-tabs" role="tablist" aria-label="Captain Quarters section">
            <button class="${captainCenterTab === "players" ? "is-active" : ""}" type="button" data-captain-center-tab="players">Team Players</button>
            <button class="${captainCenterTab === "competitions" ? "is-active" : ""}" type="button" data-captain-center-tab="competitions">Comps</button>
            <button class="${captainCenterTab === "requests" ? "is-active" : ""}" type="button" data-captain-center-tab="requests">Requests</button>
            <button class="${captainCenterTab === "settings" ? "is-active" : ""}" type="button" data-captain-center-tab="settings">Settings</button>
          </div>
          ${captainCenterTab === "players" ? `
            <div class="captain-search"><input placeholder="Search players..." data-captain-player-search /></div>
            <div class="captain-roster-table">
              <div class="captain-table-head"><span>Name</span><span>Role</span><span>Status</span></div>
              ${rows.length ? rows.map((player, index) => {
                const key = playerKey(player, index);
                return `
                  <button class="captain-player-row ${key === selectedPlayerId ? "selected" : ""}" type="button" data-captain-player="${esc(key)}">
                    <span class="registry-avatar">${esc(initials(first(player.in_game_name, player.player_name, "P")))}</span>
                    <span><strong>${esc(first(player.in_game_name, player.player_name, "Player"))}</strong><small>${esc(first(playerPlatformId(player), player.email, "No ID"))}</small></span>
                    ${statusPill(first(player.role, "Player"))}
                    ${statusPill(rosterStatus(player.status))}
                  </button>
                `;
              }).join("") : `<p class="team-empty">No players assigned yet. Add players by Bracketr ID.</p>`}
              ${pendingInvites.length ? `
                <div class="captain-pending-heading">Pending Invites</div>
                ${pendingInvites.map((invite) => `
                  <div class="captain-player-row pending-invite">
                    <span class="registry-avatar">${esc(initials(first(invite.in_game_name, invite.display_name, invite.player_name, "P")))}</span>
                    <span><strong>${esc(first(invite.in_game_name, invite.display_name, invite.player_name, "Player"))}</strong><small>${esc(first(invite.bracketr_player_id, invite.email, "Invite sent"))}</small></span>
                    ${statusPill(invite.role || "Player")}
                    ${statusPill("pending")}
                  </div>
                `).join("")}
              ` : ""}
            </div>
          ` : captainCenterTab === "competitions" ? `
            <section class="captain-comp-section is-panel">
              <div class="captain-subhead"><h4>Current Competitions</h4><span>${esc(activeApps.length ? `${activeApps.length} active` : "No active competition links")}</span></div>
              <div class="captain-comp-list">${renderTeamCompetitionRows(activeApps, true)}</div>
            </section>
            ${inactiveApps.length ? `
              <section class="captain-comp-section muted">
                <div class="captain-subhead"><h4>Archived / Inactive</h4><span>${esc(inactiveApps.length)}</span></div>
                <div class="captain-comp-list">${renderTeamCompetitionRows(inactiveApps, false)}</div>
              </section>
            ` : ""}
          ` : ""}
          ${captainCenterTab === "requests" ? renderCaptainRequests(teamId, pendingInvites, apps) : ""}
          ${captainCenterTab === "settings" ? renderCaptainSettingsSummary(team, account, rows, activeApps, canManage, canDeleteTeam, teamId) : ""}
        </section>

        <aside class="captain-column captain-detail-panel">
          <div class="captain-detail-head">
            <div>
              <span class="registry-avatar large">${esc(initials(detailTitle))}</span>
              <div><h3>${esc(detailTitle)}</h3><p>${esc(detailSub)}</p></div>
            </div>
            ${selected
              ? (canManage ? `<button class="brk-btn" type="button" data-edit-player="${esc(playerKey(selected, rosterDraft.indexOf(selected)))}">Edit</button>` : "")
              : selectedCompetitionDetail
                ? (teamApplicationIsActive(selectedCompetitionDetail) ? `<button class="brk-btn primary" type="button" data-open-captain-roster-request="${esc(competitionKey(selectedCompetitionDetail))}">Edit Roster</button>` : "")
                : (canManage ? `<button class="brk-btn" type="button" data-open-modal="captainEditTeamModal">Edit</button>` : "")}
          </div>
          ${selected ? `
            <div class="captain-detail-list">
              <div><span>Bracketr ID</span><strong>${esc(first(selected.bracketr_player_id, "Not linked"))}</strong></div>
              <div><span>Steam ID</span><strong>${esc(first(playerSteamId(selected), "Not linked"))}</strong></div>
              <div><span>Epic ID</span><strong>${esc(first(playerEpicId(selected), "Not linked"))}</strong></div>
              <div><span>PlayStation ID</span><strong>${esc(first(selected.ps4_id, "Not linked"))}</strong></div>
              <div><span>Xbox ID</span><strong>${esc(first(selected.xbox_id, "Not linked"))}</strong></div>
              ${isRocketLeagueTeam ? `
                <div><span>Tracker Profile</span><strong>${selected.player_tracker_link ? `<a href="${esc(selected.player_tracker_link)}" target="_blank" rel="noopener noreferrer">Open profile ↗</a>` : "Not linked"}</strong></div>
                <div><span>1v1 MMR</span><strong>${esc(first(selected.rocket_league_mmr_1v1, "Not set"))}</strong></div>
                <div><span>2v2 MMR</span><strong>${esc(first(selected.rocket_league_mmr_2v2, "Not set"))}</strong></div>
                <div><span>3v3 MMR</span><strong>${esc(first(selected.rocket_league_mmr_3v3, "Not set"))}</strong></div>
              ` : ""}
              <div><span>Role</span><strong>${esc(first(selected.role, "Player"))}</strong></div>
              <div><span>Access</span><strong>${esc(accessLevel(selected.access_level) === "manager" ? "Roster editor" : "Player")}</strong></div>
              <div><span>Status</span><strong>${esc(rosterStatus(selected.status))}</strong></div>
              <div><span>Email</span><strong>${esc(first(selected.email, "Not linked"))}</strong></div>
            </div>
          ` : selectedCompetitionDetail ? `
            <div class="captain-detail-list">
              <div><span>Competition</span><strong>${esc(first(selectedCompetitionDetail.league_name, "Competition"))}</strong></div>
              <div><span>Game</span><strong>${esc(first(selectedCompetitionDetail.game_title, "Not set"))}</strong></div>
              <div><span>Status</span><strong>${esc(first(selectedCompetitionDetail.status, "pending"))}</strong></div>
              <div><span>Stage</span><strong>${esc(first(selectedCompetitionDetail.competition_stage, "registration"))}</strong></div>
              <div><span>Format</span><strong>${esc(first(selectedCompetitionDetail.format, "Not set"))}</strong></div>
              <div><span>Starts</span><strong>${esc(first(formatDate(selectedCompetitionDetail.start_date || selectedCompetitionDetail.tournament_date), "Not scheduled"))}</strong></div>
              <div><span>Check-in</span><strong>${esc(first(formatDate(selectedCompetitionDetail.checkin_opened_at), "Not opened"))}</strong></div>
            </div>
            <section class="captain-comp-lineup-panel">
              <div class="captain-subhead"><h4>Tournament Lineup</h4><span>${esc(selectedCompetitionRoster.length)}</span></div>
              <div class="captain-mini-roster">
                ${selectedCompetitionRoster.length ? selectedCompetitionRoster.map((player) => `
                  <div>
                    <span class="registry-avatar">${esc(initials(rosterPersonName(player)))}</span>
                    <strong>${esc(rosterPersonName(player))}</strong>
                    <small>${esc(first(player.role, player.bracketr_player_id, "Player"))}</small>
                  </div>
                `).join("") : `<p class="team-empty compact">No locked lineup snapshot found for this competition yet.</p>`}
              </div>
            </section>
            <section class="captain-comp-lineup-panel">
              <div class="captain-subhead"><h4>Available Team Players</h4><span>${esc(selectedCompetitionAvailable.length)}</span></div>
              <div class="captain-mini-roster">
                ${selectedCompetitionAvailable.length ? selectedCompetitionAvailable.slice(0, 6).map((player) => `
                  <div>
                    <span class="registry-avatar">${esc(initials(rosterPersonName(player)))}</span>
                    <strong>${esc(rosterPersonName(player))}</strong>
                    <small>${esc(first(player.role, playerPlatformId(player), "Team roster"))}</small>
                  </div>
                `).join("") : `<p class="team-empty compact">Every active team player is already in this competition lineup.</p>`}
              </div>
            </section>
          ` : `
            <div class="captain-detail-list">
              <div><span>Team</span><strong>${esc(team ? teamDisplayName(team) : first(account.team_name, "No team"))}</strong></div>
              <div><span>Team Code</span><strong>${esc(first(team?.bracketr_team_id, "Generating"))}</strong></div>
              <div><span>Game</span><strong>${esc(teamGameLabel(team || account))}</strong></div>
              <div><span>Tag</span><strong>${esc(first(team?.short_name, account.short_name, "-"))}</strong></div>
              <div><span>Roster</span><strong>${esc(pendingInvites.length ? `${rows.length} + ${pendingInvites.length} pending` : rows.length)}</strong></div>
              <div><span>Captains</span><strong>${esc(rows.filter(canManageMembership).length)}</strong></div>
              <div><span>Active comps</span><strong>${esc(activeApps.length)}</strong></div>
            </div>
          `}
          <div class="captain-detail-actions">
            ${selected && canManage ? `
              ${isRocketLeagueTeam ? `<button class="brk-btn primary" type="button" data-refresh-player-tracker="${esc(selected.id || "")}">Refresh MMR</button>` : ""}
              <button class="brk-btn" type="button" data-edit-player="${esc(playerKey(selected, rosterDraft.indexOf(selected)))}">Edit Player</button>
              <button class="brk-btn danger" type="button" data-remove-roster-player="${esc(selected.id || "")}">Remove From Team</button>
            ` : selected ? "" : selectedCompetitionDetail ? `
              ${teamApplicationIsActive(selectedCompetitionDetail) ? `<button class="brk-btn primary" type="button" data-open-captain-roster-request="${esc(competitionKey(selectedCompetitionDetail))}">Edit Roster</button>` : ""}
              ${teamApplicationIsActive(selectedCompetitionDetail) ? `<button class="brk-btn danger" type="button" data-request-leave-comp="${esc(selectedCompetitionDetail.league_id || "")}">Request Leave Competition</button>` : ""}
            ` : canManage ? `
              <button class="brk-btn" type="button" data-open-modal="captainEditTeamModal">Edit Team Info</button>
            ` : ""}
            ${selected || selectedCompetitionDetail || !canManage ? "" : canDeleteTeam
              ? `<button class="brk-btn danger delete-team-only" type="button" data-delete-captain-team="${esc(teamId)}">Delete Team</button>`
              : `<button class="brk-btn danger delete-team-only" type="button" data-disband-team ${teamId ? "" : "disabled"}>${activeApps.length ? "Request Team Removal" : "Captain Only"}</button>`}
          </div>
        </aside>
        ${renderCaptainRosterRequestModal(selectedCompetitionDetail)}
      </section>
    `;
  }

  function renderSettings() {
    renderCaptainQuarters();
    const account = dashboard.account || {};
    const team = selectedCaptainTeam();
    const formSource = team || account;
    const setInput = (id: string, value: unknown) => {
      const el = $(id) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
      if (el && document.activeElement !== el) el.value = clean(value);
    };
    setInput("teamNameInput", formSource.team_name);
    setInput("shortNameInput", formSource.short_name);
    setInput("contactNameInput", formSource.contact_name);
    setInput("discordInput", formSource.discord);
    setInput("regionInput", formSource.region || formSource.team_region);
    fillCaptainGameSelect();
    setInput("platformInput", teamGameTitles(formSource)[0] || formSource.platform || formSource.team_platform);
    setInput("websiteInput", formSource.website_url);
    setInput("preferredContactInput", formSource.preferred_contact);
    setInput("bioInput", formSource.bio);
    setInput("availabilityInput", formSource.availability_note);
    setInput("socialXInput", formSource.social_x);
    setInput("socialYoutubeInput", formSource.social_youtube);
    const teamName = first(formSource.team_name, account.team_name, "Team");
    renderImageSlot("logoPreview", selectedLogoPreviewUrl || formSource.pending_logo_path || formSource.logo_path, teamName, `${teamName} logo preview`);
    renderBannerPreview("bannerPreview", selectedBannerPreviewUrl || formSource.pending_banner_path || formSource.banner_path);
  }

  function renderNotification(item: AnyRow) {
    const achievementUrl = ["achievement", "trophy"].includes(clean(item.type).toLowerCase())
      ? "/dashboard?view=user&section=achievements"
      : "";
    const content = `
      <article class="team-notification ${Number(item.is_read) ? "is-read" : "is-unread"}">
        <strong>${esc(item.title || "Notification")}</strong>
        ${item.message ? `<p>${esc(item.message)}</p>` : ""}
        <span>${esc(item.created_at || "")}</span>
      </article>
    `;
    return achievementUrl ? `<a class="team-notification-link" href="${achievementUrl}">${content}</a>` : content;
  }

  function renderNotifications() {
    const target = $("notificationsList");
    const notifications = list(dashboard.notifications);
    const pendingInvites = list(dashboard.rosterInvites).filter((invite) => clean(invite.status).toLowerCase() === "pending");
    const unreadNotices = notifications.filter((item) => !Number(item.is_read)).length;
    const unread = unreadNotices + pendingInvites.length;
    const bell = $("teamNotificationBell");
    const badge = $("teamNotificationBadge");
    const dropdown = $("teamNotificationDropdown");
    const summary = $("teamNotificationSummary");

    if (bell) bell.setAttribute("aria-expanded", notificationsOpen ? "true" : "false");
    if (dropdown) dropdown.classList.toggle("hidden", !notificationsOpen);
    if (badge) {
      badge.textContent = String(unread > 99 ? "99+" : unread);
      badge.classList.toggle("hidden", unread === 0);
    }
    if (summary) summary.textContent = unread ? `${unread} unread update${unread === 1 ? "" : "s"}` : "No unread updates";
    if (!target) return;
    const inviteCards = pendingInvites.map((invite) => `
      <article class="team-manager-card">
        <div class="team-notification is-unread">
          <strong>Team invite: ${esc(invite.team_name || "Team")}</strong>
          <p>${esc(first(invite.requester_in_game_name, invite.requester_display_name, "Captain"))} invited you as ${esc(invite.role || "Player")}.</p>
          <div class="team-inline-actions">
            <button class="team-primary-btn" type="button" data-roster-invite-accept="${esc(invite.id)}">Accept</button>
            <button class="team-secondary-btn" type="button" data-roster-invite-deny="${esc(invite.id)}">Deny</button>
          </div>
        </div>
      </article>
    `).join("");
    const notificationCards = notifications.slice(0, 8).map((item) => `<article class="team-manager-card">${renderNotification(item)}</article>`).join("");
    const notificationSummary = `
      <section class="team-notification-summary">
        <article class="${pendingInvites.length ? "urgent" : ""}"><span>Invites</span><strong>${esc(pendingInvites.length)}</strong></article>
        <article class="${unreadNotices ? "urgent" : ""}"><span>Unread</span><strong>${esc(unreadNotices)}</strong></article>
        <article><span>Total</span><strong>${esc(notifications.length)}</strong></article>
      </section>
    `;
    target.innerHTML = inviteCards || notificationCards
      ? `${notificationSummary}${inviteCards}${notificationCards}`
      : `${notificationSummary}<p class="team-empty">No notifications yet.</p>`;
  }

  function renderActivity() {
    const target = $("activityList");
    if (!target) return;
    const rows = list(dashboard.activity);
    const actionCounts = rows.reduce((acc, item) => {
      const key = clean(item.action).replaceAll("_", " ") || "Activity";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const topAction = Object.entries(actionCounts).sort((a, b) => b[1] - a[1])[0];
    const latest = rows[0] || null;
    target.innerHTML = `
      <section class="activity-summary">
        <article><span>Total</span><strong>${esc(rows.length)}</strong><small>recent actions</small></article>
        <article><span>Most Common</span><strong>${esc(topAction?.[0] || "None")}</strong><small>${esc(topAction ? `${topAction[1]} event${topAction[1] === 1 ? "" : "s"}` : "No events yet")}</small></article>
        <article><span>Latest</span><strong>${esc(latest ? clean(latest.action).replaceAll("_", " ") || "Activity" : "None")}</strong><small>${esc(latest?.created_at || "Waiting for activity")}</small></article>
      </section>
      <section class="activity-timeline">
        ${rows.length ? rows.map((item) => `
          <article class="team-manager-card team-activity-card">
            <span>${esc(item.created_at || "")}</span>
            <strong>${esc(clean(item.action).replaceAll("_", " ") || "Activity")}</strong>
            ${item.details ? `<p>${esc(item.details)}</p>` : ""}
          </article>
        `).join("") : `<p class="team-empty">No activity yet.</p>`}
      </section>
    `;
  }

  function openPlayerDrawer(id = "") {
    const player = id ? rosterDraft.find((item, index) => playerKey(item, index) === id) : null;
    const index = player ? rosterDraft.indexOf(player) : -1;
    ($<HTMLInputElement>("teamPlayerIndex") || {}).value = String(index);
    setText("teamPlayerDrawerTitle", player ? "Edit Player" : "Add Player");
    ($<HTMLInputElement>("teamPlayerInGameName") || {}).value = clean(player?.in_game_name);
    ($<HTMLInputElement>("teamPlayerName") || {}).value = clean(player?.player_name);
    ($<HTMLInputElement>("teamPlayerEmail") || {}).value = clean(player?.email);
    ($<HTMLInputElement>("teamPlayerPlatformId") || {}).value = player ? playerPlatformId(player) : "";
    ($<HTMLInputElement>("teamPlayerSteamId") || {}).value = player ? playerSteamId(player) : "";
    ($<HTMLInputElement>("teamPlayerEpicId") || {}).value = player ? playerEpicId(player) : "";
    ($<HTMLInputElement>("teamPlayerPs4Id") || {}).value = clean(player?.ps4_id);
    ($<HTMLInputElement>("teamPlayerXboxId") || {}).value = clean(player?.xbox_id);
    ($<HTMLInputElement>("teamPlayerMmr1v1") || {}).value = clean(player?.rocket_league_mmr_1v1);
    ($<HTMLInputElement>("teamPlayerMmr2v2") || {}).value = clean(player?.rocket_league_mmr_2v2);
    ($<HTMLInputElement>("teamPlayerMmr3v3") || {}).value = clean(player?.rocket_league_mmr_3v3);
    ($<HTMLInputElement>("teamPlayerTrackerLink") || {}).value = clean(player?.player_tracker_link) || rocketLeagueTrackerLink(player ? playerSteamId(player) : "", player ? playerEpicId(player) : "", clean(player?.ps4_id), clean(player?.xbox_id));
    $("teamPlayerRocketLeagueFields")?.classList.toggle("hidden", !clean(teamGameLabel(selectedCaptainTeam() || dashboard.account || {})).toLowerCase().includes("rocket league"));
    ($<HTMLSelectElement>("teamPlayerRole") || {}).value = teamRole(player?.role);
    ($<HTMLSelectElement>("teamPlayerAccess") || {}).value = accessLevel(player?.access_level);
    ($<HTMLSelectElement>("teamPlayerStatus") || {}).value = rosterStatus(player?.status);
    ($<HTMLSelectElement>("teamPlayerTradeStatus") || {}).value = tradeStatus(player?.trade_status);
    ($<HTMLInputElement>("teamPlayerGames") || {}).value = list(player?.games).map(clean).filter(Boolean).join(", ");
    $("deleteTeamPlayerBtn")?.classList.toggle("hidden", !player);
    $("teamPlayerDrawer")?.classList.add("open");
    $<HTMLInputElement>("teamPlayerInGameName")?.focus();
  }

  function closePlayerDrawer() {
    $("teamPlayerDrawer")?.classList.remove("open");
    setMessage("teamPlayerMessage", "Update roster details, team role, and portal access.");
  }

  function savePlayerFromDrawer() {
    const index = Number($<HTMLInputElement>("teamPlayerIndex")?.value || -1);
    const player = {
      player_name: clean($<HTMLInputElement>("teamPlayerName")?.value),
      in_game_name: clean($<HTMLInputElement>("teamPlayerInGameName")?.value),
      email: clean($<HTMLInputElement>("teamPlayerEmail")?.value).toLowerCase(),
      steam_epic_id: clean($<HTMLInputElement>("teamPlayerSteamId")?.value),
      steam_id: clean($<HTMLInputElement>("teamPlayerSteamId")?.value),
      epic_id: clean($<HTMLInputElement>("teamPlayerEpicId")?.value),
      ps4_id: clean($<HTMLInputElement>("teamPlayerPs4Id")?.value),
      xbox_id: clean($<HTMLInputElement>("teamPlayerXboxId")?.value),
      rocket_league_mmr_1v1: clean($<HTMLInputElement>("teamPlayerMmr1v1")?.value),
      rocket_league_mmr_2v2: clean($<HTMLInputElement>("teamPlayerMmr2v2")?.value),
      rocket_league_mmr_3v3: clean($<HTMLInputElement>("teamPlayerMmr3v3")?.value),
      player_tracker_link: clean($<HTMLInputElement>("teamPlayerTrackerLink")?.value),
      bracketr_player_id: clean($<HTMLInputElement>("teamPlayerPlatformId")?.value),
      role: teamRole($<HTMLSelectElement>("teamPlayerRole")?.value),
      access_level: accessLevel($<HTMLSelectElement>("teamPlayerAccess")?.value),
      status: rosterStatus($<HTMLSelectElement>("teamPlayerStatus")?.value),
      trade_status: tradeStatus($<HTMLSelectElement>("teamPlayerTradeStatus")?.value),
      games: splitGames($<HTMLInputElement>("teamPlayerGames")?.value),
    };
    if (!player.in_game_name) throw new Error("In-game name is required.");
    if (index >= 0) rosterDraft[index] = { ...rosterDraft[index], ...player };
    else rosterDraft.push(player);
    selectedPlayerId = playerKey(player, Math.max(index, rosterDraft.length - 1));
    closePlayerDrawer();
    render();
  }

  async function loadMe({ quiet = false, retry = true } = {}) {
    if (dashboardRefreshInFlight) return;
    dashboardRefreshInFlight = true;
    if (resetToken || playerActivateToken) {
      showAuth();
      dashboardRefreshInFlight = false;
      return;
    }
    try {
      const [dashboardResult, marketResult, connectedResult, oauthResult] = await Promise.all([
        api("/api/team-portal/me"),
        api("/api/team-portal/market").catch(() => ({})),
        api("/api/auth/connected-accounts").catch(() => ({ accounts: [] })),
        api("/api/auth/oauth/config").catch(() => ({ providers: {} })),
      ]);
      dashboard = dashboardResult;
      playerMarket = marketResult || {};
      connectedAccounts = list(connectedResult.accounts);
      oauthProviders = oauthResult.providers || {};
      hydrateRoster();
      // A FileList cannot be restored after replacing a file input. Keep the
      // current profile form mounted while media is waiting to be submitted.
      const transientUiOpen = document.querySelector(
        ".competition-rules-modal:not([hidden]), .player-media-library-modal:not([hidden]), .team-modal:not(.hidden), .match-room-form textarea:focus"
      );
      if (!playerOverviewEditing && !playerMediaLibraryOpen && !transientUiOpen) render();
      connectEvents();
    } catch (error) {
      const status = Number((error as Error & { status?: number })?.status || 0);
      if (status === 401 || status === 403) {
        if (!quiet) showAuth();
      } else if (!quiet && retry) {
        window.setTimeout(() => loadMe({ quiet: false, retry: false }), 500);
      }
    } finally {
      dashboardRefreshInFlight = false;
    }
  }

  function startLiveDashboardRefresh() {
    if ((window as any).__teamPortalLiveRefresh) return;
    (window as any).__teamPortalLiveRefresh = window.setInterval(() => {
      if (document.visibilityState === "visible" && !resetToken && !playerActivateToken && !playerMediaLibraryOpen) {
        loadMe({ quiet: true });
      }
    }, 10000);
  }

  function connectEvents() {
    if (!window.EventSource || (window as any).__teamPortalEvents) return;
    try {
      const events = new EventSource("/api/public/events");
      (window as any).__teamPortalEvents = events;
      ["applications-updated", "league-updated", "match-updated", "match-message"].forEach((eventName) => {
        events.addEventListener(eventName, () => loadMe());
      });
    } catch {}
  }

  let playerMediaLibraryKind: "logo" | "banner" = "logo";
  let playerMediaLibraryOpen = false;
  async function loadPlayerMediaLibrary() {
    const grid = $("playerMediaLibraryGrid");
    const status = $("playerMediaLibraryStatus");
    if (grid) grid.innerHTML = `<p class="team-empty">Loading approved images...</p>`;
    document.querySelectorAll<HTMLElement>("[data-player-media-kind]").forEach((tab) => tab.classList.toggle("active", tab.dataset.playerMediaKind === playerMediaLibraryKind));
    try {
      const result = await api(`/api/team-portal/player-profile/media-library?kind=${encodeURIComponent(playerMediaLibraryKind)}`);
      const items = list(result.items);
      if (status) status.textContent = items.length ? `${items.length} approved ${playerMediaLibraryKind === "banner" ? "banner" : "profile image"}${items.length === 1 ? "" : "s"}` : "No approved images yet";
      if (grid) grid.innerHTML = items.length ? items.map((item) => `
        <article class="player-media-library-card">
          <img src="${esc(assetPath(item.path))}" alt="${esc(item.name || "Approved image")}">
          <strong>${esc(item.name || "Approved image")}</strong>
          <div class="player-media-library-actions">
            <button class="brk-btn primary" type="button" data-use-player-media="${esc(item.path)}">Use this image</button>
            ${item.canDelete ? `<button class="brk-btn danger" type="button" data-delete-player-media="${esc(item.id)}" data-player-media-name="${esc(item.name || "this image")}">Delete</button>` : ""}
          </div>
        </article>`).join("") : `<p class="team-empty">Upload a new ${playerMediaLibraryKind === "banner" ? "banner" : "profile image"}. Once approved, it will be reusable here.</p>`;
    } catch (error) {
      if (grid) grid.innerHTML = `<p class="team-empty">${esc((error as Error).message || "Could not load image library.")}</p>`;
    }
  }

  function openPlayerMediaLibrary(kind = "logo") {
    playerMediaLibraryOpen = true;
    playerMediaLibraryKind = kind === "banner" ? "banner" : "logo";
    const modal = $("playerMediaLibraryModal");
    if (!modal) return;
    modal.hidden = false;
    loadPlayerMediaLibrary();
  }

  function closePlayerMediaLibrary() {
    playerMediaLibraryOpen = false;
    const modal = $("playerMediaLibraryModal");
    if (!modal) return;
    modal.hidden = true;
  }

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && playerMediaLibraryOpen) closePlayerMediaLibrary();
  });

  document.addEventListener("pointerdown", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.matches("#playerMediaLibraryModal")) closePlayerMediaLibrary();
  }, true);

  document.addEventListener("click", async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const scrapePlayerTracker = target.closest<HTMLButtonElement>("[data-scrape-player-tracker]");
    if (scrapePlayerTracker) {
      const form = scrapePlayerTracker.closest<HTMLFormElement>("#playerOverviewEditForm");
      const trackerInput = form?.elements.namedItem("player_tracker_link") as HTMLInputElement | null;
      if (!form || !trackerInput) return;
      scrapePlayerTracker.disabled = true;
      scrapePlayerTracker.textContent = "Getting Rankings...";
      setMessage("playerOverviewEditMessage", "Requesting current Rocket League rankings...");
      try {
        const result = await api("/api/team-portal/player-profile/refresh-tracker", {
          method: "POST",
          body: JSON.stringify({ player_tracker_link: trackerInput.value }),
        });
        dashboard = result.dashboard || dashboard;
        const ratings = result.ratings || {};
        (form.elements.namedItem("rocket_league_mmr_1v1") as HTMLInputElement | null)!.value = clean(ratings.rocket_league_mmr_1v1);
        (form.elements.namedItem("rocket_league_mmr_2v2") as HTMLInputElement | null)!.value = clean(ratings.rocket_league_mmr_2v2);
        (form.elements.namedItem("rocket_league_mmr_3v3") as HTMLInputElement | null)!.value = clean(ratings.rocket_league_mmr_3v3);
        setMessage("playerOverviewEditMessage", result.message || "Rankings imported.", "success");
      } catch (error) {
        setMessage("playerOverviewEditMessage", (error as Error).message || "Tracker rankings could not be imported.", "error");
      } finally {
        scrapePlayerTracker.disabled = false;
        scrapePlayerTracker.textContent = "Get Rankings";
      }
      return;
    }

    if (target.closest("[data-manage-profile-connections]")) {
      playerOverviewEditing = false;
      playerOverviewEditDraft = null;
      renderProfile();
      activateProfileTab("accounts");
      return;
    }

    const openMediaLibrary = target.closest<HTMLElement>("[data-open-player-media-library]");
    if (openMediaLibrary) {
      openPlayerMediaLibrary(openMediaLibrary.dataset.openPlayerMediaLibrary || "logo");
      return;
    }
    if (target.closest("[data-close-player-media-library]")) {
      closePlayerMediaLibrary();
      return;
    }
    const mediaKindTab = target.closest<HTMLElement>("[data-player-media-kind]");
    if (mediaKindTab) {
      playerMediaLibraryKind = mediaKindTab.dataset.playerMediaKind === "banner" ? "banner" : "logo";
      await loadPlayerMediaLibrary();
      return;
    }
    const usePlayerMedia = target.closest<HTMLElement>("[data-use-player-media]");
    if (usePlayerMedia) {
      const result = await api("/api/team-portal/player-profile/media-library/select", {
        method: "POST",
        body: JSON.stringify({ kind: playerMediaLibraryKind, path: usePlayerMedia.dataset.usePlayerMedia || "" }),
      });
      dashboard = result.dashboard || dashboard;
      closePlayerMediaLibrary();
      renderProfile();
      return;
    }
    const deletePlayerMedia = target.closest<HTMLElement>("[data-delete-player-media]");
    if (deletePlayerMedia) {
      const id = clean(deletePlayerMedia.dataset.deletePlayerMedia);
      const name = clean(deletePlayerMedia.dataset.playerMediaName) || "this image";
      if (!id || !window.confirm(`Delete ${name} permanently? This will free a media-library slot.`)) return;
      deletePlayerMedia.setAttribute("disabled", "true");
      try {
        const result = await api(`/api/team-portal/player-profile/media-library/${encodeURIComponent(id)}`, { method: "DELETE" });
        dashboard = result.dashboard || dashboard;
        await loadPlayerMediaLibrary();
      } catch (error) {
        deletePlayerMedia.removeAttribute("disabled");
        window.alert((error as Error).message || "Could not delete that image.");
      }
      return;
    }
    if (target.closest("[data-upload-player-media]")) {
      const input = $<HTMLInputElement>("playerMediaLibraryFile");
      const file = input?.files?.[0];
      const status = $("playerMediaLibraryStatus");
      if (!file) { if (status) status.textContent = "Choose an image first."; return; }
      const data = new FormData();
      data.append(playerMediaLibraryKind === "banner" ? "banner" : "photo", file);
      if (status) status.textContent = "Uploading securely for moderation...";
      const result = await api("/api/team-portal/player-profile/media", { method: "POST", body: data });
      dashboard = result.dashboard || dashboard;
      if (status) status.textContent = result.message || "Sent for moderation.";
      if (input) input.value = "";
      return;
    }

    const disconnectProvider = target.closest<HTMLElement>("[data-disconnect-provider]");
    if (disconnectProvider) {
      const provider = clean(disconnectProvider.dataset.disconnectProvider);
      if (!provider || !window.confirm(`Disconnect ${provider} from this Bracketr account?`)) return;
      try {
        await api(`/api/auth/connected-accounts/${encodeURIComponent(provider)}`, { method: "DELETE" });
        connectedAccounts = connectedAccounts.filter((item) => clean(item.provider) !== provider);
        renderProfile();
      } catch (error: any) {
        window.alert(error.message || "Could not disconnect that account.");
      }
      return;
    }

    const notificationBell = target.closest<HTMLElement>("#teamNotificationBell");
    if (notificationBell) {
      notificationsOpen = !notificationsOpen;
      renderNotifications();
      return;
    }
    if (target.closest("[data-notification-open]")) {
      notificationsOpen = true;
      renderNotifications();
      return;
    }
    if (!target.closest("#teamNotificationMenu") && notificationsOpen) {
      notificationsOpen = false;
      renderNotifications();
    }

    const readinessEdit = target.closest<HTMLElement>("[data-readiness-edit-profile]");
    if (readinessEdit) {
      activeSection = "profile";
      playerOverviewEditing = true;
      playerOverviewEditDraft = null;
      render();
      return;
    }

    const viewButton = target.closest<HTMLElement>("[data-view-mode]");
    if (viewButton) {
      activeViewMode = viewButton.dataset.viewMode === "player" ? "player" : "manager";
      localStorage.setItem("teamPortalViewMode", activeViewMode);
      if (activeViewMode === "player" && activeSection === "settings") activeSection = "home";
      render();
      return;
    }

    const sectionButton = target.closest<HTMLElement>("[data-section], [data-section-jump]");
    if (sectionButton) {
      setSection(sectionButton.dataset.section || sectionButton.dataset.sectionJump || "home");
      render();
      window.scrollTo({ top: 0, behavior: "instant" });
      document.querySelector<HTMLElement>(".player-portal-main")?.scrollTo({ top: 0, behavior: "instant" });
      return;
    }

    const themeButton = target.closest<HTMLElement>("[data-profile-theme]");
    if (themeButton) {
      applyProfileTheme(themeButton.dataset.profileTheme || "site");
      return;
    }

    const visibilityButton = target.closest<HTMLButtonElement>("[data-profile-visibility]");
    if (visibilityButton) {
      const visibility = visibilityButton.dataset.profileVisibility === "private" ? "private" : "public";
      const message = document.querySelector<HTMLElement>("[data-profile-privacy-message]");
      visibilityButton.disabled = true;
      if (message) {
        message.textContent = `Making your profile ${visibility}...`;
        message.dataset.tone = "";
      }
      try {
        const result = await api("/api/team-portal/player-profile/visibility", {
          method: "POST",
          body: JSON.stringify({ profile_visibility: visibility }),
        });
        dashboard = result.dashboard || dashboard;
        if (dashboard.account) dashboard.account.profile_visibility = visibility;
        renderProfile();
        const refreshedMessage = document.querySelector<HTMLElement>("[data-profile-privacy-message]");
        if (refreshedMessage) refreshedMessage.textContent = result.message || "Profile privacy updated.";
      } catch (error) {
        visibilityButton.disabled = false;
        if (message) {
          message.textContent = (error as Error).message || "Could not update profile privacy.";
          message.dataset.tone = "error";
        }
      }
      return;
    }

    if (target.closest("[data-reload-captain-quarters]")) {
      loadMe();
      return;
    }

    if (target.closest("[data-refresh-player-overview]")) {
      hydrateRoster();
      setSection("home");
      return;
    }

    if (target.closest("[data-toggle-player-edit]")) {
      playerOverviewEditing = true;
      playerOverviewEditDraft = null;
      if (activeSection === "profile") renderProfile();
      else renderPlayerPortalOverview();
      return;
    }
    if (target.closest("[data-close-player-edit]")) {
      playerOverviewEditing = false;
      playerOverviewEditDraft = null;
      if (activeSection === "profile") renderProfile();
      else renderPlayerPortalOverview();
      return;
    }

    const modalButton = target.closest<HTMLElement>("[data-open-modal]");
    if (modalButton) {
      openModal(modalButton.dataset.openModal || "");
      return;
    }
    if (target.closest("[data-close-modal]")) {
      closeModals();
      return;
    }

    const captainTeam = target.closest<HTMLElement>("[data-captain-team-key]");
    if (captainTeam) {
      const account = dashboard.account || {};
      selectedCaptainTeamKey = captainTeam.dataset.captainTeamKey || "";
      const selectedTeam = playerMemberships().find((team) => captainTeamKey(team) === selectedCaptainTeamKey);
      if (selectedTeam?.team_account_id) account.selected_team_account_id = selectedTeam.team_account_id;
      dashboard.account = account;
      selectedPlayerId = "";
      selectedCaptainCompetitionId = "";
      captainRosterRequestOpen = false;
      captainCenterTab = "players";
      renderSettings();
      return;
    }

    const captainTab = target.closest<HTMLElement>("[data-captain-center-tab]");
    if (captainTab) {
      const nextTab = captainTab.dataset.captainCenterTab || "players";
      captainCenterTab = ["competitions", "requests", "settings"].includes(nextTab) ? nextTab as typeof captainCenterTab : "players";
      if (captainCenterTab === "players") selectedCaptainCompetitionId = "";
      else selectedPlayerId = "";
      captainRosterRequestOpen = false;
      renderSettings();
      return;
    }

    const captainPlayer = target.closest<HTMLElement>("[data-captain-player]");
    if (captainPlayer) {
      selectedPlayerId = captainPlayer.dataset.captainPlayer || selectedPlayerId;
      selectedCaptainCompetitionId = "";
      captainRosterRequestOpen = false;
      captainCenterTab = "players";
      renderSettings();
      return;
    }

    const captainComp = target.closest<HTMLElement>("[data-captain-comp]");
    if (captainComp && !target.closest("[data-request-leave-comp]") && !target.closest("[data-open-captain-roster-request]")) {
      selectedCaptainCompetitionId = captainComp.dataset.captainComp || selectedCaptainCompetitionId;
      selectedPlayerId = "";
      captainRosterRequestOpen = false;
      captainCenterTab = "competitions";
      renderSettings();
      return;
    }

    const openCaptainRosterRequest = target.closest<HTMLElement>("[data-open-captain-roster-request]");
    if (openCaptainRosterRequest) {
      selectedCaptainCompetitionId = openCaptainRosterRequest.dataset.openCaptainRosterRequest || selectedCaptainCompetitionId;
      selectedPlayerId = "";
      captainCenterTab = "competitions";
      captainRosterRequestMode = "add";
      captainRosterRequestMessage = "";
      captainRosterRequestOpen = true;
      renderSettings();
      return;
    }

    if (target.closest("[data-close-captain-roster-request]")) {
      captainRosterRequestOpen = false;
      captainRosterRequestMessage = "";
      renderSettings();
      return;
    }

    const playerTeamJump = target.closest<HTMLElement>("[data-player-team-jump]");
    if (playerTeamJump) {
      const account = dashboard.account || {};
      account.selected_team_account_id = playerTeamJump.dataset.playerTeamJump || "";
      dashboard.account = account;
      selectedPlayerId = "";
      selectedCaptainCompetitionId = "";
      captainRosterRequestOpen = false;
      captainCenterTab = "players";
      setSection("settings");
      renderSettings();
      return;
    }

    const acceptInvite = target.closest<HTMLButtonElement>("[data-roster-invite-accept]");
    if (acceptInvite) {
      acceptInvite.disabled = true;
      try {
        const result = await api(`/api/team-portal/roster-invites/${encodeURIComponent(acceptInvite.dataset.rosterInviteAccept || "")}/accept`, {
          method: "POST",
          body: "{}",
        });
        dashboard = result.dashboard || dashboard;
        hydrateRoster();
        render();
      } catch (error) {
        alert((error as Error).message || "Could not accept invite.");
        acceptInvite.disabled = false;
      }
      return;
    }

    const denyInvite = target.closest<HTMLButtonElement>("[data-roster-invite-deny], [data-roster-invite-block]");
    if (denyInvite) {
      const inviteId = denyInvite.dataset.rosterInviteDeny || denyInvite.dataset.rosterInviteBlock || "";
      const block = Boolean(denyInvite.dataset.rosterInviteBlock);
      const message = prompt(block ? "Optional message to send before blocking future requests:" : "Optional denial message:") || "";
      if (block && !confirm("Block future roster requests from this captain/team?")) return;
      denyInvite.disabled = true;
      try {
        const result = await api(`/api/team-portal/roster-invites/${encodeURIComponent(inviteId)}/deny`, {
          method: "POST",
          body: JSON.stringify({ message, block }),
        });
        dashboard = result.dashboard || dashboard;
        hydrateRoster();
        render();
      } catch (error) {
        alert((error as Error).message || "Could not update invite.");
        denyInvite.disabled = false;
      }
      return;
    }

    const leaveComp = target.closest<HTMLButtonElement>("[data-request-leave-comp]");
    if (leaveComp) {
      const team = selectedCaptainTeam();
      const teamId = Number(team?.team_account_id || dashboard.account?.selected_team_account_id || 0);
      const leagueId = leaveComp.dataset.requestLeaveComp || "";
      const app = teamApplications(teamId).find((item) => String(item.league_id || "") === String(leagueId));
      if (!teamId || !leagueId) return;
      if (!confirm(`Request admin removal from ${first(app?.league_name, "this competition")}?`)) return;
      leaveComp.disabled = true;
      try {
        const result = await api(`/api/team-portal/captain-teams/${encodeURIComponent(String(teamId))}/leave-requests`, {
          method: "POST",
          body: JSON.stringify({ league_id: leagueId }),
        });
        dashboard = result.dashboard || dashboard;
        hydrateRoster();
        render();
      } catch (error) {
        alert((error as Error).message || "Could not submit leave request.");
        leaveComp.disabled = false;
      }
      return;
    }

    const deleteCaptainTeam = target.closest<HTMLButtonElement>("[data-delete-captain-team]");
    if (deleteCaptainTeam) {
      const teamId = Number(deleteCaptainTeam.dataset.deleteCaptainTeam || 0);
      const team = selectedCaptainTeam();
      if (!teamId) return;
      if (!confirm(`Delete ${first(team?.team_name, "this team")}? This cannot be undone.`)) return;
      deleteCaptainTeam.disabled = true;
      try {
        const result = await api("/api/team-portal/captain-teams/current", {
          method: "DELETE",
          body: JSON.stringify({ team_account_id: teamId }),
        });
        dashboard = result.dashboard || dashboard;
        selectedPlayerId = "";
        hydrateRoster();
        render();
      } catch (error) {
        alert((error as Error).message || "Could not delete team.");
        deleteCaptainTeam.disabled = false;
      }
      return;
    }

    const refreshPlayerTracker = target.closest<HTMLButtonElement>("[data-refresh-player-tracker]");
    if (refreshPlayerTracker) {
      const playerId = Number(refreshPlayerTracker.dataset.refreshPlayerTracker || 0);
      const team = selectedCaptainTeam();
      const teamId = Number(team?.team_account_id || dashboard.account?.selected_team_account_id || 0);
      if (!playerId || !teamId) return;
      refreshPlayerTracker.disabled = true;
      refreshPlayerTracker.textContent = "Checking Tracker...";
      try {
        const result = await api(`/api/team-portal/captain-teams/current/players/${encodeURIComponent(String(playerId))}/refresh-tracker`, {
          method: "POST",
          body: JSON.stringify({ team_account_id: teamId }),
        });
        dashboard = result.dashboard || dashboard;
        hydrateRoster();
        render();
        alert(result.message || "Rocket League MMR refreshed.");
      } catch (error) {
        alert((error as Error).message || "Tracker MMR could not be refreshed.");
        refreshPlayerTracker.disabled = false;
        refreshPlayerTracker.textContent = "Refresh MMR";
      }
      return;
    }

    const removeRosterPlayer = target.closest<HTMLButtonElement>("[data-remove-roster-player]");
    if (removeRosterPlayer) {
      const playerId = Number(removeRosterPlayer.dataset.removeRosterPlayer || 0);
      const team = selectedCaptainTeam();
      const teamId = Number(team?.team_account_id || dashboard.account?.selected_team_account_id || 0);
      const player = rosterDraft.find((item) => Number(item.id || 0) === playerId) || null;
      if (!playerId || !teamId) return;
      if (!confirm(`Remove ${first(player?.in_game_name, player?.player_name, "this player")} from ${first(team?.team_name, "this team")}?`)) return;
      removeRosterPlayer.disabled = true;
      try {
        const result = await api(`/api/team-portal/captain-teams/current/players/${encodeURIComponent(String(playerId))}`, {
          method: "DELETE",
          body: JSON.stringify({ team_account_id: teamId }),
        });
        dashboard = result.dashboard || dashboard;
        selectedPlayerId = "";
        hydrateRoster();
        render();
      } catch (error) {
        alert((error as Error).message || "Could not remove player.");
        removeRosterPlayer.disabled = false;
      }
      return;
    }

    const openCompetition = target.closest<HTMLElement>("[data-open-competition]");
    if (openCompetition) {
      activeCompetitionId = openCompetition.dataset.openCompetition || "";
      activeCompetitionTab = openCompetition.dataset.openCompetitionTab || "overview";
      renderCompetitionHub();
      setSection("competition-hub");
      return;
    }

    const competitionTab = target.closest<HTMLElement>("[data-competition-tab]");
    if (competitionTab) {
      activeCompetitionTab = competitionTab.dataset.competitionTab || "overview";
      renderCompetitionHub();
      return;
    }

    const leaveLadder = target.closest<HTMLElement>("[data-leave-ladder]");
    if (leaveLadder) {
      const appId = leaveLadder.dataset.leaveLadder || "";
      if (!confirm("Leave this ladder? Your team will stay visible in the inactive archive at the bottom of the ladder.")) return;
      leaveLadder.setAttribute("disabled", "true");
      try {
        const result = await api(`/api/team-portal/applications/${encodeURIComponent(appId)}/leave-ladder`, { method: "POST" });
        alert(result.message || "Your team has left the ladder.");
        await loadMe();
      } catch (error) {
        alert((error as Error).message);
        leaveLadder.removeAttribute("disabled");
      }
      return;
    }

    const rejoinLadder = target.closest<HTMLButtonElement>("[data-rejoin-ladder]");
    if (rejoinLadder) {
      const appId = rejoinLadder.dataset.rejoinLadder || "";
      if (!confirm("Rejoin this ladder at the bottom of the active standings?")) return;
      rejoinLadder.disabled = true;
      try {
        const result = await api(`/api/team-portal/applications/${encodeURIComponent(appId)}/rejoin-ladder`, { method: "POST" });
        alert(result.message || "Your team has rejoined the ladder.");
        await loadMe();
      } catch (error) {
        alert((error as Error).message);
        rejoinLadder.disabled = false;
      }
      return;
    }

    const ladderAction = target.closest<HTMLButtonElement>("[data-ladder-accept], [data-ladder-decline], [data-ladder-cancel]");
    if (ladderAction) {
      const app = selectedCompetition();
      const challengeId = ladderAction.dataset.ladderAccept || ladderAction.dataset.ladderDecline || ladderAction.dataset.ladderCancel || "";
      const action = ladderAction.dataset.ladderAccept ? "accept" : ladderAction.dataset.ladderDecline ? "decline" : "cancel";
      if (!app?.league_id || !challengeId) return;
      if (action !== "accept" && !confirm(`${action === "decline" ? "Decline" : "Cancel"} this challenge offer?`)) return;
      ladderAction.disabled = true;
      try {
        await api(`/api/leagues/${encodeURIComponent(app.league_id)}/ladder/challenges/${encodeURIComponent(challengeId)}/${action}`, {
          method: "POST",
          body: JSON.stringify(action === "accept" ? { accepted_slot_index: Number(ladderAction.dataset.slotIndex || 0) } : {}),
        });
        await loadMe();
      } catch (error) {
        alert((error as Error).message);
        ladderAction.disabled = false;
      }
      return;
    }

    const broadcastRequest = target.closest<HTMLElement>("[data-broadcast-request]");
    if (broadcastRequest) {
      const kind = broadcastRequest.dataset.requestKind || "broadcast";
      const result = await api(`/api/team-portal/matches/${encodeURIComponent(broadcastRequest.dataset.broadcastRequest || "")}/broadcast-requests`, {
        method: "POST",
        body: JSON.stringify({ request_kind: kind }),
      });
      dashboard = result.dashboard || dashboard;
      hydrateRoster();
      render();
      return;
    }

    const row = target.closest<HTMLElement>("[data-player-id], [data-view-player]");
    if (row) {
      selectedPlayerId = row.dataset.playerId || row.dataset.viewPlayer || selectedPlayerId;
      renderRoster();
    }

    const edit = target.closest<HTMLElement>("[data-edit-player], [data-open-player-drawer]");
    if (edit) openPlayerDrawer(edit.dataset.editPlayer || "");

    const addGame = target.closest<HTMLElement>("[data-add-game]");
    if (addGame) {
      const key = addGame.dataset.addGame || "";
      const player = rosterDraft.find((item, index) => playerKey(item, index) === key);
      const select = document.querySelector<HTMLSelectElement>(`[data-game-select="${CSS.escape(key)}"]`);
      const game = clean(select?.value);
      if (player && game) {
        setPlayerGames(player, [...list(player.games).map(clean), game]);
        selectedPlayerId = key;
        renderRoster();
      }
      return;
    }

    const removeGame = target.closest<HTMLElement>("[data-remove-game]");
    if (removeGame) {
      const key = removeGame.dataset.gamePlayer || "";
      const player = rosterDraft.find((item, index) => playerKey(item, index) === key);
      const game = clean(removeGame.dataset.removeGame).toLowerCase();
      if (player && game) {
        setPlayerGames(player, list(player.games).map(clean).filter((item) => item.toLowerCase() !== game));
        selectedPlayerId = key;
        renderRoster();
      }
      return;
    }

    if (target.closest("[data-close-player-drawer]")) closePlayerDrawer();

    if (target.closest("[data-disband-team]")) {
      setSection("requests");
      const type = $<HTMLSelectElement>("requestType");
      const reason = $<HTMLTextAreaElement>("requestReason");
      if (type) type.value = "disband";
      if (reason) reason.value = "Please review this team disband request.";
    }

    const matchFilter = target.closest<HTMLElement>("[data-match-filter]");
    if (matchFilter) {
      activeMatchFilter = matchFilter.dataset.matchFilter || "all";
      document.querySelectorAll<HTMLElement>("[data-match-filter]").forEach((button) => button.classList.toggle("is-active", button === matchFilter));
      renderMatches();
    }

    const matchWorkspaceFilter = target.closest<HTMLElement>("[data-match-workspace-filter]");
    if (matchWorkspaceFilter) {
      activeMatchFilter = matchWorkspaceFilter.dataset.matchWorkspaceFilter || "all";
      document.querySelectorAll<HTMLElement>("[data-match-filter]").forEach((button) => button.classList.toggle("is-active", button.dataset.matchFilter === activeMatchFilter));
      selectedMatchId = "";
      renderMatches();
      return;
    }

    if (target.closest("[data-reset-match-filters]")) {
      matchCompetitionSearch = "";
      matchCompetitionGame = "all";
      matchCompetitionStatus = "all";
      matchCompetitionSort = "next";
      renderMatches();
      return;
    }

    const matchCompetitionSelect = target.closest<HTMLElement>("[data-match-competition-select]");
    if (matchCompetitionSelect) {
      selectedMatchCompetitionId = matchCompetitionSelect.dataset.matchCompetitionSelect || "";
      selectedMatchId = "";
      matchCompetitionColumnCollapsed = false;
      activeMatchFilter = "all";
      document.querySelectorAll<HTMLElement>("[data-match-filter]").forEach((button) => button.classList.toggle("is-active", button.dataset.matchFilter === activeMatchFilter));
      renderMatches();
      return;
    }

    const matchWorkspaceSelect = target.closest<HTMLElement>("[data-match-workspace-select]");
    if (matchWorkspaceSelect) {
      selectedMatchId = matchWorkspaceSelect.dataset.matchWorkspaceSelect || selectedMatchId;
      activeMatchDetailTab = "details";
      matchCompetitionColumnCollapsed = true;
      renderMatches();
      return;
    }

    const matchCompetitionCollapse = target.closest<HTMLElement>("[data-match-competition-collapse]");
    if (matchCompetitionCollapse) {
      matchCompetitionColumnCollapsed = !matchCompetitionColumnCollapsed;
      renderMatches();
      return;
    }

    const matchDetailTab = target.closest<HTMLElement>("[data-match-detail-tab]");
    if (matchDetailTab) {
      const tab = matchDetailTab.dataset.matchDetailTab;
      if (tab === "details" || tab === "lobby" || tab === "chat") activeMatchDetailTab = tab;
      renderMatches();
      return;
    }

    const matchLadderTeam = target.closest<HTMLElement>("[data-match-ladder-team]");
    if (matchLadderTeam) {
      selectedMatchLadderTeamId = matchLadderTeam.dataset.matchLadderTeam || "";
      renderMatches();
      return;
    }

    const competitionCheckin = target.closest<HTMLElement>("[data-competition-checkin]");
    if (competitionCheckin) {
      const result = await api(`/api/team-portal/applications/${encodeURIComponent(competitionCheckin.dataset.competitionCheckin || "")}/check-in`, { method: "POST", body: "{}" });
      dashboard = result.dashboard || dashboard;
      hydrateRoster();
      render();
    }

    const matchCheckin = target.closest<HTMLElement>("[data-match-checkin]");
    if (matchCheckin) {
      const result = await api(`/api/team-portal/matches/${encodeURIComponent(matchCheckin.dataset.matchCheckin || "")}/check-in`, { method: "POST", body: "{}" });
      dashboard = result.dashboard || dashboard;
      render();
    }
  });

  document.addEventListener("change", (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (!target) return;
    const rosterRequestType = target.closest<HTMLSelectElement>("#captainRosterRequestType");
    if (rosterRequestType) {
      captainRosterRequestMode = ["remove", "replace"].includes(rosterRequestType.value) ? rosterRequestType.value as typeof captainRosterRequestMode : "add";
      captainRosterRequestMessage = "";
      renderSettings();
    }
  });

  document.addEventListener("input", (event) => {
    const target = event.target instanceof HTMLTextAreaElement ? event.target : null;
    const form = target?.closest<HTMLFormElement>("[data-match-message]");
    if (!target || !form?.dataset.matchMessage) return;
    matchChatDrafts.set(form.dataset.matchMessage, target.value);
  });

  [$("teamRosterGlobalSearch"), $("teamRosterSearchInput"), $("teamRosterGameFilter"), $("teamRosterAccessFilter"), $("teamRosterStatusFilter")]
    .forEach((el) => el?.addEventListener("input", renderRoster));

  const updateDiscoveryUrl = () => {
    const url = new URL(window.location.href);
    const values = { compGame: $<HTMLSelectElement>("teamTournamentGameFilter")?.value || "ALL", compFormat: $<HTMLSelectElement>("teamTournamentTypeFilter")?.value || "ALL", compRegion: $<HTMLSelectElement>("teamTournamentRegionFilter")?.value || "ALL", compStatus: $<HTMLSelectElement>("teamTournamentStatusFilter")?.value || "ALL", compTab: discoveryTab, compSort: discoverySort, compPage: String(discoveryPage) };
    Object.entries(values).forEach(([key, value]) => value === "ALL" || value === "all" || value === "recent" || value === "1" ? url.searchParams.delete(key) : url.searchParams.set(key, value));
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  };
  const refreshDiscovery = () => { discoveryPage = 1; renderOpenCompetitions(); updateDiscoveryUrl(); };
  $("teamTournamentSearchInput")?.addEventListener("input", () => { window.clearTimeout(discoverySearchTimer); discoverySearchTimer = window.setTimeout(refreshDiscovery, 250); });
  [$("teamTournamentGameFilter"), $("teamTournamentTypeFilter"), $("teamTournamentRegionFilter"), $("teamTournamentStatusFilter")]
    .forEach((el) => el?.addEventListener("change", refreshDiscovery));

  const updateEntryRulesGate = (scroll: HTMLElement) => {
    const modal = scroll.closest<HTMLElement>("[data-entry-rules-modal]");
    if (!modal) return;
    const reachedBottom = scroll.scrollTop + scroll.clientHeight >= scroll.scrollHeight - 8;
    const accept = modal.querySelector<HTMLButtonElement>("[data-accept-entry-rules]");
    const status = modal.querySelector<HTMLElement>("[data-entry-rules-status]");
    if (accept) accept.disabled = !reachedBottom;
    if (status) status.textContent = reachedBottom ? "You’ve reached the end. Confirm to continue." : "Scroll to the bottom to enable confirmation.";
  };
  const updateEntryRulesSection = (scroll: HTMLElement) => {
    const modal = scroll.closest<HTMLElement>("[data-entry-rules-modal]");
    if (!modal) return;
    const sections = Array.from(scroll.querySelectorAll<HTMLElement>(".competition-rule-section"));
    const documentTop = scroll.getBoundingClientRect().top;
    const readingLine = scroll.scrollTop + 18;
    let activeId = scroll.id;
    sections.forEach((section) => {
      const sectionTop = section.getBoundingClientRect().top - documentTop + scroll.scrollTop;
      if (sectionTop <= readingLine) activeId = section.id;
    });
    const active = modal.querySelector<HTMLElement>(`[data-entry-rule-target="${CSS.escape(activeId)}"]`);
    modal.querySelectorAll("[data-entry-rule-target]").forEach((card) => card.classList.toggle("is-active", card === active));
    active?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  $("openCompetitionsList")?.addEventListener("click", (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    const entryTab = target?.closest<HTMLElement>("[data-entry-tab][data-entry-league]");
    const detailsToggle = target?.closest<HTMLElement>("[data-toggle-entry-details]");
    const openRules = target?.closest<HTMLElement>("[data-open-entry-rules]");
    const ruleSection = target?.closest<HTMLElement>("[data-entry-rule-target]");
    const closeRules = target?.closest<HTMLElement>("[data-close-entry-rules]");
    const acceptRules = target?.closest<HTMLElement>("[data-accept-entry-rules]");
    const tab = target?.closest<HTMLElement>("[data-discovery-tab]");
    const page = target?.closest<HTMLElement>("[data-discovery-page]");
    if (ruleSection) {
      const modal = ruleSection.closest<HTMLElement>("[data-entry-rules-modal]");
      const scroll = modal?.querySelector<HTMLElement>("[data-entry-rules-scroll]");
      const targetId = ruleSection.dataset.entryRuleTarget || "";
      const section = targetId ? document.getElementById(targetId) : null;
      if (scroll && section) {
        modal?.querySelectorAll("[data-entry-rule-target]").forEach((card) => card.classList.toggle("is-active", card === ruleSection));
        if (section === scroll) scroll.scrollTo({ top: 0, behavior: "smooth" });
        else section.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }
    if (openRules) {
      const leagueKey = openRules.dataset.openEntryRules || "";
      const card = openRules.closest<HTMLElement>("[data-recommended-competition]");
      const modal = card?.querySelector<HTMLElement>(`[data-entry-rules-modal="${leagueKey}"]`);
      if (modal) {
        modal.hidden = false;
        document.body.classList.add("competition-rules-open");
        const scroll = modal.querySelector<HTMLElement>("[data-entry-rules-scroll]");
        window.setTimeout(() => {
          if (scroll) {
            scroll.focus();
            updateEntryRulesGate(scroll);
          }
        }, 0);
      }
      return;
    }
    if (closeRules) {
      const modal = closeRules.closest<HTMLElement>("[data-entry-rules-modal]");
      if (modal) modal.hidden = true;
      document.body.classList.remove("competition-rules-open");
      return;
    }
    if (acceptRules && !acceptRules.hasAttribute("disabled")) {
      const leagueKey = acceptRules.dataset.acceptEntryRules || "";
      discoveryRulesAccepted.add(leagueKey);
      document.body.classList.remove("competition-rules-open");
      renderOpenCompetitions();
      return;
    }
    if (entryTab) {
      discoveryEntryTabs[entryTab.dataset.entryLeague || ""] = entryTab.dataset.entryTab || "enter";
      renderOpenCompetitions();
      return;
    }
    if (detailsToggle) {
      const leagueKey = detailsToggle.dataset.toggleEntryDetails || "";
      discoveryEntryExpanded[leagueKey] = !discoveryEntryExpanded[leagueKey];
      renderOpenCompetitions();
      return;
    }
    if (tab) { discoveryTab = tab.dataset.discoveryTab || "all"; discoveryPage = 1; renderOpenCompetitions(); updateDiscoveryUrl(); }
    if (page) { discoveryPage = Number(page.dataset.discoveryPage || 1); renderOpenCompetitions(); updateDiscoveryUrl(); }
    if (target?.closest("[data-reset-discovery]")) $("clearTournamentFilters")?.click();
  });
  $("openCompetitionsList")?.addEventListener("change", (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    const teamSelect = target?.closest<HTMLSelectElement>("[data-entry-team]");
    const playerCheck = target?.closest<HTMLInputElement>("[data-entry-player]");
    if (teamSelect) {
      const leagueKey = teamSelect.dataset.entryTeam || "";
      discoveryEntryTeams[leagueKey] = teamSelect.value;
      discoveryEntryPlayers.delete(`${leagueKey}:${teamSelect.value}`);
      renderOpenCompetitions();
      return;
    }
    if (playerCheck) {
      const leagueKey = playerCheck.dataset.entryPlayer || "";
      const teamId = discoveryEntryTeams[leagueKey] || "";
      const selected = discoveryEntryPlayers.get(`${leagueKey}:${teamId}`) || new Set<string>();
      if (playerCheck.checked) selected.add(playerCheck.value);
      else selected.delete(playerCheck.value);
      discoveryEntryPlayers.set(`${leagueKey}:${teamId}`, selected);
      renderOpenCompetitions();
      return;
    }
    const select = target instanceof HTMLSelectElement ? target.closest<HTMLSelectElement>("#competitionDiscoverySort") : null;
    if (select) { discoverySort = select.value; discoveryPage = 1; renderOpenCompetitions(); updateDiscoveryUrl(); }
  });
  $("openCompetitionsList")?.addEventListener("submit", async (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target.closest<HTMLFormElement>("[data-competition-entry-form]") : null;
    if (!form) return;
    event.preventDefault();
    const leagueKey = form.dataset.competitionEntryForm || "";
    const teamId = discoveryEntryTeams[leagueKey] || "";
    const selected = [...(discoveryEntryPlayers.get(`${leagueKey}:${teamId}`) || new Set<string>())];
    const message = form.querySelector<HTMLElement>("[data-entry-message]");
    const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (message) message.textContent = "Submitting your team entry...";
    if (submit) submit.disabled = true;
    try {
      const result = await api("/api/team-portal/competition-applications", {
        method: "POST",
        body: JSON.stringify({
          league_id: leagueKey,
          team_account_id: teamId,
          player_ids: selected,
          rules_confirmed: discoveryRulesAccepted.has(leagueKey),
        }),
      });
      if (message) message.textContent = result.message || "Application submitted.";
      await loadMe({ quiet: true });
    } catch (error) {
      if (message) message.textContent = (error as Error).message;
      if (submit) submit.disabled = false;
    }
  });
  $("openCompetitionsList")?.addEventListener("scroll", (event) => {
    const scroll = event.target instanceof HTMLElement ? event.target.closest<HTMLElement>("[data-entry-rules-scroll]") : null;
    if (scroll) {
      updateEntryRulesGate(scroll);
      updateEntryRulesSection(scroll);
    }
  }, true);

  $("clearTournamentFilters")?.addEventListener("click", () => {
    const search = $<HTMLInputElement>("teamTournamentSearchInput");
    const game = $<HTMLSelectElement>("teamTournamentGameFilter");
    const type = $<HTMLSelectElement>("teamTournamentTypeFilter");
    const region = $<HTMLSelectElement>("teamTournamentRegionFilter");
    const status = $<HTMLSelectElement>("teamTournamentStatusFilter");
    if (search) search.value = "";
    if (game) game.value = "ALL";
    if (type) type.value = "ALL";
    if (region) region.value = "ALL";
    if (status) status.value = "ALL";
    discoveryTab = "all";
    discoveryPage = 1;
    renderOpenCompetitions();
    updateDiscoveryUrl();
  });

  $("requestSource")?.addEventListener("change", () => {
    renderMarketSelect();
    applySelectedMarketPlayer();
  });
  $("requestMarketPlayer")?.addEventListener("change", applySelectedMarketPlayer);

  $("clearRosterFilters")?.addEventListener("click", () => {
    [$<HTMLInputElement>("teamRosterGlobalSearch"), $<HTMLInputElement>("teamRosterSearchInput")].forEach((el) => { if (el) el.value = ""; });
    [$<HTMLSelectElement>("teamRosterGameFilter"), $<HTMLSelectElement>("teamRosterAccessFilter"), $<HTMLSelectElement>("teamRosterStatusFilter")].forEach((el) => { if (el) el.value = "ALL"; });
    renderRoster();
  });

  $("teamPlayerForm")?.addEventListener("submit", (event) => {
    event.preventDefault();
    try {
      savePlayerFromDrawer();
    } catch (error) {
      setMessage("teamPlayerMessage", (error as Error).message, "error");
    }
  });

  $("deleteTeamPlayerBtn")?.addEventListener("click", () => {
    const index = Number($<HTMLInputElement>("teamPlayerIndex")?.value || -1);
    if (index >= 0) {
      rosterDraft.splice(index, 1);
      selectedPlayerId = playerKey(rosterDraft[0] || {}, 0);
      closePlayerDrawer();
      render();
    }
  });

  $("saveRosterBtn")?.addEventListener("click", () => {
    saveProfile("rosterMessage").catch((error) => setMessage("rosterMessage", error.message, "error"));
  });

  $("saveSettingsBtn")?.addEventListener("click", () => {
    saveCaptainTeamProfile("settingsMessage")
      .then(() => closeModals())
      .catch((error) => setMessage("settingsMessage", error.message, "error"));
  });

  document.addEventListener("input", (event) => {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    const form = input?.closest<HTMLFormElement>("#playerOverviewEditForm");
    if (!input || !form) return;
    const formData = new FormData(form);
    playerOverviewEditDraft = {
      games: formData.getAll("games").map(clean).filter(Boolean),
    };
    if (!["steam_id", "epic_id", "ps4_id", "xbox_id"].includes(input.name)) return;
    const trackerInput = form.elements.namedItem("player_tracker_link") as HTMLInputElement | null;
    if (!trackerInput) return;
    const current = clean(trackerInput.value);
    const previousGenerated = clean(trackerInput.dataset.generatedValue);
    const nextGenerated = rocketLeagueTrackerLink(
      (form.elements.namedItem("steam_id") as HTMLInputElement | null)?.value,
      (form.elements.namedItem("epic_id") as HTMLInputElement | null)?.value,
      (form.elements.namedItem("ps4_id") as HTMLInputElement | null)?.value,
      (form.elements.namedItem("xbox_id") as HTMLInputElement | null)?.value,
    );
    if (!current || current === previousGenerated) {
      trackerInput.value = nextGenerated;
      trackerInput.dataset.generatedValue = nextGenerated;
    }
    const openTracker = form.querySelector<HTMLAnchorElement>("[data-open-player-tracker]");
    if (openTracker) {
      openTracker.href = trackerHref(trackerInput.value) || "#";
      openTracker.setAttribute("aria-disabled", trackerInput.value ? "false" : "true");
    }
  });
  document.addEventListener("change", (event) => {
    const input = event.target instanceof HTMLInputElement ? event.target : null;
    if (input?.form?.id === "playerProfileMediaForm" && input.type === "file") {
      const file = input.files?.[0];
      const preview = document.querySelector<HTMLElement>(`[data-player-media-preview="${input.name}"]`);
      if (file && preview) {
        const previousUrl = preview.dataset.previewUrl;
        if (previousUrl) URL.revokeObjectURL(previousUrl);
        const url = URL.createObjectURL(file);
        preview.dataset.previewUrl = url;
        preview.innerHTML = `<img src="${esc(url)}" alt="Selected ${esc(input.name)} preview"><em>Ready to submit</em>`;
      }
      return;
    }
    const form = input?.closest<HTMLFormElement>("#playerOverviewEditForm");
    if (!input || !form || input.name !== "games") return;
    const formData = new FormData(form);
    playerOverviewEditDraft = {
      games: formData.getAll("games").map(clean).filter(Boolean),
    };
  });

  document.addEventListener("submit", async (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form) return;
    if (form.id === "playerProfileMediaForm") {
      event.preventDefault();
      const message = $("playerProfileMediaMessage");
      const data = new FormData(form);
      if (!data.get("photo")?.valueOf() && !data.get("banner")?.valueOf()) return;
      if (message) message.textContent = "Uploading securely to site moderation...";
      try {
        const result = await api("/api/team-portal/player-profile/media", { method: "POST", body: data });
        dashboard = result.dashboard || dashboard;
        if (dashboard.account && result.pendingMedia) {
          if (result.pendingMedia.photo) dashboard.account.pending_player_logo_path = result.pendingMedia.photo;
          if (result.pendingMedia.banner) dashboard.account.pending_player_banner_path = result.pendingMedia.banner;
        }
        renderProfile();
        const refreshed = $("playerProfileMediaMessage");
        if (refreshed) refreshed.textContent = result.message || "Images sent for approval.";
      } catch (error) {
        if (message) { message.textContent = (error as Error).message; message.dataset.tone = "error"; }
      }
      return;
    }
    if (form.id !== "playerOverviewEditForm") return;
    event.preventDefault();
    const message = $("playerOverviewEditMessage");
    if (message) {
      message.textContent = "Saving player details...";
      message.dataset.tone = "";
    }
    const data = new FormData(form);
    const selectedGames = Array.from(form.querySelectorAll<HTMLInputElement>("input[name='games']:checked"))
      .map((input) => clean(input.value))
      .filter(Boolean);
    try {
      const result = await api("/api/team-portal/player-profile", {
        method: "POST",
        body: JSON.stringify({
          in_game_name: data.get("in_game_name"),
          player_name: data.get("player_name"),
          display_name: data.get("display_name"),
          steam_id: data.get("steam_id"),
          epic_id: data.get("epic_id"),
          ps4_id: data.get("ps4_id"),
          xbox_id: data.get("xbox_id"),
          rocket_league_mmr_1v1: data.get("rocket_league_mmr_1v1"),
          rocket_league_mmr_2v2: data.get("rocket_league_mmr_2v2"),
          rocket_league_mmr_3v3: data.get("rocket_league_mmr_3v3"),
          player_external_id: data.get("player_external_id"),
          player_tracker_link: data.get("player_tracker_link"),
          twitch_url: data.get("twitch_url"),
          kick_url: data.get("kick_url"),
          youtube_url: data.get("youtube_url"),
          games_json: JSON.stringify(selectedGames),
          profile_visibility: dashboard.account?.profile_visibility || "public",
        }),
      });
      dashboard = result.dashboard || dashboard;
      if (dashboard.account) {
        dashboard.account.games = selectedGames;
        dashboard.account.games_json = JSON.stringify(selectedGames);
      }
      playerOverviewEditing = false;
      playerOverviewEditDraft = null;
      hydrateRoster();
      render();
    } catch (error) {
      if (message) {
        message.textContent = (error as Error).message || "Could not save player details.";
        message.dataset.tone = "error";
      }
    }
  });

  $("captainCreateTeamForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      setMessage("captainCreateTeamMessage", "Creating team...");
      const result = await api("/api/team-portal/captain-teams", {
        method: "POST",
        body: JSON.stringify({
          team_name: $<HTMLInputElement>("captainTeamName")?.value,
          short_name: $<HTMLInputElement>("captainTeamShortName")?.value,
          region: $<HTMLSelectElement>("captainTeamRegion")?.value,
          platform: $<HTMLSelectElement>("captainTeamPlatform")?.value,
        }),
      });
      dashboard = result.dashboard || dashboard;
      activeViewMode = "manager";
      localStorage.setItem("teamPortalViewMode", activeViewMode);
      hydrateRoster();
      (event.target as HTMLFormElement).reset();
      setMessage("captainCreateTeamMessage", "Team created. You are the Captain.", "ok");
      closeModals();
      render();
    } catch (error) {
      setMessage("captainCreateTeamMessage", (error as Error).message, "error");
    }
  });

  $("captainAddPlayerForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      const typedId = captainPlayerLookupInputId();
      if (!captainPlayerLookup || typedId !== captainPlayerLookupId) {
        throw new Error("Confirm the player preview before adding them.");
      }
      if (captainPlayerLookup.already_on_roster) throw new Error("That player is already on this roster.");
      if (captainPlayerLookup.pending_invite) throw new Error("A roster invite is already pending for that player.");
      if (!captainPlayerLookup.portal_linked) throw new Error("That Bracketr ID is not linked to a Player Portal account yet.");
      setMessage("captainAddPlayerMessage", "Adding player...");
      const result = await api("/api/team-portal/captain-teams/current/players", {
        method: "POST",
        body: JSON.stringify({
          team_account_id: selectedCaptainTeam()?.team_account_id || dashboard.account?.selected_team_account_id || "",
          bracketr_player_id: typedId,
          role: $<HTMLSelectElement>("captainPlayerRole")?.value,
        }),
      });
      dashboard = result.dashboard || dashboard;
      activeViewMode = "manager";
      localStorage.setItem("teamPortalViewMode", activeViewMode);
      hydrateRoster();
      (event.target as HTMLFormElement).reset();
      setMessage("captainAddPlayerMessage", result.message || "Roster request sent.", "ok");
      if (result.message) alert(result.message);
      else closeModals();
      render();
    } catch (error) {
      setMessage("captainAddPlayerMessage", (error as Error).message, "error");
    }
  });

  $("captainPlayerBracketrId")?.addEventListener("input", () => {
    captainPlayerLookup = null;
    captainPlayerLookupId = "";
    const id = captainPlayerLookupInputId();
    const submit = $<HTMLButtonElement>("captainAddPlayerSubmit");
    if (submit) submit.disabled = true;
    window.clearTimeout(captainPlayerLookupTimer);
    if (!id) {
      resetCaptainPlayerLookup();
      return;
    }
    const preview = $("captainPlayerLookupPreview");
    if (preview) preview.innerHTML = `<p class="team-empty compact">Waiting for ID...</p>`;
    captainPlayerLookupTimer = window.setTimeout(() => lookupCaptainPlayerById(), 350);
  });

  $("logoInput")?.addEventListener("change", () => {
    selectedLogoPreviewUrl = filePreviewUrl("logoInput", selectedLogoPreviewUrl);
    renderSettings();
  });

  $("bannerInput")?.addEventListener("change", () => {
    selectedBannerPreviewUrl = filePreviewUrl("bannerInput", selectedBannerPreviewUrl);
    renderSettings();
  });

  $("refreshBtn")?.addEventListener("click", loadMe);

  $("logoutBtn")?.addEventListener("click", async () => {
    await fetch("/api/team-portal/logout", { method: "POST", credentials: "same-origin" });
    window.location.href = "/dashboard?view=user";
  });

  $("loginForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      setMessage("loginMessage", "Logging in...");
      const result = await api("/api/team-portal/login", {
        method: "POST",
        body: JSON.stringify({
          email: $<HTMLInputElement>("loginEmail")?.value,
          password: $<HTMLInputElement>("loginPassword")?.value,
        }),
      });
      dashboard = result.dashboard || {};
      playerMarket = await api("/api/team-portal/market").catch(() => ({}));
      hydrateRoster();
      setMessage("loginMessage", "");
      render();
      connectEvents();
    } catch (error) {
      setMessage("loginMessage", (error as Error).message, "error");
    }
  });

  $("forgotToggleBtn")?.addEventListener("click", () => {
    $("forgotCard")?.classList.toggle("is-open");
    const email = $<HTMLInputElement>("loginEmail")?.value;
    if (email && $("forgotEmail")) ($<HTMLInputElement>("forgotEmail") as HTMLInputElement).value = email;
  });

  $("signupToggleBtn")?.addEventListener("click", () => {
    $("signupCard")?.classList.toggle("is-open");
    const email = $<HTMLInputElement>("loginEmail")?.value;
    if (email && $("signupEmail")) ($<HTMLInputElement>("signupEmail") as HTMLInputElement).value = email;
  });

  function openSignupFromHash() {
    if (window.location.hash !== "#signup") return;
    $("signupCard")?.classList.add("is-open");
    $("signupEmail")?.scrollIntoView({ block: "center", behavior: "smooth" });
    window.setTimeout(() => $<HTMLInputElement>("signupEmail")?.focus(), 250);
  }

  window.addEventListener("hashchange", openSignupFromHash);
  openSignupFromHash();

  $("signupForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      setMessage("signupMessage", "Creating player account...");
      const result = await api("/api/team-portal/player-signup", {
        method: "POST",
        body: JSON.stringify({
          email: $<HTMLInputElement>("signupEmail")?.value,
          in_game_name: $<HTMLInputElement>("signupInGameName")?.value,
          player_name: $<HTMLInputElement>("signupPlayerName")?.value,
          steam_id: $<HTMLInputElement>("signupSteamId")?.value,
          epic_id: $<HTMLInputElement>("signupEpicId")?.value,
          password: $<HTMLInputElement>("signupPassword")?.value,
          confirm_password: $<HTMLInputElement>("signupConfirmPassword")?.value,
        }),
      });
      dashboard = result.dashboard || {};
      playerMarket = {};
      hydrateRoster();
      setMessage("signupMessage", result.bracketrPlayerId ? `Player account created. Your ID is ${result.bracketrPlayerId}.` : "Player account created.", "ok");
      window.setTimeout(() => {
        render();
        connectEvents();
      }, 500);
    } catch (error) {
      setMessage("signupMessage", (error as Error).message, "error");
    }
  });

  $("resendActivationBtn")?.addEventListener("click", async () => {
    try {
      const email = $<HTMLInputElement>("loginEmail")?.value;
      if (!email) throw new Error("Enter the team email first.");
      const result = await api("/api/team-portal/resend-activation", { method: "POST", body: JSON.stringify({ email }) });
      setMessage("loginMessage", result.message || "If needed, an activation email has been sent.", "ok");
    } catch (error) {
      setMessage("loginMessage", (error as Error).message, "error");
    }
  });

  $("forgotForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      setMessage("forgotMessage", "Sending...");
      const result = await api("/api/team-portal/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email: $<HTMLInputElement>("forgotEmail")?.value }),
      });
      setMessage("forgotMessage", result.message || "If this email exists, reset instructions have been sent.", "ok");
    } catch (error) {
      setMessage("forgotMessage", (error as Error).message, "error");
    }
  });

  $("resetForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      const password = $<HTMLInputElement>("resetPassword")?.value || "";
      const confirmPassword = $<HTMLInputElement>("resetConfirm")?.value || "";
      if (password !== confirmPassword) throw new Error("Passwords do not match.");
      const result = await api(playerActivateToken ? "/api/team-portal/player-activate" : "/api/team-portal/reset-password", {
        method: "POST",
        body: JSON.stringify({ token: playerActivateToken || resetToken, password }),
      });
      setMessage("resetMessage", result.message || "Password updated.", "ok");
      if (result.dashboard) {
        dashboard = result.dashboard || {};
        playerMarket = await api("/api/team-portal/market").catch(() => ({}));
        hydrateRoster();
        window.history.replaceState({}, document.title, "/dashboard?view=user&activated=1");
        render();
        connectEvents();
      } else {
        window.setTimeout(() => { window.location.href = "/dashboard?view=user"; }, 900);
      }
    } catch (error) {
      setMessage("resetMessage", (error as Error).message, "error");
    }
  });

  $("requestForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      const requestType = $<HTMLSelectElement>("requestType")?.value || "add";
      const source = $<HTMLSelectElement>("requestSource")?.value || "manual";
      const marketIndex = Number($<HTMLSelectElement>("requestMarketPlayer")?.value ?? -1);
      const marketPlayer = marketRowsForSource(source)[marketIndex] || {};
      const account = dashboard.account || {};
      setMessage("requestMessage", "Submitting...");
      const result = await api("/api/team-portal/trade-requests", {
        method: "POST",
        body: JSON.stringify({
          league_id: $<HTMLSelectElement>("requestLeagueId")?.value,
          request_type: requestType,
          player_name: $<HTMLInputElement>("requestPlayerName")?.value,
          in_game_name: $<HTMLInputElement>("requestInGameName")?.value || (requestType === "disband" ? account.team_name : ""),
          role: $<HTMLInputElement>("requestRole")?.value,
          steam_epic_id: $<HTMLInputElement>("requestSteamEpicId")?.value,
          target_player_account_id: marketPlayer.player_account_id || "",
          source_team_account_id: marketPlayer.team_account_id || "",
          reason: $<HTMLTextAreaElement>("requestReason")?.value,
        }),
      });
      dashboard = result.dashboard || dashboard;
      playerMarket = await api("/api/team-portal/market").catch(() => playerMarket);
      hydrateRoster();
      (event.target as HTMLFormElement).reset();
      setMessage("requestMessage", "Request submitted.", "ok");
      render();
    } catch (error) {
      setMessage("requestMessage", (error as Error).message, "error");
    }
  });

  document.addEventListener("submit", async (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (form?.dataset.ladderCounterForm) {
      event.preventDefault();
      const app = selectedCompetition();
      const data = new FormData(form);
      try {
        if (!app?.league_id) throw new Error("Open an active ladder entry first.");
        const proposedSlots = ["slot_1", "slot_2", "slot_3"].map((name) => {
          const [date, time = ""] = clean(data.get(name)).split("T");
          return { date, time };
        });
        if (proposedSlots.some((slot) => !slot.date || !slot.time)) throw new Error("Choose all three proposed match times.");
        if (new Set(proposedSlots.map((slot) => `${slot.date}T${slot.time}`)).size !== 3) throw new Error("Choose three different proposed match times.");
        await api(`/api/leagues/${encodeURIComponent(app.league_id)}/ladder/challenges/${encodeURIComponent(form.dataset.ladderCounterForm)}/counter`, {
          method: "POST",
          body: JSON.stringify({ proposed_slots: proposedSlots, message: clean(data.get("message")) }),
        });
        await loadMe();
      } catch (error) {
        alert((error as Error).message);
      }
      return;
    }
    if (form?.dataset.ladderChallengeForm !== undefined) {
      event.preventDefault();
      const app = applications.find((item) => competitionKey(item) === clean(form.dataset.competitionKey))
        || selectedCompetition();
      const data = new FormData(form);
      try {
        if (!app?.league_id || !app?.created_team_id) throw new Error("Open an active ladder entry first.");
        const proposedSlots = ["slot_1", "slot_2", "slot_3"].map((name) => {
          const value = clean(data.get(name));
          const [date, time = ""] = value.split("T");
          return { date, time };
        });
        if (proposedSlots.some((slot) => !slot.date || !slot.time)) throw new Error("Choose all three proposed match times.");
        if (new Set(proposedSlots.map((slot) => `${slot.date}T${slot.time}`)).size !== 3) throw new Error("Choose three different proposed match times.");
        await api(`/api/leagues/${encodeURIComponent(app.league_id)}/ladder/challenges`, {
          method: "POST",
          body: JSON.stringify({
            challenger_team_id: app.created_team_id,
            defender_team_id: data.get("defender_team_id"),
            proposed_slots: proposedSlots,
            message: clean(data.get("message")),
          }),
        });
        await loadMe();
      } catch (error) {
        alert((error as Error).message);
      }
      return;
    }
    if (form?.dataset.captainRosterRequestForm !== undefined) {
      event.preventDefault();
      const app = selectedCaptainCompetition();
      const mode = captainRosterRequestMode;
      const compRoster = captainCompetitionRoster(app);
      const availableRoster = captainAvailableRosterForCompetition(app);
      const addKey = $<HTMLSelectElement>("captainRosterAddPlayer")?.value || "";
      const removeKey = $<HTMLSelectElement>("captainRosterRemovePlayer")?.value || "";
      const addPlayer = availableRoster.find((player) => rosterPersonKey(player) === addKey) || null;
      const removePlayer = compRoster.find((player) => rosterPersonKey(player) === removeKey) || null;
      const reason = clean($<HTMLTextAreaElement>("captainRosterRequestReason")?.value);
      try {
        if (!app?.league_id) throw new Error("Select a competition first.");
        if (mode === "add" && !addPlayer) throw new Error("Choose a team player to add.");
        if (mode === "remove" && !removePlayer) throw new Error("Choose a competition lineup player to remove.");
        if (mode === "replace" && (!addPlayer || !removePlayer)) throw new Error("Choose both the player joining and the player leaving.");
        const primary = mode === "remove" ? removePlayer : addPlayer;
        if (!primary) throw new Error("Choose a player for this request.");
        captainRosterRequestMessage = "Submitting...";
        renderSettings();
        const notes = [
          mode === "add" ? `Add ${rosterPersonName(addPlayer || {})} to the competition lineup.`
            : mode === "remove" ? `Remove ${rosterPersonName(removePlayer || {})} from the competition lineup.`
              : `Swap ${rosterPersonName(removePlayer || {})} out and ${rosterPersonName(addPlayer || {})} in.`,
          reason,
        ].filter(Boolean).join("\n\n");
        const result = await api("/api/team-portal/trade-requests", {
          method: "POST",
          body: JSON.stringify({
            league_id: app.league_id,
            team_account_id: selectedCaptainTeam()?.team_account_id || dashboard.account?.selected_team_account_id || "",
            request_type: mode,
            player_name: first(primary.player_name, primary.display_name, rosterPersonName(primary)),
            in_game_name: rosterPersonName(primary),
            role: first(primary.role, "Player"),
            steam_epic_id: first(playerSteamId(primary), playerEpicId(primary), primary.steam_epic_id),
            target_player_account_id: primary.player_account_id || "",
            reason: notes,
          }),
        });
        dashboard = result.dashboard || dashboard;
        hydrateRoster();
        captainRosterRequestOpen = false;
        captainRosterRequestMessage = "";
        captainCenterTab = "requests";
        render();
      } catch (error) {
        captainRosterRequestMessage = (error as Error).message || "Could not submit request.";
        renderSettings();
      }
      return;
    }
    if (form?.dataset.leagueMessage) {
      event.preventDefault();
      const data = new FormData(form);
      try {
        const result = await api(`/api/team-portal/leagues/${encodeURIComponent(form.dataset.leagueMessage)}/messages`, {
          method: "POST",
          body: JSON.stringify({ message: clean(data.get("message")) }),
        });
        dashboard = result.dashboard || dashboard;
        form.reset();
        render();
      } catch (error) {
        alert((error as Error).message);
      }
      return;
    }
    if (form?.dataset.matchMessage) {
      event.preventDefault();
      const data = new FormData(form);
      const matchId = form.dataset.matchMessage;
      try {
        const result = await api(`/api/team-portal/matches/${encodeURIComponent(matchId)}/messages`, {
          method: "POST",
          body: JSON.stringify({ message: clean(data.get("message")) }),
        });
        dashboard = result.dashboard || dashboard;
        matchChatDrafts.delete(matchId);
        form.reset();
        activeMatchDetailTab = "chat";
        render();
      } catch (error) {
        alert((error as Error).message);
      }
      return;
    }
    if (form?.dataset.teamStreamMatch) {
      event.preventDefault();
      const data = new FormData(form);
      try {
        const result = await api(`/api/team-portal/matches/${encodeURIComponent(form.dataset.teamStreamMatch)}/broadcast-requests`, {
          method: "POST",
          body: JSON.stringify({ request_kind: "team_stream", stream_url: clean(data.get("stream_url")) }),
        });
        dashboard = result.dashboard || dashboard;
        hydrateRoster();
        render();
      } catch (error) {
        alert((error as Error).message);
      }
      return;
    }
    if (!form?.dataset.resultMatch) return;
    event.preventDefault();
    const data = new FormData(form);
    try {
      const team1 = Number(data.get("team1_score") || 0);
      const team2 = Number(data.get("team2_score") || 0);
      const result = await api(`/api/team-portal/matches/${encodeURIComponent(form.dataset.resultMatch)}/result-submissions`, {
        method: "POST",
        body: JSON.stringify({
          team1_score: team1,
          team2_score: team2,
          notes: clean(data.get("notes")),
          games: [{ game: 1, team1_score: team1, team2_score: team2, map: "" }],
        }),
      });
      dashboard = result.dashboard || dashboard;
      render();
    } catch (error) {
      alert((error as Error).message);
    }
  });

  $("passwordForm")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      const newPassword = $<HTMLInputElement>("newPassword")?.value || "";
      const confirmPassword = $<HTMLInputElement>("confirmNewPassword")?.value || "";
      if (newPassword !== confirmPassword) throw new Error("New passwords do not match.");
      const result = await api("/api/team-portal/change-password", {
        method: "POST",
        body: JSON.stringify({
          current_password: $<HTMLInputElement>("currentPassword")?.value,
          new_password: newPassword,
        }),
      });
      setMessage("passwordMessage", result.message || "Password changed.", "ok");
      (event.target as HTMLFormElement).reset();
    } catch (error) {
      setMessage("passwordMessage", (error as Error).message, "error");
    }
  });

  $("markNotificationsReadBtn")?.addEventListener("click", async () => {
    const result = await api("/api/team-portal/notifications/read", { method: "POST", body: "{}" });
    dashboard = result.dashboard || dashboard;
    notificationsOpen = true;
    render();
  });

  if (params.get("activated")) setMessage("loginMessage", "Account activated. You are logged in.", "ok");
  window.setInterval(updateMatchCounters, 1000);
  startLiveDashboardRefresh();
  loadMe();
})();
